import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Funnel, ChartBar, TrendUp, Trophy, Target, CaretUp, CaretDown, CaretRight, ShareNetwork, Prohibit, Percent, Check, Clock, Crosshair, Info, Sun, Moon, Lightning, Gear, PencilSimple, Shield, Scales, Warning, ArrowUp, ArrowDown, SlidersHorizontal, ListBullets, Lock, Equals, ShieldCheck } from "phosphor-react";
import { Button } from "@/components/ui/button";
import GeniLoader from "../components/GeniLoader";
import { useLanguage } from "../context/LanguageContext";
import { useSharedProfile } from "../context/SharedProfileContext";

// Footer branding
const LOGO_PUREBRAIN = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ShareModal from "../components/ShareModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Timeframe order for sorting
const TIMEFRAME_ORDER = {
  "1M": 1, "3M": 2, "5M": 3, "10M": 4, "15M": 5, "20M": 6, "30M": 7, "45M": 8,
  "1H": 9, "2H": 10, "3H": 11, "4H": 12, "6H": 13, "8H": 14, "12H": 15, 
  "1D": 16, "3D": 17, "1W": 18
};

// Minimum trades required for a timeframe to appear in analysis table
const MIN_TRADES_FOR_TIMEFRAME_ANALYSIS = 5;

/**
 * Normalize timeframe to canonical format for analytics grouping
 * 
 * Handles multiple input formats and converts to standard display format:
 * - Minutes: 1M, 3M, 5M, 10M, 15M, 20M, 30M, 45M
 * - Hours: 1H, 2H, 3H, 4H, 6H, 8H, 12H
 * - Days/Weeks: 1D, 3D, 1W
 * 
 * Examples:
 * - "1h", "1H", "60m", "60M", "60" → "1H"
 * - "30", "30m", "30M" → "30M"
 * - "4h", "4H", "240m", "240" → "4H"
 * - "1d", "1D", "D" → "1D"
 * 
 * @param {string} timeframe - Raw timeframe value
 * @returns {string|null} - Normalized timeframe or null if invalid
 */
const normalizeTimeframeForAnalytics = (timeframe) => {
  if (!timeframe || typeof timeframe !== 'string') return null;
  
  const tf = timeframe.trim().toUpperCase();
  if (!tf) return null;
  
  // Direct canonical matches (already normalized)
  const canonicalFormats = ['1M', '3M', '5M', '10M', '15M', '20M', '30M', '45M', '1H', '2H', '3H', '4H', '6H', '8H', '12H', '1D', '3D', '1W'];
  if (canonicalFormats.includes(tf)) return tf;
  
  // Handle "D" and "W" shorthand
  if (tf === 'D') return '1D';
  if (tf === 'W') return '1W';
  
  // Try to extract numeric value and unit
  const match = tf.match(/^(\d+)(M|H|D|W)?$/);
  if (!match) return null;
  
  const num = parseInt(match[1], 10);
  const unit = match[2] || 'M'; // Default to minutes if no unit (historical data like "30")
  
  if (isNaN(num) || num <= 0) return null;
  
  // Convert to minutes for standardization
  let minutes;
  switch (unit) {
    case 'M':
      minutes = num;
      break;
    case 'H':
      minutes = num * 60;
      break;
    case 'D':
      minutes = num * 1440;
      break;
    case 'W':
      minutes = num * 10080;
      break;
    default:
      return null;
  }
  
  // Convert minutes to canonical format
  // Weeks
  if (minutes >= 10080) {
    const weeks = minutes / 10080;
    return weeks === 1 ? '1W' : null; // Only support 1W for now
  }
  // Days
  if (minutes >= 1440) {
    const days = minutes / 1440;
    if (days === 1) return '1D';
    if (days === 3) return '3D';
    return `${Math.round(days)}D`;
  }
  // Hours (>= 60 minutes)
  if (minutes >= 60) {
    const hours = minutes / 60;
    if (Number.isInteger(hours)) {
      return `${hours}H`;
    }
    // For non-integer hours, round to nearest supported
    const roundedHours = Math.round(hours);
    return `${roundedHours}H`;
  }
  // Minutes
  return `${minutes}M`;
};

// Profit Factor color based on value
const getProfitFactorColor = (pf) => {
  const pfValue = parseFloat(pf) || 0;
  if (pfValue < 1.0) return "text-[#FF3B30]";  // Unprofitable
  if (pfValue <= 1.5) return "text-[#FFC107]"; // Decent
  return "text-[#00E676]"; // Strong
};

// Profit Factor friendly label based on value
const getProfitFactorLabel = (pf) => {
  const pfValue = parseFloat(pf) || 0;
  if (isNaN(pfValue) || pfValue === 0) return "N/A";
  if (pfValue < 1.0) return "Unprofitable";
  if (pfValue <= 1.5) return "Decent";
  return "Strong";
};

const MetricCard = ({ title, value, subtitle, icon: Icon, highlight = false, trend = null, testId = null }) => (
  <div className="bg-[#121212] border border-white/10 rounded-sm p-6" data-testid={testId}>
    <div className="flex items-start justify-between mb-3">
      <div className="text-white/40 text-xs font-mono uppercase tracking-wider">{title}</div>
      {Icon && <Icon size={20} weight="duotone" className="text-white/40" />}
    </div>
    <div className={`text-3xl font-bold font-mono mb-1 ${highlight ? highlight : 'text-white'}`}>
      {value}
    </div>
    {subtitle && <div className="text-white/60 text-sm">{subtitle}</div>}
    {trend && <div className={`text-xs font-mono mt-2 ${trend.colorClass}`} data-testid={testId ? `${testId}-trend` : undefined}>{trend.label}</div>}
  </div>
);

export default function AdvancedAnalytics() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // Shared/Public profile mode - when viewing another user's profile
  const { isSharedProfileView, isReadOnly, publicProfileUserId } = useSharedProfile();
  
  // Helper to get the correct route for public vs private mode
  const getRoute = (privatePath) => {
    if (isSharedProfileView && publicProfileUserId) {
      return `/profile/${publicProfileUserId}${privatePath}`;
    }
    return privatePath;
  };
  
  // Helper for navigating to filtered history in public mode, or detail page in private mode
  const navigateToFilteredView = (route, options = {}) => {
    // Build query params and state for History navigation
    const queryParams = new URLSearchParams();
    const state = {};
    
    // Trade Type filter
    if (options.tradeType) {
      queryParams.set('trade_type', options.tradeType);
    }
    
    // Asset Group filter
    if (options.assetGroupId) {
      queryParams.set('asset_group_id', options.assetGroupId);
      state.assetGroupName = options.assetGroupName || 'Asset Group';
    }
    
    // Setup filter (from Advanced Analytics setup selector)
    if (options.setupFilter) {
      queryParams.set('setup_id', options.setupFilter);
    }
    
    const queryString = queryParams.toString();
    const targetUrl = route + (queryString ? `?${queryString}` : '');
    
    if (isSharedProfileView && publicProfileUserId) {
      // In public mode, use public route
      navigate(getRoute('/history') + (queryString ? `?${queryString}` : ''), { state });
    } else {
      // In private mode, navigate to history with filters
      navigate(targetUrl, { state });
    }
  };
  
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  
  // Filters
  const [setups, setSetups] = useState([]);
  const [assetGroups, setAssetGroups] = useState([]);
  const [selectedSetup, setSelectedSetup] = useState("");
  const [selectedAssetGroup, setSelectedAssetGroup] = useState("");
  const [selectedTimeframe, setSelectedTimeframe] = useState("");
  const [selectedTradeType, setSelectedTradeType] = useState("");
  
  // Alias for setup filter to use in drilldowns
  const advancedSetupFilter = selectedSetup || null;

  // Timeframe table sorting
  const [tfSortColumn, setTfSortColumn] = useState(null); // 'timeframe', 'trades', 'win_rate', 'avg_rr', 'profit_factor'
  const [tfSortDirection, setTfSortDirection] = useState(null); // 'asc', 'desc', or null

  // Share functionality
  const [showShareModal, setShowShareModal] = useState(false);

  // Canceled trades and all trades data for behavior sections
  const [canceledTrades, setCanceledTrades] = useState([]);
  const [allTrades, setAllTrades] = useState([]);
  const [totalTrades, setTotalTrades] = useState(0);

  // Local setup filter for Partial sections ONLY
  const [partialSetupFilter, setPartialSetupFilter] = useState("");

  // Local setup filter for Planned Trades section
  const [plannedSetupFilter, setPlannedSetupFilter] = useState("");

  // Local setup filter for Execution Quality section
  const [executionSetupFilter, setExecutionSetupFilter] = useState("");

  // Local setup filter for Hedge Analytics section
  const [hedgeSetupFilter, setHedgeSetupFilter] = useState("");

  // Local setup filter for Hand Size Consistency section
  const [handSizeSetupFilter, setHandSizeSetupFilter] = useState("");

  // Local setup filter for Stop Adjustments section
  const [stopAdjustmentsSetupFilter, setStopAdjustmentsSetupFilter] = useState("");

  // Local setup filter for Performance by Timeframe section
  const [timeframeSetupFilter, setTimeframeSetupFilter] = useState("");

  // Local setup filter for Performance by Asset Group section
  const [assetGroupSetupFilter, setAssetGroupSetupFilter] = useState("");

  // Local setup filter for Trade Type Comparison section
  const [tradeTypeSetupFilter, setTradeTypeSetupFilter] = useState("");

  // Local setup filter for Filtered Trade Performance (summary) section
  const [summarySetupFilter, setSummarySetupFilter] = useState("");

  // Collapsible section states - load from localStorage or use defaults
  const [expandedSections, setExpandedSections] = useState(() => {
    const defaultState = {
      summary: true,
      timeframe: true,
      assetGroup: true,
      tradeType: true,
      partial: true,
      hedge: true,
      stopAdjustments: true,
      handSize: true,
      planned: true,
      executionQuality: true
    };
    
    try {
      const saved = localStorage.getItem('geni_advanced_collapsed_state');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults to handle any new sections
        return { ...defaultState, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load collapsed state from localStorage:', e);
    }
    return defaultState;
  });

  // Edit Layout mode state
  const [isEditingLayout, setIsEditingLayout] = useState(false);

  // Default section order
  const DEFAULT_SECTION_ORDER = [
    'summary',
    'timeframe',
    'assetGroup',
    'tradeType',
    'partial',
    'executionQuality',
    'hedge',
    'handSize',
    'stopAdjustments',
    'planned'
  ];

  // User custom section order (loaded from localStorage)
  const [userSectionOrder, setUserSectionOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('geni_advanced_section_order');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that all sections are present
        if (Array.isArray(parsed) && parsed.length === DEFAULT_SECTION_ORDER.length) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error loading section order:', e);
    }
    return DEFAULT_SECTION_ORDER;
  });

  // Save section order to localStorage
  const saveSectionOrder = (order) => {
    try {
      localStorage.setItem('geni_advanced_section_order', JSON.stringify(order));
    } catch (e) {
      console.error('Error saving section order:', e);
    }
  };

  // Move section up in order
  const moveSectionUp = (sectionId) => {
    const currentIndex = userSectionOrder.indexOf(sectionId);
    if (currentIndex > 0) {
      const newOrder = [...userSectionOrder];
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
      setUserSectionOrder(newOrder);
      saveSectionOrder(newOrder);
    }
  };

  // Move section down in order
  const moveSectionDown = (sectionId) => {
    const currentIndex = userSectionOrder.indexOf(sectionId);
    if (currentIndex < userSectionOrder.length - 1) {
      const newOrder = [...userSectionOrder];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
      setUserSectionOrder(newOrder);
      saveSectionOrder(newOrder);
    }
  };

  // Reset to default order
  const resetSectionOrder = () => {
    setUserSectionOrder(DEFAULT_SECTION_ORDER);
    saveSectionOrder(DEFAULT_SECTION_ORDER);
  };

  // Toggle edit layout mode
  const toggleEditLayoutMode = () => {
    setIsEditingLayout(!isEditingLayout);
  };

  // Toggle section expand/collapse
  // Save collapsed state to localStorage
  const saveCollapsedState = (state) => {
    try {
      localStorage.setItem('geni_advanced_collapsed_state', JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save collapsed state to localStorage:', e);
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => {
      const newState = {
        ...prev,
        [sectionKey]: !prev[sectionKey]
      };
      saveCollapsedState(newState);
      return newState;
    });
  };

  const timeframes = ["1M", "5M", "15M", "30M", "1H", "2H", "4H", "6H", "12H", "1D", "1W"];

  // ============================================
  // GLOBAL FILTERS - Applied to entire Advanced page
  // These create the base dataset for all sections
  // ============================================
  
  // Apply global filters (Asset Group, Timeframe, Trade Type) to create base dataset
  // Setup is NOT included here - it's a local/section-level filter only
  const globallyFilteredTrades = useMemo(() => {
    let filtered = [...allTrades];
    
    // Filter by Asset Group
    if (selectedAssetGroup) {
      filtered = filtered.filter(t => t.asset_group_id === selectedAssetGroup);
    }
    
    // Filter by Timeframe
    if (selectedTimeframe) {
      filtered = filtered.filter(t => 
        t.timeframe && t.timeframe.toUpperCase() === selectedTimeframe.toUpperCase()
      );
    }
    
    // Filter by Trade Type
    if (selectedTradeType) {
      filtered = filtered.filter(t => t.trade_type === selectedTradeType);
    }
    
    return filtered;
  }, [allTrades, selectedAssetGroup, selectedTimeframe, selectedTradeType]);

  // ============================================
  // FILTERED TRADE PERFORMANCE (Summary) SECTION
  // ============================================

  // Filter trades for Summary section based on local setup filter
  // Uses globallyFilteredTrades as base, then applies local Setup filter
  const tradesForSummaryAnalysis = useMemo(() => {
    if (!summarySetupFilter) return globallyFilteredTrades;
    
    if (summarySetupFilter === '__no_setup__') {
      return globallyFilteredTrades.filter(t => !t.setup_id);
    }
    return globallyFilteredTrades.filter(t => t.setup_id === summarySetupFilter);
  }, [globallyFilteredTrades, summarySetupFilter]);

  // Get selected setup name for Summary display
  const summarySetupDisplayName = useMemo(() => {
    if (!summarySetupFilter) return null;
    if (summarySetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === summarySetupFilter);
    return setup?.name || null;
  }, [summarySetupFilter, setups]);

  // Compute summary metrics locally from filtered trades
  const computedSummaryMetrics = useMemo(() => {
    // Filter to closed trades only
    const closedTrades = tradesForSummaryAnalysis.filter(t => t.status === 'closed');
    const totalTrades = closedTrades.length;

    if (totalTrades === 0) {
      return {
        noData: true,
        total_trades: 0,
        win_count: 0,
        loss_count: 0,
        even_count: 0,
        win_rate: 0,
        win_rate_excluding_even: 0,
        average_rr: 0,
        total_r: 0,  // TOTAL R
        profit_factor: 0
      };
    }

    const wins = closedTrades.filter(t => t.outcome === 'win');
    const losses = closedTrades.filter(t => t.outcome === 'loss');
    const evens = closedTrades.filter(t => t.outcome === 'breakeven');
    const winCount = wins.length;
    const lossCount = losses.length;
    const evenCount = evens.length;
    const winRate = (winCount / totalTrades) * 100;
    const decisiveTrades = winCount + lossCount;
    const winRateExcludingEven = decisiveTrades > 0 ? (winCount / decisiveTrades) * 100 : 0;

    // Calculate average RR and total R
    const rrValues = closedTrades
      .map(t => t.rr)
      .filter(rr => rr !== null && rr !== undefined && !isNaN(rr));
    const totalR = rrValues.reduce((sum, rr) => sum + rr, 0);  // TOTAL R: sum of all realized R
    const avgRR = rrValues.length > 0 
      ? totalR / rrValues.length
      : 0;

    // Calculate profit factor
    const winningRRs = rrValues.filter(rr => rr > 0);
    const losingRRs = rrValues.filter(rr => rr < 0);
    const sumWins = winningRRs.reduce((sum, rr) => sum + rr, 0);
    const sumLosses = Math.abs(losingRRs.reduce((sum, rr) => sum + rr, 0));
    const profitFactor = sumLosses > 0 
      ? sumWins / sumLosses
      : (sumWins > 0 ? Infinity : 0);

    return {
      noData: false,
      total_trades: totalTrades,
      win_count: winCount,
      loss_count: lossCount,
      even_count: evenCount,
      win_rate: winRate,
      win_rate_excluding_even: winRateExcludingEven,
      average_rr: avgRR,
      total_r: totalR,  // TOTAL R for summary metrics
      profit_factor: profitFactor
    };
  }, [tradesForSummaryAnalysis]);

  // ============================================
  // MONTHLY COMPARISON (Total Trades + Total R only)
  // Current calendar month-to-date vs. same day-range of previous calendar month
  // Reuses the same closed-trades dataset and realized R (t.rr) as computedSummaryMetrics above
  // ============================================
  const monthlyComparison = useMemo(() => {
    const closedTrades = tradesForSummaryAnalysis.filter(t => t.status === 'closed');
    const getTimelineDate = (t) => {
      const raw = t.closed_at || t.created_at || t.updated_at;
      const date = raw ? new Date(raw) : null;
      return date && !isNaN(date.getTime()) ? date : null;
    };

    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const previousMonthDayCount = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    const previousPeriodEnd = new Date(
      now.getFullYear(), now.getMonth() - 1, Math.min(now.getDate(), previousMonthDayCount), 23, 59, 59, 999
    );

    let currentTrades = 0, previousTrades = 0, currentR = 0, previousR = 0;

    closedTrades.forEach((t) => {
      const date = getTimelineDate(t);
      if (!date) return;
      const rr = (t.rr !== null && t.rr !== undefined && !isNaN(t.rr)) ? t.rr : 0;
      if (date >= currentMonthStart && date <= now) {
        currentTrades += 1;
        currentR += rr;
      } else if (date >= previousMonthStart && date <= previousPeriodEnd) {
        previousTrades += 1;
        previousR += rr;
      }
    });

    if (previousTrades === 0) {
      return { available: false };
    }

    const tradesChangePct = ((currentTrades - previousTrades) / previousTrades) * 100;
    const rDiff = currentR - previousR;
    const signCrossed = previousR !== 0 && Math.sign(previousR) !== Math.sign(currentR || previousR);
    const rChangePct = (previousR !== 0 && !signCrossed) ? (rDiff / Math.abs(previousR)) * 100 : null;

    return {
      available: true,
      currentTrades,
      previousTrades,
      tradesChangePct,
      currentR,
      previousR,
      rDiff,
      rChangePct
    };
  }, [tradesForSummaryAnalysis]);

  const tradesTrend = useMemo(() => {
    if (!monthlyComparison.available) return null;
    const pct = monthlyComparison.tradesChangePct;
    const arrow = pct > 0 ? '\u2191' : pct < 0 ? '\u2193' : '\u2192';
    const label = `${arrow} ${Math.abs(pct).toFixed(0)}% ${language === 'ptBR' ? 'vs. mês anterior' : 'vs. last month'}`;
    return { label, colorClass: 'text-white/45' };
  }, [monthlyComparison, language]);

  const totalRTrend = useMemo(() => {
    if (!monthlyComparison.available) return null;
    const { rDiff, rChangePct } = monthlyComparison;
    const arrow = rDiff > 0 ? '\u2191' : rDiff < 0 ? '\u2193' : '\u2192';
    const diffLabel = `${rDiff >= 0 ? '+' : ''}${rDiff.toFixed(2)}R`;
    const pctLabel = rChangePct !== null ? ` (${rChangePct >= 0 ? '+' : ''}${rChangePct.toFixed(0)}%)` : '';
    const suffix = language === 'ptBR' ? 'vs. mês anterior' : 'vs. last month';
    const colorClass = rDiff > 0 ? 'text-[#00E676]' : rDiff < 0 ? 'text-[#FF3B30]' : 'text-white/45';
    return { label: `${arrow} ${diffLabel}${pctLabel} ${suffix}`, colorClass };
  }, [monthlyComparison, language]);

  const handleTfSort = (column) => {
    if (tfSortColumn !== column) {
      // New column - start with descending
      setTfSortColumn(column);
      setTfSortDirection('desc');
    } else if (tfSortDirection === 'desc') {
      // Same column, currently desc - switch to asc
      setTfSortDirection('asc');
    } else if (tfSortDirection === 'asc') {
      // Same column, currently asc - reset to no sort
      setTfSortColumn(null);
      setTfSortDirection(null);
    }
  };

  // Filter trades for Performance by Timeframe section based on local setup filter
  // IMPORTANT: Must be declared BEFORE sortedTimeframes and bestTimeframe which depend on it
  // Uses globallyFilteredTrades as base, then applies local Setup filter
  const tradesForTimeframeAnalysis = useMemo(() => {
    if (!timeframeSetupFilter) return globallyFilteredTrades;
    
    if (timeframeSetupFilter === '__no_setup__') {
      return globallyFilteredTrades.filter(t => !t.setup_id);
    }
    return globallyFilteredTrades.filter(t => t.setup_id === timeframeSetupFilter);
  }, [globallyFilteredTrades, timeframeSetupFilter]);

  // Get selected setup name for Timeframe Performance display
  const timeframeSetupDisplayName = useMemo(() => {
    if (!timeframeSetupFilter) return null;
    if (timeframeSetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === timeframeSetupFilter);
    return setup?.name || null;
  }, [timeframeSetupFilter, setups]);

  // Compute timeframe metrics locally from filtered trades
  // IMPORTANT: Must be declared BEFORE sortedTimeframes and bestTimeframe which depend on it
  // 
  // This normalizes timeframe variants before grouping:
  // - "1h", "1H", "60m", "60M" → all become "1H"
  // - "30", "30m", "30M" → all become "30M"
  // - Applies minimum trade threshold (5) AFTER normalization and aggregation
  const computedTimeframeData = useMemo(() => {
    // Filter to closed trades only (status === 'closed')
    const closedTrades = tradesForTimeframeAnalysis.filter(t => 
      t.status === 'closed' && 
      t.timeframe && 
      t.timeframe.trim() !== ''
    );

    if (closedTrades.length === 0) {
      return [];
    }

    // Group trades by NORMALIZED timeframe
    const tfGroups = {};
    closedTrades.forEach(trade => {
      const normalizedTf = normalizeTimeframeForAnalytics(trade.timeframe);
      if (!normalizedTf) return; // Skip invalid/unknown timeframes
      
      if (!tfGroups[normalizedTf]) {
        tfGroups[normalizedTf] = [];
      }
      tfGroups[normalizedTf].push(trade);
    });

    // Calculate metrics for each normalized timeframe
    const timeframeMetrics = Object.entries(tfGroups).map(([timeframe, trades]) => {
      const totalTrades = trades.length;
      const wins = trades.filter(t => t.outcome === 'win').length;
      const losses = trades.filter(t => t.outcome === 'loss').length;
      const evens = trades.filter(t => t.outcome === 'breakeven').length;
      const winRate = totalTrades > 0 ? ((wins / totalTrades) * 100).toFixed(1) : '0';
      const decisiveTrades = wins + losses;
      const winRateExcludingEven = decisiveTrades > 0 ? ((wins / decisiveTrades) * 100).toFixed(1) : '0';

      // Calculate average RR and total R
      const rrValues = trades
        .map(t => t.rr)
        .filter(rr => rr !== null && rr !== undefined && !isNaN(rr));
      const totalR = rrValues.reduce((sum, rr) => sum + rr, 0);  // TOTAL R
      const avgRR = rrValues.length > 0 
        ? (totalR / rrValues.length).toFixed(2)
        : 'N/A';

      // Calculate profit factor (sum of wins / abs(sum of losses))
      const winningRRs = rrValues.filter(rr => rr > 0);
      const losingRRs = rrValues.filter(rr => rr < 0);
      const sumWins = winningRRs.reduce((sum, rr) => sum + rr, 0);
      const sumLosses = Math.abs(losingRRs.reduce((sum, rr) => sum + rr, 0));
      const profitFactor = sumLosses > 0 
        ? (sumWins / sumLosses).toFixed(2)
        : (sumWins > 0 ? '∞' : 'N/A');

      return {
        timeframe,
        total_trades: totalTrades,
        wins,
        losses,
        evens,
        win_rate: winRate,
        win_rate_excluding_even: winRateExcludingEven,
        average_rr: avgRR,
        total_r: parseFloat(totalR.toFixed(2)),  // TOTAL R for this timeframe
        profit_factor: profitFactor
      };
    });

    // Apply minimum trades threshold AFTER normalization and aggregation
    const eligibleTimeframes = timeframeMetrics.filter(
      tf => tf.total_trades >= MIN_TRADES_FOR_TIMEFRAME_ANALYSIS
    );

    // Sort by TIMEFRAME_ORDER
    return eligibleTimeframes.sort((a, b) => {
      const orderA = TIMEFRAME_ORDER[a.timeframe] || 999;
      const orderB = TIMEFRAME_ORDER[b.timeframe] || 999;
      return orderA - orderB;
    });
  }, [tradesForTimeframeAnalysis]);

  // Sort timeframes data - uses computed data when setup filter is active
  const sortedTimeframes = useMemo(() => {
    // Use computed data when setup filter is active, otherwise use breakdown data
    const sourceData = timeframeSetupFilter 
      ? computedTimeframeData 
      : breakdown?.timeframes;
    
    if (!sourceData || sourceData.length === 0) return [];
    
    let data = [...sourceData];
    
    if (!tfSortColumn || !tfSortDirection) {
      return data;
    }

    data.sort((a, b) => {
      let aVal, bVal;

      switch (tfSortColumn) {
        case 'timeframe':
          aVal = TIMEFRAME_ORDER[a.timeframe] || 999;
          bVal = TIMEFRAME_ORDER[b.timeframe] || 999;
          break;
        case 'trades':
          aVal = a.total_trades || 0;
          bVal = b.total_trades || 0;
          break;
        case 'win_rate':
          aVal = parseFloat(a.win_rate_excluding_even ?? a.win_rate) || 0;
          bVal = parseFloat(b.win_rate_excluding_even ?? b.win_rate) || 0;
          break;
        case 'avg_rr':
          aVal = a.total_r !== undefined ? a.total_r : ((a.average_rr === 'N/A' ? -Infinity : parseFloat(a.average_rr)) || 0);
          bVal = b.total_r !== undefined ? b.total_r : ((b.average_rr === 'N/A' ? -Infinity : parseFloat(b.average_rr)) || 0);
          break;
        case 'profit_factor':
          aVal = a.profit_factor === 'N/A' || a.profit_factor === '∞' ? -Infinity : parseFloat(a.profit_factor) || 0;
          bVal = b.profit_factor === 'N/A' || b.profit_factor === '∞' ? -Infinity : parseFloat(b.profit_factor) || 0;
          break;
        default:
          return 0;
      }

      // Handle N/A values - push to bottom
      if (aVal === -Infinity && bVal === -Infinity) return 0;
      if (aVal === -Infinity) return 1;
      if (bVal === -Infinity) return -1;

      // Apply sort direction
      if (tfSortDirection === 'desc') {
        return bVal - aVal;
      } else {
        return aVal - bVal;
      }
    });

    return data;
  }, [breakdown?.timeframes, computedTimeframeData, timeframeSetupFilter, tfSortColumn, tfSortDirection]);

  // ============================================
  // ASSET GROUP SECTION - Filter and Computed Data
  // ============================================
  
  // Filter trades for Asset Group section based on local setup filter
  // Uses globallyFilteredTrades as base, then applies local Setup filter
  const tradesForAssetGroupAnalysis = useMemo(() => {
    if (!assetGroupSetupFilter) return globallyFilteredTrades;
    
    if (assetGroupSetupFilter === '__no_setup__') {
      return globallyFilteredTrades.filter(t => !t.setup_id);
    }
    return globallyFilteredTrades.filter(t => t.setup_id === assetGroupSetupFilter);
  }, [globallyFilteredTrades, assetGroupSetupFilter]);

  // Get selected setup name for Asset Group display
  const assetGroupSetupDisplayName = useMemo(() => {
    if (!assetGroupSetupFilter) return null;
    if (assetGroupSetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === assetGroupSetupFilter);
    return setup?.name || null;
  }, [assetGroupSetupFilter, setups]);

  // Compute asset group metrics locally from filtered trades
  const computedAssetGroupData = useMemo(() => {
    // Filter to closed trades only
    const closedTrades = tradesForAssetGroupAnalysis.filter(t => t.status === 'closed');
    
    if (closedTrades.length === 0 || !breakdown?.asset_groups) {
      return [];
    }

    // Get list of asset groups from breakdown (we need the group definitions)
    const assetGroupIds = breakdown.asset_groups.map(g => ({
      id: g.asset_group_id,
      name: g.asset_group_name
    }));

    // Calculate metrics for each asset group
    return assetGroupIds.map(group => {
      const groupTrades = closedTrades.filter(t => t.asset_group_id === group.id);
      const totalTrades = groupTrades.length;
      
      if (totalTrades === 0) {
        return {
          asset_group_id: group.id,
          asset_group_name: group.name,
          total_trades: 0,
          win_rate: '0',
          profit_factor: 'N/A'
        };
      }

      const wins = groupTrades.filter(t => t.outcome === 'win').length;
      const winRate = ((wins / totalTrades) * 100).toFixed(1);

      // Calculate profit factor
      const rrValues = groupTrades
        .map(t => t.rr)
        .filter(rr => rr !== null && rr !== undefined && !isNaN(rr));
      const winningRRs = rrValues.filter(rr => rr > 0);
      const losingRRs = rrValues.filter(rr => rr < 0);
      const sumWins = winningRRs.reduce((sum, rr) => sum + rr, 0);
      const sumLosses = Math.abs(losingRRs.reduce((sum, rr) => sum + rr, 0));
      const profitFactor = sumLosses > 0 
        ? (sumWins / sumLosses).toFixed(2)
        : (sumWins > 0 ? '∞' : 'N/A');

      return {
        asset_group_id: group.id,
        asset_group_name: group.name,
        total_trades: totalTrades,
        win_rate: winRate,
        profit_factor: profitFactor
      };
    });
  }, [tradesForAssetGroupAnalysis, breakdown?.asset_groups]);

  // ============================================
  // TRADE TYPE COMPARISON SECTION - Filter and Computed Data
  // ============================================

  // Filter trades for Trade Type Comparison section based on local setup filter
  // Uses globallyFilteredTrades as base, then applies local Setup filter
  const tradesForTradeTypeAnalysis = useMemo(() => {
    if (!tradeTypeSetupFilter) return globallyFilteredTrades;
    
    if (tradeTypeSetupFilter === '__no_setup__') {
      return globallyFilteredTrades.filter(t => !t.setup_id);
    }
    return globallyFilteredTrades.filter(t => t.setup_id === tradeTypeSetupFilter);
  }, [globallyFilteredTrades, tradeTypeSetupFilter]);

  // Get selected setup name for Trade Type display
  const tradeTypeSetupDisplayName = useMemo(() => {
    if (!tradeTypeSetupFilter) return null;
    if (tradeTypeSetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === tradeTypeSetupFilter);
    return setup?.name || null;
  }, [tradeTypeSetupFilter, setups]);

  // Compute trade type metrics locally from filtered trades
  const computedTradeTypeData = useMemo(() => {
    // Filter to closed trades only
    const closedTrades = tradesForTradeTypeAnalysis.filter(t => t.status === 'closed');
    
    // Helper function to determine trade type based on trade_type field
    const getTradeTypeCategory = (trade) => {
      const tradeType = trade.trade_type;
      if (tradeType === 'scalp') return 'scalp';
      if (tradeType === 'day_trade') return 'day_trade';
      if (tradeType === 'swing_trade') return 'swing_trade';
      // Fallback to day_trade if not set
      return 'day_trade';
    };

    // Calculate metrics for a given type
    const calcMetrics = (trades) => {
      const totalTrades = trades.length;
      if (totalTrades === 0) {
        return {
          total_trades: 0,
          win_rate: '0',
          profit_factor: 'N/A',
          average_rr: 'N/A'
        };
      }

      const wins = trades.filter(t => t.outcome === 'win').length;
      const winRate = ((wins / totalTrades) * 100).toFixed(1);

      // Calculate average RR
      const rrValues = trades
        .map(t => t.rr)
        .filter(rr => rr !== null && rr !== undefined && !isNaN(rr));
      const avgRR = rrValues.length > 0 
        ? (rrValues.reduce((sum, rr) => sum + rr, 0) / rrValues.length).toFixed(2)
        : 'N/A';

      // Calculate profit factor
      const winningRRs = rrValues.filter(rr => rr > 0);
      const losingRRs = rrValues.filter(rr => rr < 0);
      const sumWins = winningRRs.reduce((sum, rr) => sum + rr, 0);
      const sumLosses = Math.abs(losingRRs.reduce((sum, rr) => sum + rr, 0));
      const profitFactor = sumLosses > 0 
        ? (sumWins / sumLosses).toFixed(2)
        : (sumWins > 0 ? '∞' : 'N/A');

      return {
        total_trades: totalTrades,
        win_rate: winRate,
        profit_factor: profitFactor,
        average_rr: avgRR
      };
    };

    // Categorize trades
    const scalpTrades = closedTrades.filter(t => getTradeTypeCategory(t) === 'scalp');
    const dayTrades = closedTrades.filter(t => getTradeTypeCategory(t) === 'day_trade');
    const swingTrades = closedTrades.filter(t => getTradeTypeCategory(t) === 'swing_trade');

    return {
      scalp: calcMetrics(scalpTrades),
      day_trade: calcMetrics(dayTrades),
      swing_trade: calcMetrics(swingTrades)
    };
  }, [tradesForTradeTypeAnalysis]);

  // Filter trades for partial sections based on local setup filter
  // Uses globallyFilteredTrades as base, then applies local Setup filter
  const tradesForPartialAnalysis = useMemo(() => {
    if (!partialSetupFilter) return globallyFilteredTrades;
    
    // Filter by setup_id
    if (partialSetupFilter === '__no_setup__') {
      return globallyFilteredTrades.filter(t => !t.setup_id);
    }
    return globallyFilteredTrades.filter(t => t.setup_id === partialSetupFilter);
  }, [globallyFilteredTrades, partialSetupFilter]);

  // Get selected setup name for display
  const partialSetupDisplayName = useMemo(() => {
    if (!partialSetupFilter) return null;
    if (partialSetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === partialSetupFilter);
    return setup?.name || null;
  }, [partialSetupFilter, setups]);

  // Filter canceled trades for Planned Trades section based on local setup filter
  // Uses globallyFilteredTrades as base for percentage calculation
  const globallyFilteredCanceledTrades = useMemo(() => {
    return globallyFilteredTrades.filter(t => t.status === 'canceled');
  }, [globallyFilteredTrades]);

  const filteredCanceledTrades = useMemo(() => {
    if (!plannedSetupFilter) return globallyFilteredCanceledTrades;
    
    if (plannedSetupFilter === '__no_setup__') {
      return globallyFilteredCanceledTrades.filter(t => !t.setup_id);
    }
    return globallyFilteredCanceledTrades.filter(t => t.setup_id === plannedSetupFilter);
  }, [globallyFilteredCanceledTrades, plannedSetupFilter]);

  // Get total trades for the selected setup (for accurate percentage calculation)
  const totalTradesForPlannedFilter = useMemo(() => {
    if (!plannedSetupFilter) return globallyFilteredTrades.length;
    
    if (plannedSetupFilter === '__no_setup__') {
      return globallyFilteredTrades.filter(t => !t.setup_id).length;
    }
    return globallyFilteredTrades.filter(t => t.setup_id === plannedSetupFilter).length;
  }, [globallyFilteredTrades, plannedSetupFilter]);

  // Get selected setup name for Planned Trades display
  const plannedSetupDisplayName = useMemo(() => {
    if (!plannedSetupFilter) return null;
    if (plannedSetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === plannedSetupFilter);
    return setup?.name || null;
  }, [plannedSetupFilter, setups]);

  // Filter trades for Hedge Analytics based on setup filter
  // Uses globallyFilteredTrades as base, then applies local Setup filter
  const tradesForHedgeAnalysis = useMemo(() => {
    if (!hedgeSetupFilter) return globallyFilteredTrades;
    
    if (hedgeSetupFilter === '__no_setup__') {
      return globallyFilteredTrades.filter(t => !t.setup_id);
    }
    return globallyFilteredTrades.filter(t => t.setup_id === hedgeSetupFilter);
  }, [globallyFilteredTrades, hedgeSetupFilter]);

  // Get selected setup name for Hedge Analytics display
  const hedgeSetupDisplayName = useMemo(() => {
    if (!hedgeSetupFilter) return null;
    if (hedgeSetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === hedgeSetupFilter);
    return setup?.name || null;
  }, [hedgeSetupFilter, setups]);

  // Compute partial trades dynamically from filtered trades
  // Partial trade = closed trade where abs(profit) < hand_size
  const partialTradesData = useMemo(() => {
    // Filter to only closed trades (WIN or LOSS) with valid profit and hand_size
    const closedTrades = tradesForPartialAnalysis.filter(t => 
      t.status === 'closed' && 
      (t.outcome === 'win' || t.outcome === 'loss') &&
      t.profit_usd !== null && 
      t.profit_usd !== undefined &&
      t.hand_size !== null && 
      t.hand_size !== undefined &&
      t.hand_size > 0
    );

    // Classify partial trades
    const partialTrades = closedTrades.filter(t => {
      const absProfit = Math.abs(t.profit_usd);
      // Allow small tolerance for floating point comparison (0.01)
      return absProfit < (t.hand_size - 0.01);
    });

    // Separate into reduced losses and partial profits
    const reducedLosses = partialTrades.filter(t => t.outcome === 'loss');
    const partialProfits = partialTrades.filter(t => t.outcome === 'win');

    return {
      total: partialTrades.length,
      reducedLosses: reducedLosses.length,
      partialProfits: partialProfits.length,
      trades: partialTrades
    };
  }, [tradesForPartialAnalysis]);

  // Advanced partial analytics based on first-partial telemetry
  // Calculates profit given back after first partial action
  const partialAnalytics = useMemo(() => {
    // Filter to only completed trades (WIN or LOSS) with partial_used = true
    const completedTrades = tradesForPartialAnalysis.filter(t => 
      t.status === 'closed' && 
      (t.outcome === 'win' || t.outcome === 'loss')
    );

    // Trades with partial action
    const tradesWithPartial = completedTrades.filter(t => t.partial_used === true);

    // Trades with valid first_partial_rr for advanced calculations
    const validPartialTrades = tradesWithPartial.filter(t => 
      t.first_partial_rr !== null && 
      t.first_partial_rr !== undefined &&
      t.hand_size !== null &&
      t.hand_size !== undefined &&
      t.hand_size > 0 &&
      t.profit_usd !== null &&
      t.profit_usd !== undefined
    );

    // Calculate metrics for each valid trade
    const tradeMetrics = validPartialTrades.map(t => {
      const finalRealizedR = t.profit_usd / t.hand_size;
      const givenBackR = Math.max(0, t.first_partial_rr - finalRealizedR);
      const givenBackUsd = givenBackR * t.hand_size;
      
      return {
        firstPartialRR: t.first_partial_rr,
        finalRealizedR,
        givenBackR,
        givenBackUsd
      };
    });

    // Calculate aggregates
    const count = tradesWithPartial.length;
    const validCount = validPartialTrades.length;
    
    const avgFirstPartialRR = validCount > 0
      ? tradeMetrics.reduce((sum, m) => sum + m.firstPartialRR, 0) / validCount
      : 0;
    
    const avgFinalRealizedR = validCount > 0
      ? tradeMetrics.reduce((sum, m) => sum + m.finalRealizedR, 0) / validCount
      : 0;
    
    const avgGivenBackR = validCount > 0
      ? tradeMetrics.reduce((sum, m) => sum + m.givenBackR, 0) / validCount
      : 0;
    
    const totalGivenBackUsd = tradeMetrics.reduce((sum, m) => sum + m.givenBackUsd, 0);

    // Count trades that actually gave back profit (givenBackR > 0)
    const tradesWithGivenBack = tradeMetrics.filter(m => m.givenBackR > 0).length;

    return {
      completedCount: completedTrades.length,
      partialUsedCount: count,
      validCount,
      percentage: completedTrades.length > 0 
        ? ((count / completedTrades.length) * 100).toFixed(1)
        : 0,
      avgFirstPartialRR: avgFirstPartialRR.toFixed(2),
      avgFinalRealizedR: avgFinalRealizedR.toFixed(2),
      avgGivenBackR: avgGivenBackR.toFixed(2),
      totalGivenBackUsd: totalGivenBackUsd.toFixed(2),
      tradesWithGivenBack,
      // Keep raw values for insight calculation
      _rawAvgFirstPartialRR: avgFirstPartialRR,
      _rawAvgFinalRealizedR: avgFinalRealizedR,
      _rawAvgGivenBackR: avgGivenBackR,
      // Track sample size for low data warning
      _insufficientData: count < 3
    };
  }, [tradesForPartialAnalysis]);

  // Generate decision-oriented insight for partial analytics based on priority rules
  const partialInsight = useMemo(() => {
    const tradesWithPartial = partialAnalytics.partialUsedCount;
    
    if (tradesWithPartial === 0) {
      return null;
    }

    const avgFinalR = partialAnalytics._rawAvgFinalRealizedR;
    const avgGivenBackR = partialAnalytics._rawAvgGivenBackR;
    const avgFirstPartialRR = partialAnalytics._rawAvgFirstPartialRR;

    // Build supporting context line
    const contextLine = `Avg first partial: ${avgFirstPartialRR.toFixed(2)}R • Avg final: ${avgFinalR.toFixed(2)}R • Avg given back: ${avgGivenBackR.toFixed(2)}R`;

    // MINIMUM SAMPLE RULE — If tradesWithPartial < 3, show limited data message
    if (tradesWithPartial < 3) {
      return {
        type: 'info',
        icon: '📊',
        title: 'Limited data',
        message: "Not enough partial execution data to generate a reliable decision for this setup yet.",
        color: '#9E9E9E',
        context: null // Don't show context for limited data
      };
    }

    // PRIORITY 1 — Strong negative (giving back too much)
    if (avgGivenBackR >= 0.5) {
      return {
        type: 'warning',
        icon: '⚠️',
        title: 'Partial is hurting your edge',
        message: "You tend to give back too much after the first partial on this setup. Consider reducing partial usage or improving post-partial trade management.",
        color: '#FF3B30',
        context: contextLine
      };
    }

    // PRIORITY 2 — Winners should run longer
    if (avgFinalR > 0 && avgFirstPartialRR > avgFinalR && avgGivenBackR >= 0.25) {
      return {
        type: 'alert',
        icon: '📈',
        title: 'Let winners run longer',
        message: "Your trades often reach profit before partial, but a noticeable portion is given back afterward. This setup may benefit from better post-partial management.",
        color: '#FF9800',
        context: contextLine
      };
    }

    // PRIORITY 3 — Efficient partial
    if (avgGivenBackR < 0.15 && avgFinalR > 0) {
      return {
        type: 'success',
        icon: '✅',
        title: 'Partial execution is efficient',
        message: "On this setup, partial execution is protecting profits without giving much back.",
        color: '#00E676',
        context: contextLine
      };
    }

    // DEFAULT — Balanced
    return {
      type: 'neutral',
      icon: '⚖️',
      title: 'Balanced execution',
      message: "Your partial execution on this setup looks balanced. You secure profits, but still give some back.",
      color: '#00B8D4',
      context: contextLine
    };
  }, [partialAnalytics]);

  // Compute partial vs non-partial comparison for decision block
  const partialDecision = useMemo(() => {
    // Get completed trades from the filtered subset
    const completedTrades = tradesForPartialAnalysis.filter(t => 
      t.status === 'closed' && 
      (t.outcome === 'win' || t.outcome === 'loss') &&
      t.profit_usd !== null &&
      t.profit_usd !== undefined &&
      t.hand_size !== null &&
      t.hand_size !== undefined &&
      t.hand_size > 0
    );

    // Separate into trades with partial and without partial
    const tradesWithPartial = completedTrades.filter(t => t.partial_used === true);
    const tradesWithoutPartial = completedTrades.filter(t => !t.partial_used);

    // Check minimum sample sizes
    const hasEnoughPartial = tradesWithPartial.length >= 3;
    const hasEnoughNoPartial = tradesWithoutPartial.length >= 3;

    if (!hasEnoughPartial || !hasEnoughNoPartial) {
      return {
        type: 'insufficient',
        title: 'Not enough data to evaluate partial vs non-partial yet',
        subtext: `Partial trades: ${tradesWithPartial.length} • Non-partial trades: ${tradesWithoutPartial.length}`,
        color: '#9E9E9E',
        icon: '📊',
        avgPartialR: null,
        avgNoPartialR: null,
        deltaR: null
      };
    }

    // Calculate average R for each group
    const avgPartialR = tradesWithPartial.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / tradesWithPartial.length;
    const avgNoPartialR = tradesWithoutPartial.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / tradesWithoutPartial.length;
    const deltaR = avgPartialR - avgNoPartialR;

    // CASE 1 — Partial is worse
    if (deltaR <= -0.25) {
      return {
        type: 'negative',
        title: 'Better to AVOID partial on this setup',
        subtext: 'Trades without partial are outperforming trades with partial.',
        color: '#FF3B30',
        icon: '🚫',
        avgPartialR,
        avgNoPartialR,
        deltaR,
        partialCount: tradesWithPartial.length,
        noPartialCount: tradesWithoutPartial.length
      };
    }

    // CASE 2 — Partial is better
    if (deltaR >= 0.25) {
      return {
        type: 'positive',
        title: 'Partial is BENEFICIAL on this setup',
        subtext: 'Trades with partial are outperforming trades without partial.',
        color: '#00E676',
        icon: '✅',
        avgPartialR,
        avgNoPartialR,
        deltaR,
        partialCount: tradesWithPartial.length,
        noPartialCount: tradesWithoutPartial.length
      };
    }

    // CASE 3 — No meaningful difference
    return {
      type: 'neutral',
      title: 'Partial makes no significant difference',
      subtext: 'Performance is similar with and without partial.',
      color: '#00B8D4',
      icon: '⚖️',
      avgPartialR,
      avgNoPartialR,
      deltaR,
      partialCount: tradesWithPartial.length,
      noPartialCount: tradesWithoutPartial.length
    };
  }, [tradesForPartialAnalysis]);

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (tfSortColumn !== column) {
      return <span className="text-white/20 ml-1">⇅</span>;
    }
    if (tfSortDirection === 'desc') {
      return <CaretDown size={12} weight="bold" className="inline ml-1 text-[#00E676]" />;
    }
    return <CaretUp size={12} weight="bold" className="inline ml-1 text-[#00E676]" />;
  };

  // Determine best timeframe (only timeframes with at least 1 trade and valid metrics)
  // Uses filtered data when setup filter is active
  const bestTimeframe = useMemo(() => {
    // Use computed data when setup filter is active, otherwise use breakdown data
    const sourceData = timeframeSetupFilter 
      ? computedTimeframeData 
      : breakdown?.timeframes;
    
    if (!sourceData || sourceData.length === 0) return null;
    
    const eligible = sourceData.filter(tf => 
      tf.total_trades > 0 && 
      tf.profit_factor !== 'N/A' && 
      tf.profit_factor !== '∞' &&
      tf.profit_factor !== null
    );
    
    if (eligible.length === 0) return null;
    
    // Sort by: Profit Factor → Win Rate → Avg RR → Trades (all descending)
    const sorted = [...eligible].sort((a, b) => {
      // 1. Highest Profit Factor
      const pfA = parseFloat(a.profit_factor) || 0;
      const pfB = parseFloat(b.profit_factor) || 0;
      if (pfB !== pfA) return pfB - pfA;
      
      // 2. Highest Win Rate (Excluding Even)
      const wrA = parseFloat(a.win_rate_excluding_even ?? a.win_rate) || 0;
      const wrB = parseFloat(b.win_rate_excluding_even ?? b.win_rate) || 0;
      if (wrB !== wrA) return wrB - wrA;
      
      // 3. Highest Avg RR
      const rrA = a.average_rr === 'N/A' ? 0 : parseFloat(a.average_rr) || 0;
      const rrB = b.average_rr === 'N/A' ? 0 : parseFloat(b.average_rr) || 0;
      if (rrB !== rrA) return rrB - rrA;
      
      // 4. Highest Trades
      return (b.total_trades || 0) - (a.total_trades || 0);
    });
    
    return sorted[0]?.timeframe || null;
  }, [breakdown?.timeframes, computedTimeframeData, timeframeSetupFilter]);

  // Calculate cancel reason breakdown for the main summary section (uses filtered data)
  const cancelReasonBreakdown = useMemo(() => {
    if (filteredCanceledTrades.length === 0) return null;
    
    const cancelReasonOptions = [
      { value: 'setup_not_triggered', label: 'Setup not triggered' },
      { value: 'no_entry_reach', label: 'Did not reach entry' },
      { value: 'skipped_execution', label: 'Skipped execution' }
    ];
    
    const counts = {
      setup_not_triggered: 0,
      no_entry_reach: 0,
      skipped_execution: 0
    };
    
    // Count reasons - normalize null/undefined/unknown to "skipped_execution"
    filteredCanceledTrades.forEach(trade => {
      const reason = trade.cancel_reason;
      if (reason && counts.hasOwnProperty(reason)) {
        counts[reason]++;
      } else {
        // Treat null/undefined/unknown as "skipped_execution"
        counts.skipped_execution++;
      }
    });
    
    const total = filteredCanceledTrades.length;
    
    // Always return all 3 categories (even if 0)
    return cancelReasonOptions.map(opt => ({
      label: opt.label,
      count: counts[opt.value],
      percentage: ((counts[opt.value] / total) * 100).toFixed(0)
    }));
  }, [filteredCanceledTrades]);

  // Local insight for Planned Trades (Not Triggered) section (uses filtered data)
  const plannedTradesInsight = useMemo(() => {
    const totalNotTriggered = filteredCanceledTrades.length;
    const totalForFilter = totalTradesForPlannedFilter;
    const isSetupFiltered = !!plannedSetupFilter;
    
    // Different minimum thresholds for global vs setup-specific
    // Global: at least 10 canceled OR 15 total trades
    // Setup-specific: at least 4 canceled OR 6 total trades (smaller but meaningful samples)
    const minCanceled = isSetupFiltered ? 4 : 10;
    const minTotal = isSetupFiltered ? 6 : 15;
    
    if (totalNotTriggered < minCanceled && totalForFilter < minTotal) return null;
    if (totalNotTriggered === 0) return null;
    
    // Calculate breakdown counts
    const counts = {
      setup_not_triggered: 0,
      no_entry_reach: 0,
      skipped_execution: 0
    };
    
    filteredCanceledTrades.forEach(trade => {
      const reason = trade.cancel_reason;
      if (reason && counts.hasOwnProperty(reason)) {
        counts[reason]++;
      } else {
        counts.skipped_execution++;
      }
    });
    
    // Calculate rates using filtered totals (setup-specific denominator)
    const notTriggeredRate = totalForFilter > 0 ? totalNotTriggered / totalForFilter : 0;
    const setupRate = totalNotTriggered > 0 ? counts.setup_not_triggered / totalNotTriggered : 0;
    const entryRate = totalNotTriggered > 0 ? counts.no_entry_reach / totalNotTriggered : 0;
    const skippedRate = totalNotTriggered > 0 ? counts.skipped_execution / totalNotTriggered : 0;
    
    // Determine severity (same logic for both global and setup-specific)
    let severity;
    if (notTriggeredRate >= 0.4) {
      severity = 'high';
    } else if (notTriggeredRate >= 0.2) {
      severity = 'medium';
    } else {
      severity = 'low';
    }
    
    // Determine dominant cause with tie handling
    // If top two reasons are within 5%, treat as a tie
    const rates = [
      { cause: 'entry', rate: entryRate },
      { cause: 'setup', rate: setupRate },
      { cause: 'execution', rate: skippedRate }
    ].sort((a, b) => b.rate - a.rate);
    
    const topRate = rates[0].rate;
    const secondRate = rates[1].rate;
    const TIE_THRESHOLD = 0.05; // 5% difference threshold
    
    let dominantCause;
    let isTie = false;
    let tieType = null;
    
    if (topRate - secondRate <= TIE_THRESHOLD && topRate > 0 && secondRate > 0) {
      // Tie between top two reasons
      isTie = true;
      const causes = [rates[0].cause, rates[1].cause].sort();
      if (causes.includes('entry') && causes.includes('setup')) {
        tieType = 'entry_setup';
      } else if (causes.includes('entry') && causes.includes('execution')) {
        tieType = 'entry_execution';
      } else if (causes.includes('setup') && causes.includes('execution')) {
        tieType = 'setup_execution';
      }
      dominantCause = 'tie';
    } else {
      dominantCause = rates[0].cause;
    }
    
    // Setup name for contextual messages
    const setupName = plannedSetupDisplayName;
    
    // Generate message based on dominant cause, severity, and tie status
    let title, message, icon, color;
    
    if (isTie) {
      // Handle tie cases with combined insights
      if (severity === 'high') {
        icon = "⚠️";
        color = "#FF9800";
        if (tieType === 'entry_setup') {
          title = setupName 
            ? `${setupName} setups often fail before execution`
            : "Your setups often fail before execution";
          message = "Your entries and setup conditions may need refinement";
        } else if (tieType === 'entry_execution') {
          title = setupName
            ? `${setupName} trades fail at entry or get skipped`
            : "Trades fail at entry or get skipped";
          message = "Review your entry levels and execution discipline";
        } else {
          title = setupName
            ? `${setupName} setups fail to trigger or get skipped`
            : "Setups fail to trigger or get skipped";
          message = "Review your setup conditions and execution consistency";
        }
      } else if (severity === 'medium') {
        icon = "📊";
        color = "#FFA726";
        if (tieType === 'entry_setup') {
          title = setupName
            ? `${setupName} setups sometimes fail before execution`
            : "Some setups fail before execution";
          message = "Consider refining both entry levels and setup conditions";
        } else if (tieType === 'entry_execution') {
          title = setupName
            ? `${setupName} trades sometimes miss entry or get skipped`
            : "Some trades miss entry or get skipped";
          message = "Review your entry precision and execution commitment";
        } else {
          title = setupName
            ? `${setupName} setups sometimes don't trigger or get skipped`
            : "Some setups don't trigger or get skipped";
          message = "Review your setup quality and execution discipline";
        }
      } else {
        icon = "✓";
        color = "#00E676";
        title = setupName
          ? `${setupName} planned trades are performing well`
          : "Your planned trades are performing well";
        message = "No single dominant failure reason stands out";
      }
    } else if (dominantCause === 'entry') {
      if (severity === 'high') {
        title = setupName 
          ? `${setupName} setups often don't reach entry`
          : "Most of your setups don't reach entry";
        message = "Your entry levels may be too far from price action";
        icon = "⚠️";
        color = "#FF9800";
      } else if (severity === 'medium') {
        title = setupName
          ? `Some ${setupName} setups are not reaching entry`
          : "Some setups are not reaching entry";
        message = "You may want to refine your entry precision";
        icon = "📊";
        color = "#FFA726";
      } else {
        title = setupName
          ? `${setupName} entry levels are generally reached`
          : "Your entry levels are generally being reached";
        message = "Most planned trades are getting proper entries";
        icon = "✓";
        color = "#00E676";
      }
    } else if (dominantCause === 'setup') {
      if (severity === 'high') {
        title = setupName
          ? `Many ${setupName} setups never trigger`
          : "Many of your setups never trigger";
        message = "You may need to refine your setup conditions";
        icon = "⚠️";
        color = "#FF9800";
      } else if (severity === 'medium') {
        title = setupName
          ? `Some ${setupName} setups are not triggering`
          : "Some setups are not triggering";
        message = "Review your setup quality and timing";
        icon = "📊";
        color = "#FFA726";
      } else {
        title = setupName
          ? `${setupName} setups are triggering consistently`
          : "Your setups are triggering consistently";
        message = "Setup conditions are well-aligned with market";
        icon = "✓";
        color = "#00E676";
      }
    } else {
      // execution (skipped)
      if (severity === 'high') {
        title = setupName
          ? `You're skipping many ${setupName} trades`
          : "You're skipping many planned trades";
        message = "This may indicate hesitation or lack of discipline";
        icon = "⚠️";
        color = "#FF9800";
      } else if (severity === 'medium') {
        title = setupName
          ? `You occasionally skip ${setupName} setups`
          : "You occasionally skip valid setups";
        message = "Improving execution consistency may help";
        icon = "📊";
        color = "#FFA726";
      } else {
        title = setupName
          ? `You're executing ${setupName} trades consistently`
          : "You're executing your planned trades consistently";
        message = "Good discipline in following your trading plan";
        icon = "✓";
        color = "#00E676";
      }
    }
    
    return { title, message, icon, color, severity, dominantCause, isTie, tieType };
  }, [filteredCanceledTrades, totalTradesForPlannedFilter, plannedSetupDisplayName, plannedSetupFilter]);

  // ============================================
  // EXECUTION QUALITY METRICS
  // ============================================
  
  // Get selected setup name for Execution Quality display
  const executionSetupDisplayName = useMemo(() => {
    if (!executionSetupFilter || executionSetupFilter === 'all') return null;
    if (executionSetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === executionSetupFilter);
    return setup?.name || null;
  }, [executionSetupFilter, setups]);

  // Filter trades for Execution Quality analysis
  const tradesForExecutionAnalysis = useMemo(() => {
    // Only consider closed trades
    const closedTrades = allTrades.filter(t => t.status === 'closed');
    
    if (!executionSetupFilter || executionSetupFilter === 'all') return closedTrades;
    
    if (executionSetupFilter === '__no_setup__') {
      return closedTrades.filter(t => !t.setup_id);
    }
    return closedTrades.filter(t => t.setup_id === executionSetupFilter);
  }, [allTrades, executionSetupFilter]);

  // Execution Quality computed data
  const executionQualityData = useMemo(() => {
    const trades = tradesForExecutionAnalysis;
    const total = trades.length;
    
    if (total === 0) {
      return {
        total: 0,
        eligibleCount: 0,
        followedPlanPercent: 0,
        followedPlanCount: 0,
        disciplineLevel: 'none',
        avgHoldTimeMinutes: 0,
        expectedHoldTimeMinutes: 0,
        holdTimeDelta: 0,
        holdTimeInterpretation: 'none',
        riskViolationPercent: 0,
        earlyExitPercent: 0,
        overholdPercent: 0,
        insight: null
      };
    }

    // ============================================
    // 1. FOLLOWED PLAN CALCULATION
    // A trade "Followed Plan" if BOTH:
    //   A. Risk Respected: Did not exceed planned stop meaningfully
    //   B. Exit Respected: realized_rr >= planned_rr * 0.8 (don't penalize upside)
    // ============================================
    let followedPlanCount = 0;
    let riskViolationCount = 0;
    let earlyExitCount = 0;
    let eligibleTrades = 0;
    let overholdCount = 0;
    
    trades.forEach(trade => {
      const hasRR = trade.rr !== null && trade.rr !== undefined;
      const hasOriginalRR = trade.original_rr !== null && trade.original_rr !== undefined;
      const hasStop = trade.stop_loss !== null && trade.stop_loss !== undefined;
      
      // Only include trades where we can meaningfully evaluate plan adherence
      // Need both realized RR and either original RR or stop defined
      const isEligible = hasRR && (hasOriginalRR || hasStop);
      
      if (!isEligible) return;
      
      eligibleTrades++;
      
      const realizedRR = trade.rr;
      const plannedRR = trade.original_rr || Math.abs(realizedRR); // Fallback to realized if no planned
      
      // A. RISK RESPECTED
      // For losses: check if loss exceeded planned stop (-1R) by more than 30%
      // A loss of -1.3R or worse means risk was violated
      let riskRespected = true;
      if (trade.outcome === 'loss') {
        // If realized loss is worse than -1.3R (30% beyond stop), risk was violated
        if (realizedRR < -1.3) {
          riskRespected = false;
          riskViolationCount++;
        }
      }
      
      // B. EXIT RESPECTED
      // realized_rr >= planned_rr * 0.8 (for wins)
      // Don't penalize outperformance - if realized > planned, that's good
      // For losses at stop level, this is already covered by risk check
      let exitRespected = true;
      if (trade.outcome === 'win' && hasOriginalRR && plannedRR > 0) {
        // Early exit = got less than 80% of planned RR
        if (realizedRR < plannedRR * 0.8) {
          exitRespected = false;
          earlyExitCount++;
        }
        // Note: realizedRR > plannedRR is NOT penalized (outperformance is good)
      } else if (trade.outcome === 'loss') {
        // For losses, exit is respected if they hit stop (not panic exit)
        // Panic exit = loss that's too small (e.g., -0.3R when stop was -1R)
        if (realizedRR > -0.5 && realizedRR < 0) {
          // This indicates a panic exit before stop
          exitRespected = false;
          earlyExitCount++;
        }
      }
      
      // FOLLOWED PLAN = Risk Respected AND Exit Respected
      if (riskRespected && exitRespected) {
        followedPlanCount++;
      }
    });
    
    const followedPlanPercent = eligibleTrades > 0 ? Math.round((followedPlanCount / eligibleTrades) * 100) : 0;
    const riskViolationPercent = eligibleTrades > 0 ? Math.round((riskViolationCount / eligibleTrades) * 100) : 0;
    const earlyExitPercent = eligibleTrades > 0 ? Math.round((earlyExitCount / eligibleTrades) * 100) : 0;
    
    // Discipline level based on followed plan percentage
    let disciplineLevel;
    if (followedPlanPercent >= 70) {
      disciplineLevel = 'high';
    } else if (followedPlanPercent >= 50) {
      disciplineLevel = 'moderate';
    } else {
      disciplineLevel = 'low';
    }

    // ============================================
    // 2. HOLD TIME CALCULATION (SPLIT BY TRADE TYPE)
    // ============================================
    
    // Day Trade hold times
    const dayTrades = trades.filter(t => t.trade_type === 'day_trade');
    let dayTradeHoldTimeMs = 0;
    let dayTradesWithHoldTime = 0;
    const dayTradeHoldTimes = [];
    
    dayTrades.forEach(trade => {
      if (trade.created_at && trade.closed_at) {
        const createdAt = new Date(trade.created_at);
        const closedAt = new Date(trade.closed_at);
        const holdTimeMs = closedAt - createdAt;
        
        if (holdTimeMs > 0 && holdTimeMs < 24 * 60 * 60 * 1000 * 30) {
          dayTradeHoldTimeMs += holdTimeMs;
          dayTradesWithHoldTime++;
          dayTradeHoldTimes.push({ holdTimeMs, outcome: trade.outcome });
        }
      }
    });
    
    // Swing Trade hold times
    const swingTrades = trades.filter(t => t.trade_type === 'swing_trade');
    let swingTradeHoldTimeMs = 0;
    let swingTradesWithHoldTime = 0;
    const swingTradeHoldTimes = [];
    
    swingTrades.forEach(trade => {
      if (trade.created_at && trade.closed_at) {
        const createdAt = new Date(trade.created_at);
        const closedAt = new Date(trade.closed_at);
        const holdTimeMs = closedAt - createdAt;
        
        if (holdTimeMs > 0 && holdTimeMs < 24 * 60 * 60 * 1000 * 30) {
          swingTradeHoldTimeMs += holdTimeMs;
          swingTradesWithHoldTime++;
          swingTradeHoldTimes.push({ holdTimeMs, outcome: trade.outcome });
        }
      }
    });
    
    // Helper function to compute hold time metrics for a trade type
    const computeHoldTimeMetrics = (holdTimesData, totalHoldMs, tradesCount) => {
      if (tradesCount < 3) {
        return {
          avgHoldMinutes: 0,
          expectedHoldMinutes: 0,
          interpretation: 'insufficient',
          tradesCount: tradesCount
        };
      }
      
      const avgHoldMinutes = Math.round(totalHoldMs / tradesCount / 60000);
      
      // Calculate average hold time for wins and losses separately
      const wins = holdTimesData.filter(h => h.outcome === 'win');
      const losses = holdTimesData.filter(h => h.outcome === 'loss');
      
      let avgWinHoldMs = 0;
      let avgLossHoldMs = 0;
      
      if (wins.length > 0) {
        avgWinHoldMs = wins.reduce((sum, h) => sum + h.holdTimeMs, 0) / wins.length;
      }
      if (losses.length > 0) {
        avgLossHoldMs = losses.reduce((sum, h) => sum + h.holdTimeMs, 0) / losses.length;
      }
      
      // Expected = midpoint between avg win and avg loss hold times
      let expectedHoldMinutes = avgHoldMinutes;
      if (wins.length >= 2 && losses.length >= 2) {
        expectedHoldMinutes = Math.round((avgWinHoldMs + avgLossHoldMs) / 2 / 60000);
      }
      
      // Interpretation
      const deltaPercent = expectedHoldMinutes > 0 
        ? Math.abs(avgHoldMinutes - expectedHoldMinutes) / expectedHoldMinutes 
        : 0;
      
      let interpretation;
      if (deltaPercent <= 0.2) {
        interpretation = 'aligned';
      } else if (avgHoldMinutes < expectedHoldMinutes) {
        interpretation = 'early';
      } else {
        interpretation = 'long';
      }
      
      return {
        avgHoldMinutes,
        expectedHoldMinutes,
        interpretation,
        tradesCount
      };
    };
    
    const dayTradeMetrics = computeHoldTimeMetrics(dayTradeHoldTimes, dayTradeHoldTimeMs, dayTradesWithHoldTime);
    const swingTradeMetrics = computeHoldTimeMetrics(swingTradeHoldTimes, swingTradeHoldTimeMs, swingTradesWithHoldTime);
    
    // Legacy combined hold time for overholding calculation
    let totalHoldTimeMs = dayTradeHoldTimeMs + swingTradeHoldTimeMs;
    let tradesWithHoldTime = dayTradesWithHoldTime + swingTradesWithHoldTime;
    const allHoldTimes = [...dayTradeHoldTimes, ...swingTradeHoldTimes];
    
    const avgHoldTimeMinutes = tradesWithHoldTime > 0 
      ? Math.round(totalHoldTimeMs / tradesWithHoldTime / 60000) 
      : 0;
    
    // Expected hold time using median
    let expectedHoldTimeMinutes = avgHoldTimeMinutes;
    if (allHoldTimes.length >= 5) {
      const sortedTimes = allHoldTimes.map(h => h.holdTimeMs).sort((a, b) => a - b);
      const medianIndex = Math.floor(sortedTimes.length / 2);
      const medianHoldTimeMs = sortedTimes.length % 2 === 0
        ? (sortedTimes[medianIndex - 1] + sortedTimes[medianIndex]) / 2
        : sortedTimes[medianIndex];
      expectedHoldTimeMinutes = Math.round(medianHoldTimeMs / 60000);
    }
    
    // Calculate overholding 
    allHoldTimes.forEach(h => {
      if (h.holdTimeMs > expectedHoldTimeMinutes * 60000 * 1.5) {
        overholdCount++;
      }
    });
    const overholdPercent = tradesWithHoldTime > 0 
      ? Math.round((overholdCount / tradesWithHoldTime) * 100) 
      : 0;
    
    // Combined hold time interpretation for insights
    const holdTimeDelta = avgHoldTimeMinutes - expectedHoldTimeMinutes;
    const deltaPercent = expectedHoldTimeMinutes > 0 
      ? Math.abs(holdTimeDelta) / expectedHoldTimeMinutes 
      : 0;
    
    let holdTimeInterpretation;
    if (deltaPercent <= 0.2) {
      holdTimeInterpretation = 'aligned';
    } else if (avgHoldTimeMinutes < expectedHoldTimeMinutes) {
      holdTimeInterpretation = 'early';
    } else {
      holdTimeInterpretation = 'long';
    }

    // ============================================
    // 3. GENERATE INSIGHT
    // ============================================
    let insight = null;
    
    if (eligibleTrades >= 5) {
      if (riskViolationPercent > 20) {
        insight = {
          type: 'warning',
          text: `Risk management needs attention: ${riskViolationPercent}% of trades exceeded planned stop loss. Consider tighter risk controls.`,
          icon: '⚠️',
          color: '#FF3B30'
        };
      } else if (earlyExitPercent > 30) {
        insight = {
          type: 'warning',
          text: `Early exits are reducing your edge. ${earlyExitPercent}% of trades closed before reaching the planned target.`,
          icon: '⏱️',
          color: '#FF9800'
        };
      } else {
        // Generate contextual hold time insight based on split trade type metrics
        const dayData = dayTradeMetrics.tradesCount >= 3;
        const swingData = swingTradeMetrics.tradesCount >= 3;
        const dayInterpretation = dayData ? dayTradeMetrics.interpretation : null;
        const swingInterpretation = swingData ? swingTradeMetrics.interpretation : null;
        
        let holdTimeInsight = null;
        
        // Case A: Both aligned
        if (dayInterpretation === 'aligned' && swingInterpretation === 'aligned') {
          holdTimeInsight = {
            type: 'positive',
            text: 'Your hold time is well aligned across both trading styles.',
            icon: '✓',
            color: '#00E676'
          };
        }
        // Case B: Only Day Trade is off
        else if (dayInterpretation === 'long' && (!swingData || swingInterpretation === 'aligned')) {
          holdTimeInsight = {
            type: 'warning',
            text: `You're holding day trades longer than expected (${formatHoldTime(dayTradeMetrics.avgHoldMinutes)} vs ${formatHoldTime(dayTradeMetrics.expectedHoldMinutes)}).`,
            icon: '⏳',
            color: '#FFA726'
          };
        }
        else if (dayInterpretation === 'early' && (!swingData || swingInterpretation === 'aligned')) {
          holdTimeInsight = {
            type: 'warning',
            text: `You're exiting day trades earlier than expected (${formatHoldTime(dayTradeMetrics.avgHoldMinutes)} vs ${formatHoldTime(dayTradeMetrics.expectedHoldMinutes)}).`,
            icon: '⏱️',
            color: '#FF9800'
          };
        }
        // Case C: Only Swing Trade is off
        else if (swingInterpretation === 'long' && (!dayData || dayInterpretation === 'aligned')) {
          holdTimeInsight = {
            type: 'warning',
            text: `You're holding swing trades longer than expected (${formatHoldTime(swingTradeMetrics.avgHoldMinutes)} vs ${formatHoldTime(swingTradeMetrics.expectedHoldMinutes)}).`,
            icon: '⏳',
            color: '#FFA726'
          };
        }
        else if (swingInterpretation === 'early' && (!dayData || dayInterpretation === 'aligned')) {
          holdTimeInsight = {
            type: 'warning',
            text: `You're exiting swing trades earlier than expected (${formatHoldTime(swingTradeMetrics.avgHoldMinutes)} vs ${formatHoldTime(swingTradeMetrics.expectedHoldMinutes)}).`,
            icon: '⏱️',
            color: '#FF9800'
          };
        }
        // Case D: Both off in same direction
        else if (dayInterpretation === 'long' && swingInterpretation === 'long') {
          holdTimeInsight = {
            type: 'warning',
            text: 'You\'re holding trades longer than expected across both styles.',
            icon: '⏳',
            color: '#FFA726'
          };
        }
        else if (dayInterpretation === 'early' && swingInterpretation === 'early') {
          holdTimeInsight = {
            type: 'warning',
            text: 'You\'re exiting trades earlier than expected across both styles.',
            icon: '⏱️',
            color: '#FF9800'
          };
        }
        // Case E: Mixed behavior
        else if (dayData && swingData && dayInterpretation !== swingInterpretation) {
          holdTimeInsight = {
            type: 'neutral',
            text: 'Your hold-time behavior differs between day trades and swing trades.',
            icon: '📊',
            color: '#90CAF9'
          };
        }
        
        // Only use hold time insight if we generated one, otherwise fall back to discipline insights
        if (holdTimeInsight && (dayInterpretation !== 'aligned' || swingInterpretation !== 'aligned')) {
          insight = holdTimeInsight;
        } else if (followedPlanPercent >= 70) {
          insight = holdTimeInsight || {
            type: 'positive',
            text: `Strong discipline. You're respecting your risk and exit plans consistently.`,
            icon: '✓',
            color: '#00E676'
          };
        } else if (followedPlanPercent >= 50) {
          insight = {
            type: 'neutral',
            text: `Moderate discipline at ${followedPlanPercent}%. Focus on reducing early exits and respecting stops.`,
            icon: '📊',
            color: '#90CAF9'
          };
        } else {
          insight = {
            type: 'warning',
            text: `Plan adherence is low at ${followedPlanPercent}%. Review your risk management and exit discipline.`,
            icon: '⚠️',
            color: '#FF9800'
          };
        }
      }
    }

    return {
      total,
      eligibleCount: eligibleTrades,
      followedPlanPercent,
      followedPlanCount,
      disciplineLevel,
      avgHoldTimeMinutes,
      expectedHoldTimeMinutes,
      holdTimeDelta,
      holdTimeInterpretation,
      // New split metrics
      dayTradeMetrics,
      swingTradeMetrics,
      riskViolationPercent,
      earlyExitPercent,
      overholdPercent,
      insight
    };
  }, [tradesForExecutionAnalysis]);

  // ============================================
  // HEDGE ANALYTICS METRICS
  // ============================================
  const hedgeAnalyticsData = useMemo(() => {
    // Filter to only closed trades with hedge (from setup-filtered trades)
    const hedgedTrades = tradesForHedgeAnalysis.filter(t => 
      t.status === 'closed' && 
      t.has_hedge === true &&
      t.pnl_without_hedge !== null &&
      t.pnl_with_hedge !== null
    );
    
    const totalHedgedTrades = hedgedTrades.length;
    
    if (totalHedgedTrades === 0) {
      return {
        totalHedgedTrades: 0,
        beneficialCount: 0,
        harmfulCount: 0,
        neutralCount: 0,
        beneficialPercent: 0,
        harmfulPercent: 0,
        totalHedgeImpact: 0,
        avgHedgeImpact: 0,
        avgBeneficialImpact: 0,
        avgHarmfulImpact: 0,
        totalWithoutHedge: 0,
        totalWithHedge: 0,
        netDifference: 0,
        verdict: null,
        insight: null
      };
    }
    
    // Classify trades
    const beneficialTrades = hedgedTrades.filter(t => t.hedge_classification === 'beneficial');
    const harmfulTrades = hedgedTrades.filter(t => t.hedge_classification === 'harmful');
    const neutralTrades = hedgedTrades.filter(t => t.hedge_classification === 'neutral');
    
    const beneficialCount = beneficialTrades.length;
    const harmfulCount = harmfulTrades.length;
    const neutralCount = neutralTrades.length;
    
    const beneficialPercent = Math.round((beneficialCount / totalHedgedTrades) * 100);
    const harmfulPercent = Math.round((harmfulCount / totalHedgedTrades) * 100);
    
    // Calculate total impacts
    const totalWithoutHedge = hedgedTrades.reduce((sum, t) => sum + (t.pnl_without_hedge || 0), 0);
    const totalWithHedge = hedgedTrades.reduce((sum, t) => sum + (t.pnl_with_hedge || 0), 0);
    const totalHedgeImpact = hedgedTrades.reduce((sum, t) => sum + (t.hedge_impact || 0), 0);
    
    const avgHedgeImpact = totalHedgeImpact / totalHedgedTrades;
    const netDifference = totalWithHedge - totalWithoutHedge;
    
    // Calculate average beneficial and harmful impacts
    const avgBeneficialImpact = beneficialCount > 0 
      ? beneficialTrades.reduce((sum, t) => sum + (t.hedge_impact || 0), 0) / beneficialCount 
      : 0;
    const avgHarmfulImpact = harmfulCount > 0 
      ? harmfulTrades.reduce((sum, t) => sum + (t.hedge_impact || 0), 0) / harmfulCount 
      : 0;
    
    // Determine overall verdict (context-aware for setup filter)
    let verdict = null;
    const setupContext = hedgeSetupFilter ? ' for this setup' : '';
    
    if (totalHedgedTrades >= 5) {
      if (netDifference > 0 && beneficialPercent >= 60) {
        verdict = {
          type: 'positive',
          text: `Hedging is improving your results${setupContext}`,
          color: '#00E676'
        };
      } else if (netDifference < 0 && harmfulPercent >= 60) {
        verdict = {
          type: 'negative',
          text: `Hedging is hurting your results${setupContext}`,
          color: '#FF3B30'
        };
      } else {
        verdict = {
          type: 'neutral',
          text: `Hedging has mixed results${setupContext}`,
          color: '#FFA726'
        };
      }
    }
    
    // Generate insight (context-aware for setup filter)
    let insight = null;
    if (totalHedgedTrades >= 3) {
      if (netDifference > 0) {
        insight = {
          type: 'success',
          text: hedgeSetupFilter 
            ? `Hedges in this setup have added $${netDifference.toFixed(2)} to your results. ${beneficialPercent}% were beneficial.`
            : `Your hedges have added $${netDifference.toFixed(2)} to your results. ${beneficialPercent}% of hedged trades were beneficial.`,
          color: '#00E676'
        };
      } else if (netDifference < 0) {
        insight = {
          type: 'warning',
          text: hedgeSetupFilter
            ? `Hedges in this setup have cost you $${Math.abs(netDifference).toFixed(2)}. Consider reviewing your hedge strategy for this setup.`
            : `Your hedges have cost you $${Math.abs(netDifference).toFixed(2)}. Consider reviewing your hedge strategy.`,
          color: '#FF3B30'
        };
      } else {
        insight = {
          type: 'neutral',
          text: hedgeSetupFilter
            ? 'Hedges in this setup have had a neutral impact overall.'
            : 'Your hedges have had a neutral impact overall.',
          color: '#9E9E9E'
        };
      }
    } else if (totalHedgedTrades > 0 && totalHedgedTrades < 3) {
      // Low hedge usage insight for setup filter
      insight = {
        type: 'info',
        text: hedgeSetupFilter
          ? `Hedge usage is low for this setup (${totalHedgedTrades} trade${totalHedgedTrades > 1 ? 's' : ''}). More data needed for reliable analysis.`
          : `Limited hedge data available. Add more hedges for meaningful insights.`,
        color: '#9E9E9E'
      };
    }
    
    return {
      totalHedgedTrades,
      beneficialCount,
      harmfulCount,
      neutralCount,
      beneficialPercent,
      harmfulPercent,
      totalHedgeImpact: Math.round(totalHedgeImpact * 100) / 100,
      avgHedgeImpact: Math.round(avgHedgeImpact * 100) / 100,
      avgBeneficialImpact: Math.round(avgBeneficialImpact * 100) / 100,
      avgHarmfulImpact: Math.round(avgHarmfulImpact * 100) / 100,
      totalWithoutHedge: Math.round(totalWithoutHedge * 100) / 100,
      totalWithHedge: Math.round(totalWithHedge * 100) / 100,
      netDifference: Math.round(netDifference * 100) / 100,
      verdict,
      insight
    };
  }, [tradesForHedgeAnalysis, hedgeSetupFilter]);

  // ============================================
  // HAND SIZE CONSISTENCY ANALYTICS
  // ============================================

  // Filter trades for Hand Size Consistency based on setup filter
  // Uses globallyFilteredTrades as base, then applies local Setup filter
  const tradesForHandSizeAnalysis = useMemo(() => {
    if (!handSizeSetupFilter) return globallyFilteredTrades;
    
    if (handSizeSetupFilter === '__no_setup__') {
      return globallyFilteredTrades.filter(t => !t.setup_id);
    }
    return globallyFilteredTrades.filter(t => t.setup_id === handSizeSetupFilter);
  }, [globallyFilteredTrades, handSizeSetupFilter]);

  // Get selected setup name for Hand Size Consistency display
  const handSizeSetupDisplayName = useMemo(() => {
    if (!handSizeSetupFilter) return null;
    if (handSizeSetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === handSizeSetupFilter);
    return setup?.name || null;
  }, [handSizeSetupFilter, setups]);

  // Helper: Calculate median of an array
  const calculateMedian = (arr) => {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 
      ? sorted[mid] 
      : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  // Helper: Get trade type based on timeframe
  const getTradeType = (timeframe) => {
    if (!timeframe) return 'day';
    const tf = timeframe.toUpperCase();
    const swingTimeframes = ['4H', '6H', '8H', '12H', '1D', 'D', '1W', 'W'];
    return swingTimeframes.some(swTf => tf.includes(swTf)) ? 'swing' : 'day';
  };

  // Hand Size Consistency Analytics Data
  const handSizeConsistencyData = useMemo(() => {
    // Filter to only closed trades with valid hand_size (the original planned risk)
    const eligibleTrades = tradesForHandSizeAnalysis.filter(t => 
      t.status === 'closed' && 
      t.hand_size !== null && 
      t.hand_size !== undefined &&
      t.hand_size > 0
    );

    const totalTrades = eligibleTrades.length;

    // Require minimum 5 trades for meaningful analysis
    if (totalTrades < 5) {
      return {
        insufficientData: true,
        totalTrades,
        minRequired: 5,
        trades: eligibleTrades
      };
    }

    // 1. Calculate BASELINE using MEDIAN (more robust than average)
    const handSizes = eligibleTrades.map(t => t.hand_size);
    const baselineHandSize = calculateMedian(handSizes);

    // 2. Calculate consistency band (±15%)
    const CONSISTENCY_THRESHOLD = 0.15;
    const lowerBand = baselineHandSize * (1 - CONSISTENCY_THRESHOLD);
    const upperBand = baselineHandSize * (1 + CONSISTENCY_THRESHOLD);

    // 3. Classify each trade
    const tradesWithClassification = eligibleTrades.map(t => {
      const handSize = t.hand_size;
      const deviation = handSize - baselineHandSize;
      const deviationPercent = (deviation / baselineHandSize) * 100;
      
      let classification;
      if (handSize < lowerBand) {
        classification = 'undersized';
      } else if (handSize > upperBand) {
        classification = 'oversized';
      } else {
        classification = 'consistent';
      }

      return {
        ...t,
        _hsClassification: classification,
        _hsDeviation: deviation,
        _hsDeviationPercent: deviationPercent,
        _tradeType: getTradeType(t.timeframe)
      };
    });

    // 4. Count classifications
    const consistentTrades = tradesWithClassification.filter(t => t._hsClassification === 'consistent');
    const oversizedTrades = tradesWithClassification.filter(t => t._hsClassification === 'oversized');
    const undersizedTrades = tradesWithClassification.filter(t => t._hsClassification === 'undersized');

    const consistencyRate = (consistentTrades.length / totalTrades) * 100;

    // 5. Calculate Material Sizing Alerts
    // Alert triggers when: outside band AND deviation > 1% of initial capital
    // Get initial capital from analytics if available, default to 10000
    const initialCapital = analytics?.initial_capital || 10000;
    const materialThreshold = initialCapital * 0.01; // 1% of initial balance

    const materialAlerts = tradesWithClassification.filter(t => 
      t._hsClassification !== 'consistent' && 
      Math.abs(t._hsDeviation) > materialThreshold
    );

    // 6. Calculate average deviations
    const avgOversizedDeviation = oversizedTrades.length > 0
      ? oversizedTrades.reduce((sum, t) => sum + t._hsDeviation, 0) / oversizedTrades.length
      : 0;
    const avgUndersizedDeviation = undersizedTrades.length > 0
      ? undersizedTrades.reduce((sum, t) => sum + Math.abs(t._hsDeviation), 0) / undersizedTrades.length
      : 0;

    // 7. Trade Type Segmentation (Day vs Swing)
    const dayTrades = tradesWithClassification.filter(t => t._tradeType === 'day');
    const swingTrades = tradesWithClassification.filter(t => t._tradeType === 'swing');

    const calculateTypeMetrics = (trades) => {
      if (trades.length < 3) {
        return {
          total: trades.length,
          insufficientData: true
        };
      }
      const consistent = trades.filter(t => t._hsClassification === 'consistent').length;
      const oversized = trades.filter(t => t._hsClassification === 'oversized').length;
      const undersized = trades.filter(t => t._hsClassification === 'undersized').length;
      const alerts = trades.filter(t => 
        t._hsClassification !== 'consistent' && 
        Math.abs(t._hsDeviation) > materialThreshold
      ).length;

      return {
        total: trades.length,
        consistentCount: consistent,
        consistencyRate: ((consistent / trades.length) * 100).toFixed(1),
        oversizedCount: oversized,
        undersizedCount: undersized,
        materialAlerts: alerts,
        insufficientData: false
      };
    };

    const dayTradeMetrics = calculateTypeMetrics(dayTrades);
    const swingTradeMetrics = calculateTypeMetrics(swingTrades);

    // 8. Generate Insights
    const generateInsight = () => {
      const isSetupFiltered = !!handSizeSetupFilter;
      const setupContext = isSetupFiltered ? ' for this setup' : '';
      
      // High consistency (>=80%)
      if (consistencyRate >= 80) {
        return {
          type: 'success',
          icon: 'check',
          text: `Your hand size is highly consistent${setupContext}. ${consistencyRate.toFixed(0)}% of trades are within your normal sizing range.`,
          color: '#00E676'
        };
      }

      // Good consistency (>=65%)
      if (consistencyRate >= 65) {
        if (oversizedTrades.length > undersizedTrades.length) {
          return {
            type: 'info',
            icon: 'warning',
            text: `Your sizing is reasonably disciplined${setupContext}, but you occasionally oversize. ${oversizedTrades.length} trades exceeded your normal risk.`,
            color: '#FFA726'
          };
        } else if (undersizedTrades.length > oversizedTrades.length) {
          return {
            type: 'info',
            icon: 'info',
            text: `Your sizing is reasonably disciplined${setupContext}, but you sometimes undersize. This may indicate hesitation or selective conviction.`,
            color: '#00B8D4'
          };
        }
        return {
          type: 'info',
          icon: 'info',
          text: `Your sizing discipline is decent${setupContext}. ${consistencyRate.toFixed(0)}% of trades match your baseline risk pattern.`,
          color: '#00B8D4'
        };
      }

      // Low consistency (<65%)
      if (materialAlerts.length > totalTrades * 0.2) {
        // Many material alerts
        const alertSkew = oversizedTrades.length > undersizedTrades.length ? 'oversizing' : 'undersizing';
        return {
          type: 'warning',
          icon: 'warning',
          text: `Your position sizing is inconsistent${setupContext}. ${materialAlerts.length} trades had material deviations, mostly from ${alertSkew}.`,
          color: '#FF3B30'
        };
      }

      // General low consistency
      return {
        type: 'warning',
        icon: 'warning',
        text: `Your hand size varies significantly${setupContext}. Consider tightening your position sizing rules to improve discipline.`,
        color: '#FFA726'
      };
    };

    // 9. Trade Type Comparison Insight
    const generateTradeTypeInsight = () => {
      if (dayTradeMetrics.insufficientData || swingTradeMetrics.insufficientData) {
        return null;
      }

      const dayRate = parseFloat(dayTradeMetrics.consistencyRate);
      const swingRate = parseFloat(swingTradeMetrics.consistencyRate);
      const difference = Math.abs(dayRate - swingRate);

      if (difference < 10) {
        return {
          type: 'neutral',
          text: 'Your sizing discipline is similar across Day and Swing trades.',
          color: '#9E9E9E'
        };
      }

      if (dayRate > swingRate) {
        return {
          type: 'info',
          text: `Day Trades show better hand size discipline (${dayRate.toFixed(0)}%) than Swing Trades (${swingRate.toFixed(0)}%).`,
          color: '#00B8D4'
        };
      } else {
        return {
          type: 'info',
          text: `Swing Trades show better hand size discipline (${swingRate.toFixed(0)}%) than Day Trades (${dayRate.toFixed(0)}%).`,
          color: '#00B8D4'
        };
      }
    };

    return {
      insufficientData: false,
      totalTrades,
      baselineHandSize: Math.round(baselineHandSize * 100) / 100,
      lowerBand: Math.round(lowerBand * 100) / 100,
      upperBand: Math.round(upperBand * 100) / 100,
      consistentCount: consistentTrades.length,
      oversizedCount: oversizedTrades.length,
      undersizedCount: undersizedTrades.length,
      consistencyRate: Math.round(consistencyRate * 10) / 10,
      materialAlertsCount: materialAlerts.length,
      materialThreshold: Math.round(materialThreshold * 100) / 100,
      initialCapital,
      avgOversizedDeviation: Math.round(avgOversizedDeviation * 100) / 100,
      avgUndersizedDeviation: Math.round(avgUndersizedDeviation * 100) / 100,
      dayTradeMetrics,
      swingTradeMetrics,
      insight: generateInsight(),
      tradeTypeInsight: generateTradeTypeInsight(),
      trades: tradesWithClassification,
      oversizedTrades,
      undersizedTrades,
      materialAlertTrades: materialAlerts
    };
  }, [tradesForHandSizeAnalysis, handSizeSetupFilter, analytics]);

  // ============================================
  // STOP ADJUSTMENTS ANALYTICS
  // ============================================

  // Filter trades for Stop Adjustments based on setup filter
  // Uses globallyFilteredTrades as base, then applies local Setup filter
  const tradesForStopAdjustments = useMemo(() => {
    if (!stopAdjustmentsSetupFilter) return globallyFilteredTrades;
    
    if (stopAdjustmentsSetupFilter === '__no_setup__') {
      return globallyFilteredTrades.filter(t => !t.setup_id);
    }
    return globallyFilteredTrades.filter(t => t.setup_id === stopAdjustmentsSetupFilter);
  }, [globallyFilteredTrades, stopAdjustmentsSetupFilter]);

  // Get selected setup name for Stop Adjustments display
  const stopAdjustmentsSetupDisplayName = useMemo(() => {
    if (!stopAdjustmentsSetupFilter) return null;
    if (stopAdjustmentsSetupFilter === '__no_setup__') return 'No setup';
    const setup = setups.find(s => s.id === stopAdjustmentsSetupFilter);
    return setup?.name || null;
  }, [stopAdjustmentsSetupFilter, setups]);

  // Stop Adjustments Analytics Data
  const stopAdjustmentsData = useMemo(() => {
    // Filter to closed trades only
    const closedTrades = tradesForStopAdjustments.filter(t => t.status === 'closed');
    const totalClosed = closedTrades.length;

    if (totalClosed === 0) {
      return {
        noData: true,
        totalClosed: 0,
        adjustedTrades: [],
        adjustedCount: 0
      };
    }

    // Determine adjusted trades: stop_adjusted = true OR stop_adjustment_history has entries
    const adjustedTrades = closedTrades.filter(t => 
      t.stop_adjusted === true || 
      (t.stop_adjustment_history && t.stop_adjustment_history.length > 0)
    );

    const adjustedCount = adjustedTrades.length;
    const adjustedPercent = totalClosed > 0 ? ((adjustedCount / totalClosed) * 100).toFixed(1) : 0;

    // Calculate avg adjustments per adjusted trade (if history available)
    let totalAdjustments = 0;
    let tradesWithHistory = 0;
    adjustedTrades.forEach(t => {
      if (t.stop_adjustment_history && t.stop_adjustment_history.length > 0) {
        totalAdjustments += t.stop_adjustment_history.length;
        tradesWithHistory++;
      }
    });
    const avgAdjustmentsPerTrade = tradesWithHistory > 0 
      ? (totalAdjustments / tradesWithHistory).toFixed(1) 
      : null;

    // Helper: Get trade type
    const getTradeType = (timeframe) => {
      if (!timeframe) return 'day';
      const tf = timeframe.toUpperCase();
      const swingTimeframes = ['4H', '6H', '8H', '12H', '1D', 'D', '1W', 'W'];
      return swingTimeframes.some(swTf => tf.includes(swTf)) ? 'swing' : 'day';
    };

    // Trade type segmentation
    const dayTradesClosed = closedTrades.filter(t => getTradeType(t.timeframe) === 'day');
    const swingTradesClosed = closedTrades.filter(t => getTradeType(t.timeframe) === 'swing');
    
    const dayTradesAdjusted = adjustedTrades.filter(t => getTradeType(t.timeframe) === 'day');
    const swingTradesAdjusted = adjustedTrades.filter(t => getTradeType(t.timeframe) === 'swing');

    const dayMetrics = {
      total: dayTradesClosed.length,
      adjusted: dayTradesAdjusted.length,
      percent: dayTradesClosed.length > 0 ? ((dayTradesAdjusted.length / dayTradesClosed.length) * 100).toFixed(1) : 0
    };

    const swingMetrics = {
      total: swingTradesClosed.length,
      adjusted: swingTradesAdjusted.length,
      percent: swingTradesClosed.length > 0 ? ((swingTradesAdjusted.length / swingTradesClosed.length) * 100).toFixed(1) : 0
    };

    // ============================================
    // ADJUSTMENT TYPE CLASSIFICATION (V2)
    // ============================================
    // Classify each adjusted trade's final stop position:
    // - Risk Reduction: stop improved but still on loss side
    // - Break-even: stop equals entry
    // - Profit Lock: stop crossed into profit territory

    const classifyAdjustmentType = (trade) => {
      const entry = trade.entry;
      const finalStop = trade.stop_loss;  // Current/final stop
      const direction = trade.direction;
      
      // Handle edge case: entry == stop (treat as break-even due to rounding)
      if (Math.abs(finalStop - entry) < 0.0001) {
        return 'breakeven';
      }
      
      if (direction === 'long') {
        // LONG: stop below entry = loss side, stop above entry = profit side
        if (finalStop < entry) return 'risk_reduction';
        if (finalStop > entry) return 'profit_lock';
        return 'breakeven';
      } else {
        // SHORT: stop above entry = loss side, stop below entry = profit side
        if (finalStop > entry) return 'risk_reduction';
        if (finalStop < entry) return 'profit_lock';
        return 'breakeven';
      }
    };

    // Classify all adjusted trades
    const classifiedTrades = adjustedTrades.map(t => ({
      ...t,
      _tradeType: getTradeType(t.timeframe),
      _adjustmentsCount: t.stop_adjustment_history?.length || 1,
      _adjustmentType: classifyAdjustmentType(t)
    }));

    // Count by adjustment type
    const riskReductionTrades = classifiedTrades.filter(t => t._adjustmentType === 'risk_reduction');
    const breakevenTrades = classifiedTrades.filter(t => t._adjustmentType === 'breakeven');
    const profitLockTrades = classifiedTrades.filter(t => t._adjustmentType === 'profit_lock');

    const typeDistribution = {
      riskReduction: {
        count: riskReductionTrades.length,
        percent: adjustedCount > 0 ? ((riskReductionTrades.length / adjustedCount) * 100).toFixed(1) : 0
      },
      breakeven: {
        count: breakevenTrades.length,
        percent: adjustedCount > 0 ? ((breakevenTrades.length / adjustedCount) * 100).toFixed(1) : 0
      },
      profitLock: {
        count: profitLockTrades.length,
        percent: adjustedCount > 0 ? ((profitLockTrades.length / adjustedCount) * 100).toFixed(1) : 0
      }
    };

    // Enhanced Day/Swing metrics with type breakdown
    const getTypeBreakdown = (trades) => {
      const rr = trades.filter(t => t._adjustmentType === 'risk_reduction').length;
      const be = trades.filter(t => t._adjustmentType === 'breakeven').length;
      const pl = trades.filter(t => t._adjustmentType === 'profit_lock').length;
      return { riskReduction: rr, breakeven: be, profitLock: pl };
    };

    const dayClassified = classifiedTrades.filter(t => t._tradeType === 'day');
    const swingClassified = classifiedTrades.filter(t => t._tradeType === 'swing');

    const enhancedDayMetrics = {
      ...dayMetrics,
      typeBreakdown: getTypeBreakdown(dayClassified)
    };

    const enhancedSwingMetrics = {
      ...swingMetrics,
      typeBreakdown: getTypeBreakdown(swingClassified)
    };

    // Generate insight (enhanced with type classification)
    const generateInsight = () => {
      const isSetupFiltered = !!stopAdjustmentsSetupFilter;
      const setupContext = isSetupFiltered ? ' for this setup' : '';
      const rate = parseFloat(adjustedPercent);

      if (adjustedCount === 0) {
        return {
          type: 'info',
          icon: 'info',
          text: `You haven't adjusted stops on any closed trades${setupContext} yet. Use the Adjust Stop feature on open positions to actively manage risk.`,
          color: '#9E9E9E'
        };
      }

      // Check dominant adjustment type
      const rrPct = parseFloat(typeDistribution.riskReduction.percent);
      const bePct = parseFloat(typeDistribution.breakeven.percent);
      const plPct = parseFloat(typeDistribution.profitLock.percent);

      // Profit Lock dominant (>40%)
      if (plPct >= 40) {
        return {
          type: 'success',
          icon: 'lock',
          text: `You actively lock profits by moving stops beyond entry${setupContext}. ${plPct}% of your adjustments resulted in profit protection.`,
          color: '#00E676'
        };
      }

      // Break-even dominant (>40%)
      if (bePct >= 40) {
        return {
          type: 'info',
          icon: 'breakeven',
          text: `You frequently move stops to break-even${setupContext}. ${bePct}% of your adjustments aimed to eliminate risk.`,
          color: '#00B8D4'
        };
      }

      // Risk Reduction dominant (>50%)
      if (rrPct >= 50) {
        return {
          type: 'info',
          icon: 'shield',
          text: `Most of your adjustments are risk reduction, not profit locking${setupContext}. ${rrPct}% reduced risk while staying on the loss side.`,
          color: '#FFA726'
        };
      }

      // Check Day vs Swing differences
      const dayRate = parseFloat(enhancedDayMetrics.percent);
      const swingRate = parseFloat(enhancedSwingMetrics.percent);
      if (Math.abs(dayRate - swingRate) > 15 && dayRate > 0 && swingRate > 0) {
        if (dayRate > swingRate) {
          return {
            type: 'info',
            icon: 'info',
            text: `Day trades show more aggressive stop management (${dayRate}%) than swing trades (${swingRate}%)${setupContext}.`,
            color: '#00B8D4'
          };
        } else {
          return {
            type: 'info',
            icon: 'info',
            text: `Swing trades show more active stop management (${swingRate}%) than day trades (${dayRate}%)${setupContext}.`,
            color: '#00B8D4'
          };
        }
      }

      // High adjustment rate (>=40%)
      if (rate >= 40) {
        return {
          type: 'info',
          icon: 'gear',
          text: `You frequently adjust stops after entry${setupContext}. ${rate}% of your trades involved active stop management.`,
          color: '#00B8D4'
        };
      }

      // Default moderate/low
      return {
        type: 'neutral',
        icon: 'info',
        text: `You ${rate >= 20 ? 'moderately' : 'rarely'} adjust stops${setupContext}. ${rate}% of closed trades had stop management after entry.`,
        color: '#9E9E9E'
      };
    };

    return {
      noData: false,
      totalClosed,
      adjustedTrades: classifiedTrades,
      adjustedCount,
      adjustedPercent: parseFloat(adjustedPercent),
      avgAdjustmentsPerTrade,
      dayMetrics: enhancedDayMetrics,
      swingMetrics: enhancedSwingMetrics,
      typeDistribution,
      riskReductionCount: riskReductionTrades.length,
      breakevenCount: breakevenTrades.length,
      profitLockCount: profitLockTrades.length,
      insight: generateInsight()
    };
  }, [tradesForStopAdjustments, stopAdjustmentsSetupFilter]);

  // Format hold time for display
  const formatHoldTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
    return `${Math.round(minutes / 1440)}d`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (setups.length > 0 || assetGroups.length > 0) {
      fetchAnalytics();
    }
  }, [selectedAssetGroup, selectedTimeframe, selectedTradeType]);

  const fetchData = async () => {
    try {
      const [setupsRes, groupsRes, breakdownRes, tradesRes] = await Promise.all([
        api.get('/setups'),
        api.get('/asset-groups'),
        api.get('/analytics/breakdown'),
        api.get('/trades')
      ]);

      setSetups(setupsRes.data);
      setAssetGroups(groupsRes.data);
      setBreakdown(breakdownRes.data);
      
      // Process trades data for behavior sections
      const trades = tradesRes.data;
      setAllTrades(trades);
      setTotalTrades(trades.length);
      setCanceledTrades(trades.filter(t => t.status === 'canceled'));
      
      await fetchAnalytics();
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      // Global filters only: Asset Group, Timeframe, Trade Type
      // Setup is now a local/section-level filter only
      if (selectedAssetGroup) params.append("asset_group_id", selectedAssetGroup);
      if (selectedTimeframe) params.append("timeframe", selectedTimeframe);
      if (selectedTradeType) params.append("trade_type", selectedTradeType);

      const response = await api.get(`/analytics/advanced?${params}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  const clearFilters = () => {
    setSelectedAssetGroup("");
    setSelectedTimeframe("");
    setSelectedTradeType("");
  };

  const hasActiveFilters = selectedAssetGroup || selectedTimeframe || selectedTradeType;

  // ============================================
  // SMART SECTION ORDERING
  // Sections with data appear first, sections without data appear last
  // ============================================
  const sectionOrder = useMemo(() => {
    // Define all sections with their default order and data availability check
    const sections = [
      {
        id: 'summary',
        defaultOrder: 1,
        // Summary always has data if there are any closed trades
        hasMeaningfulData: !computedSummaryMetrics.noData
      },
      {
        id: 'timeframe',
        defaultOrder: 2,
        // Timeframe has data if there are timeframe entries
        hasMeaningfulData: sortedTimeframes.length > 0
      },
      {
        id: 'assetGroup',
        defaultOrder: 3,
        // Asset group has data if breakdown has asset groups with trades
        hasMeaningfulData: (() => {
          const data = assetGroupSetupFilter ? computedAssetGroupData : breakdown?.asset_groups;
          return data && data.length > 0 && data.some(g => g.total_trades > 0);
        })()
      },
      {
        id: 'tradeType',
        defaultOrder: 4,
        // Trade type has data if any of the 3 types have trades
        hasMeaningfulData: (() => {
          const data = tradeTypeSetupFilter ? computedTradeTypeData : breakdown?.trade_type;
          return data && (
            (data.scalp?.total_trades > 0) ||
            (data.day_trade?.total_trades > 0) ||
            (data.swing_trade?.total_trades > 0)
          );
        })()
      },
      {
        id: 'partial',
        defaultOrder: 5,
        // Partial has data if there are partial execution trades
        hasMeaningfulData: partialTradesData.total > 0
      },
      {
        id: 'executionQuality',
        defaultOrder: 6,
        // Execution quality has data if there are closed trades to analyze
        hasMeaningfulData: executionQualityData.total > 0
      },
      {
        id: 'hedge',
        defaultOrder: 7,
        // Hedge has data if there are hedged trades
        hasMeaningfulData: (hedgeAnalyticsData?.totalHedgedTrades || 0) > 0
      },
      {
        id: 'handSize',
        defaultOrder: 8,
        // Hand size has data if there are enough trades for analysis
        hasMeaningfulData: (handSizeConsistencyData?.totalTrades || 0) > 0
      },
      {
        id: 'stopAdjustments',
        defaultOrder: 9,
        // Stop adjustments has data if there are adjusted trades
        hasMeaningfulData: (stopAdjustmentsData?.totalAdjustedTrades || 0) > 0
      },
      {
        id: 'planned',
        defaultOrder: 10,
        // Planned trades has data if there are canceled/not-triggered trades
        hasMeaningfulData: filteredCanceledTrades.length > 0
      }
    ];

    // Sort by user's custom order first
    const sortByUserOrder = (a, b) => {
      const indexA = userSectionOrder.indexOf(a.id);
      const indexB = userSectionOrder.indexOf(b.id);
      // If not found in user order, fall back to default order
      const orderA = indexA >= 0 ? indexA : a.defaultOrder;
      const orderB = indexB >= 0 ? indexB : b.defaultOrder;
      return orderA - orderB;
    };

    // Split into sections with data and sections without
    const withData = sections.filter(s => s.hasMeaningfulData).sort(sortByUserOrder);
    const withoutData = sections.filter(s => !s.hasMeaningfulData).sort(sortByUserOrder);

    // Combine: data sections first, then empty sections (smart ordering)
    return [...withData, ...withoutData];
  }, [
    userSectionOrder,
    computedSummaryMetrics,
    sortedTimeframes,
    assetGroupSetupFilter,
    computedAssetGroupData,
    breakdown?.asset_groups,
    tradeTypeSetupFilter,
    computedTradeTypeData,
    breakdown?.trade_type,
    partialTradesData,
    executionQualityData,
    hedgeAnalyticsData,
    handSizeConsistencyData,
    stopAdjustmentsData,
    filteredCanceledTrades
  ]);

  // Helper to get section position for rendering
  const getSectionPosition = (sectionId) => {
    return sectionOrder.findIndex(s => s.id === sectionId);
  };

  // Check if section has meaningful data
  const sectionHasData = (sectionId) => {
    const section = sectionOrder.find(s => s.id === sectionId);
    return section?.hasMeaningfulData || false;
  };

  // Get current position of section in user order (for Move Up/Down logic)
  const getUserOrderPosition = (sectionId) => {
    return userSectionOrder.indexOf(sectionId);
  };

  // Section Edit Controls Component
  const SectionEditControls = ({ sectionId }) => {
    if (!isEditingLayout) return null;
    
    const position = getUserOrderPosition(sectionId);
    const isFirst = position === 0;
    const isLast = position === userSectionOrder.length - 1;
    
    return (
      <div className="flex items-center gap-1 ml-3" data-testid={`edit-controls-${sectionId}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            moveSectionUp(sectionId);
          }}
          disabled={isFirst}
          className={`p-1.5 rounded transition-all ${
            isFirst 
              ? 'text-white/20 cursor-not-allowed' 
              : 'text-white/60 hover:text-[#00E676] hover:bg-[#00E676]/10'
          }`}
          title="Move Up"
          data-testid={`move-up-${sectionId}`}
        >
          <ArrowUp size={16} weight="bold" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            moveSectionDown(sectionId);
          }}
          disabled={isLast}
          className={`p-1.5 rounded transition-all ${
            isLast 
              ? 'text-white/20 cursor-not-allowed' 
              : 'text-white/60 hover:text-[#00E676] hover:bg-[#00E676]/10'
          }`}
          title="Move Down"
          data-testid={`move-down-${sectionId}`}
        >
          <ArrowDown size={16} weight="bold" />
        </button>
      </div>
    );
  };

  // Section labels for display
  const SECTION_LABELS = {
    summary: 'Filtered Trade Performance',
    timeframe: 'Performance by Timeframe',
    assetGroup: 'Performance by Asset Group',
    tradeType: 'Trade Type Comparison',
    partial: 'Partial Execution',
    executionQuality: 'Execution Quality',
    hedge: 'Hedge Analytics',
    handSize: 'Hand Size Consistency',
    stopAdjustments: 'Stop Adjustments',
    planned: 'Planned Trades'
  };

  // Get the CSS order value for a section based on sectionOrder
  const getSectionCssOrder = (sectionId) => {
    const index = sectionOrder.findIndex(s => s.id === sectionId);
    return index >= 0 ? index : 999;
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8" data-testid="advanced-analytics-page">
      <div className="w-full max-w-[1600px] mx-auto">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-2">
              {t('analytics.advancedTitle')}
            </h2>
            <p className="text-white/60 text-base font-sans">
              {t('analytics.advancedSubtitle')}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Edit Layout Button - Hidden in shared profile mode */}
            {!isSharedProfileView && (
              <Button
                onClick={toggleEditLayoutMode}
                variant="outline"
                data-testid="edit-layout-button"
                className={`flex items-center gap-2 transition-all ${
                  isEditingLayout 
                    ? 'border-[#00E676] bg-[#00E676]/10 text-[#00E676]' 
                    : 'border-white/20 text-white/60 hover:border-white/40 hover:text-white'
                }`}
              >
                <ListBullets size={18} weight={isEditingLayout ? "bold" : "regular"} />
                <span className="hidden sm:inline">
                  {isEditingLayout ? 'Done' : 'Edit Layout'}
                </span>
              </Button>
            )}

            {/* Reset to Default - Only visible in edit mode */}
            {isEditingLayout && !isSharedProfileView && (
              <Button
                onClick={resetSectionOrder}
                variant="ghost"
                data-testid="reset-layout-button"
                className="text-white/40 hover:text-white text-xs"
              >
                Reset to Default
              </Button>
            )}

            {/* Share Button */}
            <Button
              onClick={() => setShowShareModal(true)}
              variant="outline"
              data-testid="share-advanced-button"
              className="border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/10 flex items-center gap-2"
            >
              <ShareNetwork size={20} weight="duotone" />
              <span className="hidden sm:inline">{language === 'ptBR' ? 'Compartilhar' : 'Share'}</span>
            </Button>
          </div>
        </div>

        {/* Global Filters - Applied to entire Advanced page */}
        <div className="mb-8 bg-[#121212] border border-white/10 rounded-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Funnel size={20} weight="duotone" className="text-white" />
            <h3 className="text-lg font-bold font-heading">Global Filters</h3>
            <span className="text-white/40 text-xs ml-2">(applies to entire page)</span>
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                data-testid="clear-filters"
                variant="ghost"
                size="sm"
                className="ml-auto text-white/60 hover:text-white text-xs"
              >
                {t('common.clearAll')}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-white/60 mb-2 block font-mono">{language === 'ptBR' ? 'GRUPO DE ATIVOS' : 'ASSET GROUP'}</label>
              <select
                value={selectedAssetGroup}
                onChange={(e) => setSelectedAssetGroup(e.target.value)}
                data-testid="filter-asset-group"
                className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none"
              >
                <option value="">{t('analytics.allGroups')}</option>
                {assetGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs text-white/60 mb-2 block font-mono">TIMEFRAME</label>
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                data-testid="filter-timeframe"
                className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none"
              >
                <option value="">All Timeframes</option>
                {timeframes.map((tf) => (
                  <option key={tf} value={tf}>
                    {tf}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs text-white/60 mb-2 block font-mono">TRADE TYPE</label>
              <select
                value={selectedTradeType}
                onChange={(e) => setSelectedTradeType(e.target.value)}
                data-testid="filter-trade-type"
                className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none"
              >
                <option value="">All Types</option>
                <option value="scalp">Scalp</option>
                <option value="day_trade">Day Trade</option>
                <option value="swing_trade">Swing Trade</option>
              </select>
              <p className="text-white/30 text-[10px] mt-1">
                <Link to="/settings" className="text-[#00E676]/70 hover:text-[#00E676]">Edit thresholds</Link>
              </p>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 text-sm text-white/60">
              <span className="font-mono">Active: </span>
              {selectedAssetGroup && <span className="text-[#00E676]">Asset Group </span>}
              {selectedTimeframe && <span className="text-[#00E676]">Timeframe </span>}
              {selectedTradeType && <span className="text-[#00E676]">Trade Type</span>}
            </div>
          )}
        </div>

        {/* === ORDERABLE SECTIONS CONTAINER === */}
        {/* Uses CSS flexbox with order property for dynamic reordering */}
        <div className="flex flex-col" data-testid="sections-container">

        {/* Filtered Trade Performance - Summary section with local Setup filter */}
        <div className={`mb-8 ${isEditingLayout ? 'ring-1 ring-[#00E676]/20 rounded-lg' : ''}`} data-section-id="summary" style={{ order: getSectionCssOrder('summary') }}>
          {/* Collapsible Header */}
          <button
            onClick={() => toggleSection('summary')}
            className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors mb-3"
            data-testid="section-toggle-summary"
          >
            <div className="flex items-center gap-3">
              {expandedSections.summary ? (
                <CaretDown size={20} weight="bold" className="text-white/60" />
              ) : (
                <CaretRight size={20} weight="bold" className="text-white/60" />
              )}
              <div className="text-left">
                <h3 className="text-lg font-semibold text-white">Filtered Trade Performance</h3>
                <p className="text-white/40 text-sm">Overall performance metrics for the current filter context</p>
              </div>
              <SectionEditControls sectionId="summary" />
            </div>
            <span className="text-white/30 text-sm font-mono">
              {computedSummaryMetrics.noData ? '0' : computedSummaryMetrics.total_trades} trades
            </span>
          </button>

          {/* Collapsible Content */}
          <div className={`overflow-hidden transition-all duration-300 ${
            expandedSections.summary ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}>
            {/* Setup Filter Row */}
            <div className="flex items-center justify-between mb-4">
              <div /> {/* Spacer */}
              <div className="flex items-center gap-2">
                <span className="text-white/40 text-xs font-mono">SETUP:</span>
                <select
                  value={summarySetupFilter}
                  onChange={(e) => setSummarySetupFilter(e.target.value)}
                  className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs font-mono focus:border-[#00E676] focus:outline-none min-w-[140px]"
                  data-testid="summary-setup-filter"
                >
                  <option value="">All Setups</option>
                  {setups.map((setup) => (
                    <option key={setup.id} value={setup.id}>{setup.name}</option>
                  ))}
                  <option value="__no_setup__">No setup</option>
                </select>
              </div>
            </div>

            {/* Filter indicator when setup is selected */}
            {summarySetupFilter && summarySetupDisplayName && (
              <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
                <span>Showing performance for:</span>
                <span className="font-bold text-[#00E676]">{summarySetupDisplayName}</span>
                <button
                  onClick={() => setSummarySetupFilter("")}
                  className="text-white/40 hover:text-white/60 ml-1"
                  data-testid="clear-summary-filter"
                >
                  (clear)
                </button>
              </div>
            )}

            {/* Content: Metric Cards or Empty State */}
            {computedSummaryMetrics.noData ? (
              <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
                <ChartBar size={48} weight="duotone" className="text-white/40 mx-auto mb-4" />
                {summarySetupFilter ? (
                  <>
                    <h4 className="text-lg font-semibold text-white mb-2">No performance data for this setup</h4>
                    <p className="text-white/50 text-sm max-w-md mx-auto">
                      This setup has no closed trades inside the current global filter context.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-white/60 mb-4">No trades match the selected global filters</p>
                    {hasActiveFilters && (
                      <Button
                        onClick={clearFilters}
                        className="bg-white text-black font-bold hover:bg-gray-200"
                      >
                        Clear Global Filters
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  title="Total Trades"
                  value={computedSummaryMetrics.total_trades}
                  subtitle={`${computedSummaryMetrics.win_count}W / ${computedSummaryMetrics.loss_count}L`}
                  icon={ChartBar}
                  trend={tradesTrend}
                  testId="advanced-analytics-total-trades-card"
                />
                <MetricCard
                  title="Win Rate"
                  value={`${computedSummaryMetrics.win_rate.toFixed(1)}%`}
                  subtitle={computedSummaryMetrics.win_rate >= 50 ? "Above average" : "Below average"}
                  icon={Target}
                  highlight={computedSummaryMetrics.win_rate >= 50 ? "text-[#00E676]" : "text-[#FFC107]"}
                />
                <MetricCard
                  title="TOTAL R"
                  value={`${(computedSummaryMetrics.total_r || 0) >= 0 ? '+' : ''}${(computedSummaryMetrics.total_r || 0).toFixed(2)}R`}
                  subtitle="Sum of realized R"
                  icon={TrendUp}
                  highlight={(computedSummaryMetrics.total_r || 0) >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}
                  trend={totalRTrend}
                  testId="advanced-analytics-total-r-card"
                />
                <MetricCard
                  title="Profit Factor"
                  value={computedSummaryMetrics.profit_factor === Infinity ? '∞' : computedSummaryMetrics.profit_factor.toFixed(2)}
                  subtitle={getProfitFactorLabel(computedSummaryMetrics.profit_factor)}
                  icon={Trophy}
                  highlight={getProfitFactorColor(computedSummaryMetrics.profit_factor)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Breakdowns */}
        {/* Timeframe Performance - Collapsible Section */}
        {breakdown && (
          <div className={`${isEditingLayout ? 'ring-1 ring-[#00E676]/20 rounded-lg' : ''}`} data-section-id="timeframe" style={{ order: getSectionCssOrder('timeframe') }}>
              {/* Collapsible Header */}
              <button
                onClick={() => toggleSection('timeframe')}
                className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors mb-3"
                data-testid="section-toggle-timeframe"
              >
                <div className="flex items-center gap-3">
                  {expandedSections.timeframe ? (
                    <CaretDown size={20} weight="bold" className="text-white/60" />
                  ) : (
                    <CaretRight size={20} weight="bold" className="text-white/60" />
                  )}
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-white">Performance by Timeframe</h3>
                    <p className="text-white/40 text-sm">Analyze which timeframes yield the best results</p>
                  </div>
                  <SectionEditControls sectionId="timeframe" />
                </div>
                <span className="text-white/30 text-sm font-mono">
                  {sortedTimeframes.length} timeframes
                </span>
              </button>

              {/* Collapsible Content */}
              <div className={`overflow-hidden transition-all duration-300 ${
                expandedSections.timeframe ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                {/* Setup Filter Row */}
                <div className="flex items-center justify-end mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-white/40 text-xs font-mono">SETUP:</span>
                    <select
                      value={timeframeSetupFilter}
                      onChange={(e) => setTimeframeSetupFilter(e.target.value)}
                      className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs font-mono focus:border-[#00E676] focus:outline-none min-w-[140px]"
                      data-testid="timeframe-setup-filter"
                    >
                      <option value="">All Setups</option>
                      {setups.map((setup) => (
                        <option key={setup.id} value={setup.id}>{setup.name}</option>
                      ))}
                      <option value="__no_setup__">No setup</option>
                    </select>
                  </div>
                </div>

              {/* Filter indicator when setup is selected */}
              {timeframeSetupFilter && timeframeSetupDisplayName && (
                <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
                  <span>Showing timeframe performance for:</span>
                  <span className="font-bold text-[#00E676]">{timeframeSetupDisplayName}</span>
                  <button
                    onClick={() => setTimeframeSetupFilter("")}
                    className="text-white/40 hover:text-white/60 ml-1"
                    data-testid="clear-timeframe-filter"
                  >
                    (clear)
                  </button>
                </div>
              )}

              {/* Content: Table or Empty State */}
              {sortedTimeframes.length === 0 ? (
                <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                    <Clock size={32} className="text-white/30" />
                  </div>
                  {timeframeSetupFilter ? (
                    <>
                      <h4 className="text-lg font-semibold text-white mb-2">No timeframe data for this setup</h4>
                      <p className="text-white/50 text-sm max-w-md mx-auto">
                        This setup does not have enough closed trades with timeframes to analyze performance yet.
                      </p>
                    </>
                  ) : (
                    <>
                      <h4 className="text-lg font-semibold text-white mb-2">No timeframe data available</h4>
                      <p className="text-white/50 text-sm max-w-md mx-auto">
                        Add closed trades with timeframes to see performance breakdown.
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#0A0A0A]">
                      <tr className="text-left text-xs text-white/40 font-mono">
                        <th 
                          className={`p-3 cursor-pointer hover:text-white/60 transition-colors select-none ${tfSortColumn === 'timeframe' ? 'text-white/80' : ''}`}
                          onClick={() => handleTfSort('timeframe')}
                        >
                          TIMEFRAME {renderSortIndicator('timeframe')}
                        </th>
                        <th 
                          className={`p-3 cursor-pointer hover:text-white/60 transition-colors select-none ${tfSortColumn === 'trades' ? 'text-white/80' : ''}`}
                          onClick={() => handleTfSort('trades')}
                        >
                          TRADES {renderSortIndicator('trades')}
                        </th>
                        <th 
                          className={`p-3 cursor-pointer hover:text-white/60 transition-colors select-none ${tfSortColumn === 'win_rate' ? 'text-white/80' : ''}`}
                          onClick={() => handleTfSort('win_rate')}
                        >
                          WIN RATE {renderSortIndicator('win_rate')}
                        </th>
                        <th 
                          className={`p-3 cursor-pointer hover:text-white/60 transition-colors select-none ${tfSortColumn === 'avg_rr' ? 'text-white/80' : ''}`}
                          onClick={() => handleTfSort('avg_rr')}
                        >
                          TOTAL R {renderSortIndicator('avg_rr')}
                        </th>
                        <th 
                          className={`p-3 cursor-pointer hover:text-white/60 transition-colors select-none ${tfSortColumn === 'profit_factor' ? 'text-white/80' : ''}`}
                          onClick={() => handleTfSort('profit_factor')}
                        >
                          PROFIT FACTOR {renderSortIndicator('profit_factor')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                        {sortedTimeframes.map((tf) => {
                          const isBestTimeframe = tf.timeframe === bestTimeframe;
                          const hasTradesForDrilldown = tf.total_trades > 0;
                          
                          // In public mode, navigate to history filtered by timeframe
                          const handleTimeframeClick = () => {
                            if (!hasTradesForDrilldown) return;
                            if (isSharedProfileView) {
                              navigate(getRoute('/history'), { state: { filter: { timeframe: tf.timeframe } } });
                            } else {
                              navigate(`/analytics/timeframe/${tf.timeframe}`);
                            }
                          };
                          
                          return (
                            <tr 
                              key={tf.timeframe} 
                              onClick={handleTimeframeClick}
                              className={`border-t transition-colors ${
                                hasTradesForDrilldown ? 'cursor-pointer hover:bg-white/5' : ''
                              } ${
                                isBestTimeframe 
                                  ? 'border-[#00E676]/30 bg-[#00E676]/5' 
                                  : 'border-white/10'
                              }`}
                              data-testid={`timeframe-row-${tf.timeframe}`}
                            >
                              <td className="p-3 font-mono text-white font-bold">
                                <span className="flex items-center gap-2">
                                  {tf.timeframe}
                                  {isBestTimeframe && (
                                    <span className="text-[10px] px-2 py-0.5 bg-[#00E676]/10 border border-[#00E676]/20 rounded text-[#00E676]/80 font-mono uppercase tracking-wider">
                                      Top
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="p-3 font-mono text-white">{tf.total_trades}</td>
                              <td className="p-3 font-mono text-white">{tf.win_rate_excluding_even ?? tf.win_rate}%</td>
                              <td className={`p-3 font-mono ${(tf.total_r || 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                                {(tf.total_r || 0) >= 0 ? '+' : ''}{tf.total_r || 0}R
                              </td>
                              <td className="p-3">
                                <span className={`font-mono ${getProfitFactorColor(tf.profit_factor)}`}>
                                  {tf.profit_factor}
                                </span>
                                <span className="text-xs text-white/40 ml-2">{getProfitFactorLabel(tf.profit_factor)}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
        )}

        {/* Asset Group Performance - Collapsible Section */}
        {breakdown && (
          <div className={`mt-6 ${isEditingLayout ? 'ring-1 ring-[#00E676]/20 rounded-lg' : ''}`} data-section-id="assetGroup" style={{ order: getSectionCssOrder('assetGroup') }}>
              {/* Collapsible Header */}
              <button
                onClick={() => toggleSection('assetGroup')}
                className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors mb-3"
                data-testid="section-toggle-asset-group"
              >
                <div className="flex items-center gap-3">
                  {expandedSections.assetGroup ? (
                    <CaretDown size={20} weight="bold" className="text-white/60" />
                  ) : (
                    <CaretRight size={20} weight="bold" className="text-white/60" />
                  )}
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-white">Performance by Asset Group</h3>
                    <p className="text-white/40 text-sm">Compare performance across different market categories</p>
                  </div>
                  <SectionEditControls sectionId="assetGroup" />
                </div>
                <span className="text-white/30 text-sm font-mono">
                  {(() => {
                    const data = assetGroupSetupFilter ? computedAssetGroupData : breakdown?.asset_groups;
                    return data?.length || 0;
                  })()} groups
                </span>
              </button>

              {/* Collapsible Content */}
              <div className={`overflow-hidden transition-all duration-300 ${
                expandedSections.assetGroup ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                {/* Setup Filter Row */}
                <div className="flex items-center justify-end mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-white/40 text-xs font-mono">SETUP:</span>
                    <select
                      value={assetGroupSetupFilter}
                      onChange={(e) => setAssetGroupSetupFilter(e.target.value)}
                      className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs font-mono focus:border-[#00E676] focus:outline-none min-w-[140px]"
                      data-testid="asset-group-setup-filter"
                    >
                      <option value="">All Setups</option>
                      {setups.map((setup) => (
                        <option key={setup.id} value={setup.id}>{setup.name}</option>
                      ))}
                      <option value="__no_setup__">No setup</option>
                    </select>
                  </div>
                </div>

              {/* Filter indicator when setup is selected */}
              {assetGroupSetupFilter && assetGroupSetupDisplayName && (
                <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
                  <span>Showing asset group performance for:</span>
                  <span className="font-bold text-[#00E676]">{assetGroupSetupDisplayName}</span>
                  <button
                    onClick={() => setAssetGroupSetupFilter("")}
                    className="text-white/40 hover:text-white/60 ml-1"
                    data-testid="clear-asset-group-filter"
                  >
                    (clear)
                  </button>
                </div>
              )}

              {/* Content: Cards or Empty State */}
              {(() => {
                // Use computed data when filter is active, otherwise use breakdown data
                const assetGroupData = assetGroupSetupFilter 
                  ? computedAssetGroupData 
                  : breakdown?.asset_groups;
                
                if (!assetGroupData || assetGroupData.length === 0) {
                  return (
                    <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                        <ChartBar size={32} className="text-white/30" />
                      </div>
                      {assetGroupSetupFilter ? (
                        <>
                          <h4 className="text-lg font-semibold text-white mb-2">No asset group data for this setup</h4>
                          <p className="text-white/50 text-sm max-w-md mx-auto">
                            This setup does not have enough closed trades to analyze asset group performance yet.
                          </p>
                        </>
                      ) : (
                        <>
                          <h4 className="text-lg font-semibold text-white mb-2">No asset groups configured</h4>
                          <p className="text-white/50 text-sm max-w-md mx-auto">
                            Create asset groups to see performance breakdown by market category.
                          </p>
                        </>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {assetGroupData.map((group) => (
                      <div 
                        key={group.asset_group_id} 
                        className={`bg-[#121212] border border-white/10 rounded-sm p-6 transition-all ${
                          group.total_trades > 0 
                            ? 'cursor-pointer hover:border-[#00E676]/30 hover:bg-[#121212]/80' 
                            : ''
                        }`}
                        onClick={() => {
                          if (group.total_trades > 0) {
                            navigateToFilteredView('history', {
                              assetGroupId: group.asset_group_id,
                              assetGroupName: group.asset_group_name,
                              setupFilter: advancedSetupFilter, // Preserve setup filter if applied
                            });
                          }
                        }}
                        data-testid={`asset-group-card-${group.asset_group_id}`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-bold font-heading">{group.asset_group_name}</h4>
                          {group.total_trades > 0 && (
                            <CaretRight size={16} className="text-white/30" />
                          )}
                        </div>
                        {group.total_trades > 0 ? (
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-white/60 text-sm">Trades</span>
                              <span className="text-white font-mono">{group.total_trades}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/60 text-sm">Win Rate</span>
                              <span className="text-white font-mono">{group.win_rate_excluding_even ?? group.win_rate}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/60 text-sm">Profit Factor</span>
                              <span className={`font-mono ${getProfitFactorColor(group.profit_factor)}`}>
                                {group.profit_factor}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/60 text-sm">Total R</span>
                              <span className={`font-mono ${(group.total_r || 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                                {(group.total_r || 0) >= 0 ? '+' : ''}{group.total_r || 0}R
                              </span>
                            </div>
                          </div>
                        ) : (
                          <p className="text-white/40 text-sm">No trades</p>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
              </div>
            </div>
        )}

        {/* Trade Type Comparison - Collapsible Section */}
        {breakdown && (
          <div className={`mt-6 ${isEditingLayout ? 'ring-1 ring-[#00E676]/20 rounded-lg' : ''}`} data-section-id="tradeType" style={{ order: getSectionCssOrder('tradeType') }}>
              {/* Collapsible Header */}
              <button
                onClick={() => toggleSection('tradeType')}
                className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors mb-3"
                data-testid="section-toggle-trade-type"
              >
                <div className="flex items-center gap-3">
                  {expandedSections.tradeType ? (
                    <CaretDown size={20} weight="bold" className="text-white/60" />
                  ) : (
                    <CaretRight size={20} weight="bold" className="text-white/60" />
                  )}
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-white">Trade Type Comparison</h3>
                    <p className="text-white/40 text-sm">Compare Scalp, Day Trade, and Swing performance</p>
                  </div>
                  <SectionEditControls sectionId="tradeType" />
                </div>
                <div className="flex items-center gap-2 text-white/40 text-xs">
                  <span>Based on your settings</span>
                  <Link 
                    to="/settings" 
                    className="flex items-center gap-1 text-[#00E676] hover:text-[#00E676]/80 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PencilSimple size={12} />
                    Edit
                  </Link>
                </div>
              </button>

              {/* Collapsible Content */}
              <div className={`overflow-hidden transition-all duration-300 ${
                expandedSections.tradeType ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                {/* Setup Filter Row */}
                <div className="flex items-center justify-end mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-white/40 text-xs font-mono">SETUP:</span>
                    <select
                      value={tradeTypeSetupFilter}
                      onChange={(e) => setTradeTypeSetupFilter(e.target.value)}
                      className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs font-mono focus:border-[#00E676] focus:outline-none min-w-[140px]"
                      data-testid="trade-type-setup-filter"
                    >
                      <option value="">All Setups</option>
                      {setups.map((setup) => (
                        <option key={setup.id} value={setup.id}>{setup.name}</option>
                      ))}
                      <option value="__no_setup__">No setup</option>
                    </select>
                  </div>
                </div>

              {/* Filter indicator when setup is selected */}
              {tradeTypeSetupFilter && tradeTypeSetupDisplayName && (
                <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
                  <span>Showing trade type comparison for:</span>
                  <span className="font-bold text-[#00E676]">{tradeTypeSetupDisplayName}</span>
                  <button
                    onClick={() => setTradeTypeSetupFilter("")}
                    className="text-white/40 hover:text-white/60 ml-1"
                    data-testid="clear-trade-type-filter"
                  >
                    (clear)
                  </button>
                </div>
              )}
              
              {/* Content: Cards */}
              {(() => {
                // Use computed data when filter is active, otherwise use breakdown data
                const tradeTypeData = tradeTypeSetupFilter 
                  ? computedTradeTypeData 
                  : breakdown?.trade_type;
                
                // Check if all categories are empty
                const allEmpty = !tradeTypeData ||
                  (!tradeTypeData.scalp?.total_trades && 
                   !tradeTypeData.day_trade?.total_trades && 
                   !tradeTypeData.swing_trade?.total_trades);

                if (allEmpty) {
                  return (
                    <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                        <ChartBar size={32} className="text-white/30" />
                      </div>
                      {tradeTypeSetupFilter ? (
                        <>
                          <h4 className="text-lg font-semibold text-white mb-2">No trade type data for this setup</h4>
                          <p className="text-white/50 text-sm max-w-md mx-auto">
                            This setup does not have enough closed trades to compare Scalp, Day Trade, and Swing performance yet.
                          </p>
                        </>
                      ) : (
                        <>
                          <h4 className="text-lg font-semibold text-white mb-2">No trades classified yet</h4>
                          <p className="text-white/50 text-sm mb-4 max-w-md mx-auto">
                            Start adding trades with timeframes to see your Scalp, Day Trade, and Swing performance breakdown.
                          </p>
                          <Link 
                            to="/dashboard"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm text-sm hover:bg-[#00E676]/90 transition-colors"
                          >
                            Add Trade
                          </Link>
                        </>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Scalp */}
                    <div 
                      className={`bg-[#121212] border border-white/10 rounded-sm p-6 transition-all ${
                        tradeTypeData.scalp?.total_trades > 0 
                          ? 'cursor-pointer hover:border-yellow-500/50 hover:bg-[#121212]/80' 
                          : 'hover:border-yellow-500/30'
                      }`}
                      onClick={() => {
                        if (tradeTypeData.scalp?.total_trades > 0) {
                          navigateToFilteredView('/history', {
                            tradeType: 'scalp',
                            setupFilter: tradeTypeSetupFilter || advancedSetupFilter,
                          });
                        }
                      }}
                      data-testid="trade-type-card-scalp"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                            <Lightning size={18} className="text-yellow-400" />
                          </div>
                          <h4 className="text-lg font-bold font-heading text-yellow-400">Scalp</h4>
                        </div>
                        {tradeTypeData.scalp?.total_trades > 0 && (
                          <CaretRight size={16} className="text-white/30" />
                        )}
                      </div>
                      {tradeTypeData.scalp?.total_trades > 0 ? (
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Trades</span>
                            <span className="text-white font-mono">{tradeTypeData.scalp.total_trades}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Win Rate</span>
                            <span className="text-white font-mono">{tradeTypeData.scalp.win_rate_excluding_even ?? tradeTypeData.scalp.win_rate}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Profit Factor</span>
                            <span className={`font-mono ${getProfitFactorColor(tradeTypeData.scalp.profit_factor)}`}>
                              {tradeTypeData.scalp.profit_factor}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Total R</span>
                            <span className={`font-mono ${(tradeTypeData.scalp.total_rr || 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                              {(tradeTypeData.scalp.total_rr || 0) >= 0 ? '+' : ''}{tradeTypeData.scalp.total_rr || 0}R
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-white/40 text-sm">No scalp trades</p>
                          <p className="text-white/25 text-xs mt-1">Shortest duration trades</p>
                        </div>
                      )}
                    </div>

                    {/* Day Trade */}
                    <div 
                      className={`bg-[#121212] border border-white/10 rounded-sm p-6 transition-all ${
                        tradeTypeData.day_trade?.total_trades > 0 
                          ? 'cursor-pointer hover:border-[#00E676]/50 hover:bg-[#121212]/80' 
                          : 'hover:border-[#00E676]/30'
                      }`}
                      onClick={() => {
                        if (tradeTypeData.day_trade?.total_trades > 0) {
                          navigateToFilteredView('/history', {
                            tradeType: 'day_trade',
                            setupFilter: tradeTypeSetupFilter || advancedSetupFilter,
                          });
                        }
                      }}
                      data-testid="trade-type-card-day"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
                            <TrendUp size={18} className="text-[#00E676]" />
                          </div>
                          <h4 className="text-lg font-bold font-heading text-[#00E676]">Day Trade</h4>
                        </div>
                        {tradeTypeData.day_trade?.total_trades > 0 && (
                          <CaretRight size={16} className="text-white/30" />
                        )}
                      </div>
                      {tradeTypeData.day_trade?.total_trades > 0 ? (
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Trades</span>
                            <span className="text-white font-mono">{tradeTypeData.day_trade.total_trades}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Win Rate</span>
                            <span className="text-white font-mono">{tradeTypeData.day_trade.win_rate_excluding_even ?? tradeTypeData.day_trade.win_rate}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Profit Factor</span>
                            <span className={`font-mono ${getProfitFactorColor(tradeTypeData.day_trade.profit_factor)}`}>
                              {tradeTypeData.day_trade.profit_factor}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Total R</span>
                            <span className={`font-mono ${(tradeTypeData.day_trade.total_rr || 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                              {(tradeTypeData.day_trade.total_rr || 0) >= 0 ? '+' : ''}{tradeTypeData.day_trade.total_rr || 0}R
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-white/40 text-sm">No day trades</p>
                          <p className="text-white/25 text-xs mt-1">Medium duration trades</p>
                        </div>
                      )}
                    </div>

                    {/* Swing Trade */}
                    <div 
                      className={`bg-[#121212] border border-white/10 rounded-sm p-6 transition-all ${
                        tradeTypeData.swing_trade?.total_trades > 0 
                          ? 'cursor-pointer hover:border-purple-500/50 hover:bg-[#121212]/80' 
                          : 'hover:border-purple-500/30'
                      }`}
                      onClick={() => {
                        if (tradeTypeData.swing_trade?.total_trades > 0) {
                          navigateToFilteredView('/history', {
                            tradeType: 'swing_trade',
                            setupFilter: tradeTypeSetupFilter || advancedSetupFilter,
                          });
                        }
                      }}
                      data-testid="trade-type-card-swing"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <Clock size={18} className="text-purple-400" />
                          </div>
                          <h4 className="text-lg font-bold font-heading text-purple-400">Swing Trade</h4>
                        </div>
                        {tradeTypeData.swing_trade?.total_trades > 0 && (
                          <CaretRight size={16} className="text-white/30" />
                        )}
                      </div>
                      {tradeTypeData.swing_trade?.total_trades > 0 ? (
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Trades</span>
                            <span className="text-white font-mono">{tradeTypeData.swing_trade.total_trades}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Win Rate</span>
                            <span className="text-white font-mono">{tradeTypeData.swing_trade.win_rate_excluding_even ?? tradeTypeData.swing_trade.win_rate}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Profit Factor</span>
                            <span className={`font-mono ${getProfitFactorColor(tradeTypeData.swing_trade.profit_factor)}`}>
                              {tradeTypeData.swing_trade.profit_factor}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60 text-sm">Total R</span>
                            <span className={`font-mono ${(tradeTypeData.swing_trade.total_rr || 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                              {(tradeTypeData.swing_trade.total_rr || 0) >= 0 ? '+' : ''}{tradeTypeData.swing_trade.total_rr || 0}R
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-white/40 text-sm">No swing trades</p>
                          <p className="text-white/25 text-xs mt-1">Longest duration trades</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              </div>
            </div>
        )}

      {/* === BEHAVIOR & EXECUTION ANALYSIS === */}
      
      {/* Planned Trades (Not Triggered) Section - Collapsible */}
      <div className={`w-full max-w-[1600px] mx-auto mt-8 ${isEditingLayout ? 'ring-1 ring-[#00E676]/20 rounded-lg' : ''}`} data-testid="canceled-trades-section" data-section-id="planned" style={{ order: getSectionCssOrder('planned') }}>
        {/* Collapsible Header */}
        <button
          onClick={() => toggleSection('planned')}
          className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors mb-3"
          data-testid="section-toggle-planned"
        >
          <div className="flex items-center gap-3">
            {expandedSections.planned ? (
              <CaretDown size={20} weight="bold" className="text-white/60" />
            ) : (
              <CaretRight size={20} weight="bold" className="text-white/60" />
            )}
            <Prohibit size={24} weight="duotone" className="text-[#FFA726]" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-white">Planned Trades (Not Triggered)</h3>
              <p className="text-white/40 text-sm">Setups that were planned but never executed</p>
            </div>
            <SectionEditControls sectionId="planned" />
          </div>
          <span className="text-white/30 text-sm font-mono">
            {filteredCanceledTrades.length} trades
          </span>
        </button>

        {/* Collapsible Content */}
        <div className={`overflow-hidden transition-all duration-300 ${
          expandedSections.planned ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          {/* Setup Filter Row */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-white/30 text-xs max-w-2xl">
              Common reasons: Price never reached entry area · Setup invalidated before entry · Trade intentionally skipped
            </p>
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-xs font-mono">SETUP:</span>
              <select
                value={plannedSetupFilter}
                onChange={(e) => setPlannedSetupFilter(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs font-mono focus:border-[#FFA726] focus:outline-none min-w-[140px]"
                data-testid="planned-setup-filter"
              >
                <option value="">All Setups</option>
                {setups.map((setup) => (
                  <option key={setup.id} value={setup.id}>{setup.name}</option>
                ))}
                <option value="__no_setup__">No setup</option>
              </select>
            </div>
          </div>

        {/* Filter indicator when setup is selected */}
        {plannedSetupFilter && plannedSetupDisplayName && (
          <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
            <span>Showing planned trades for:</span>
            <span className="font-bold text-[#FFA726]">{plannedSetupDisplayName}</span>
            <button
              onClick={() => setPlannedSetupFilter("")}
              className="text-white/40 hover:text-white/60 ml-1"
              data-testid="clear-planned-filter"
            >
              (clear)
            </button>
          </div>
        )}

        {filteredCanceledTrades.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center" data-testid="no-canceled-trades">
            <div className="w-16 h-16 bg-[#FFA726]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Prohibit size={32} weight="duotone" className="text-[#FFA726]/60" />
            </div>
            <h4 className="text-xl font-bold font-heading mb-2 text-white/80">
              {plannedSetupFilter ? "No planned trades for this setup yet" : "No planned trades skipped yet"}
            </h4>
            <p className="text-white/50">
              {plannedSetupFilter ? "Try selecting a different setup" : "Trades marked as canceled will appear here"}
            </p>
          </div>
        ) : (
          <div
            className="bg-[#121212] border border-white/10 rounded-sm p-6"
            data-testid="canceled-trades-card"
          >
            {/* KPIs Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div 
                className="bg-[#000000] border border-white/10 rounded-sm p-4 cursor-pointer hover:border-[#FFA726]/50 hover:bg-[#FFA726]/5 transition-colors group"
                onClick={() => {
                  // Navigate to filtered view - history in public mode, detail page in private mode
                  const filter = { status: 'canceled' };
                  if (plannedSetupFilter && plannedSetupFilter !== 'all') {
                    filter.setup = plannedSetupFilter;
                  }
                  const privateUrl = plannedSetupFilter && plannedSetupFilter !== 'all'
                    ? `/advanced/planned-trades?setup=${encodeURIComponent(plannedSetupFilter)}`
                    : '/advanced/planned-trades';
                  navigateToFilteredView(privateUrl, filter);
                }}
                data-testid="total-not-triggered-card"
              >
                <div className="text-white/40 text-xs font-mono mb-1 flex items-center justify-between">
                  <span>TOTAL NOT TRIGGERED</span>
                  <span className="text-white/20 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">View all →</span>
                </div>
                <div className="text-2xl font-bold font-mono text-[#FFA726]" data-testid="canceled-count">
                  {filteredCanceledTrades.length}
                </div>
              </div>
              <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                <div className="text-white/40 text-xs font-mono mb-1">% OF TOTAL TRADES</div>
                <div className="text-2xl font-bold font-mono text-white" data-testid="canceled-percentage">
                  {totalTradesForPlannedFilter > 0 ? ((filteredCanceledTrades.length / totalTradesForPlannedFilter) * 100).toFixed(1) : 0}%
                </div>
              </div>
            </div>
            
            {/* Planned Trades Insight Card */}
            {plannedTradesInsight && (
              <div 
                className="mt-4 p-4 rounded-sm border"
                style={{ 
                  backgroundColor: `${plannedTradesInsight.color}08`,
                  borderColor: `${plannedTradesInsight.color}30`
                }}
                data-testid="planned-trades-insight-card"
              >
                <div className="flex items-start gap-3">
                  <span className="text-lg">{plannedTradesInsight.icon}</span>
                  <div className="flex-1">
                    <div className="text-white/40 text-[10px] font-mono mb-1">INSIGHT</div>
                    <h5 
                      className="font-bold text-sm mb-1"
                      style={{ color: plannedTradesInsight.color }}
                    >
                      {plannedTradesInsight.title}
                    </h5>
                    <p className="text-white/60 text-xs leading-relaxed">
                      {plannedTradesInsight.message}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Cancel Reason Breakdown - Always visible when there are canceled trades */}
            {cancelReasonBreakdown && (
              <div className="pt-4 border-t border-white/5">
                <div className="text-white/40 text-xs font-mono mb-3">REASON BREAKDOWN</div>
                <div className="grid grid-cols-3 gap-3">
                  {cancelReasonBreakdown.map((item, idx) => (
                    <div key={idx} className="bg-[#0A0A0A] border border-white/5 rounded-sm px-3 py-2 text-center">
                      <div className="text-white/50 text-[10px] font-mono mb-1 truncate">{item.label}</div>
                      <div className="text-[#FFA726] text-lg font-mono font-bold">
                        {item.percentage}%
                      </div>
                      <div className="text-white/30 text-[10px]">({item.count})</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Partial Execution Section - Collapsible */}
      <div className={`w-full max-w-[1600px] mx-auto mt-8 ${isEditingLayout ? 'ring-1 ring-[#00E676]/20 rounded-lg' : ''}`} data-testid="partial-trades-section" data-section-id="partial" style={{ order: getSectionCssOrder('partial') }}>
        {/* Collapsible Header */}
        <button
          onClick={() => toggleSection('partial')}
          className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/8 transition-colors mb-3"
          data-testid="section-toggle-partial"
        >
          <div className="flex items-center gap-3">
            {expandedSections.partial ? (
              <CaretDown size={20} weight="bold" className="text-white/60" />
            ) : (
              <CaretRight size={20} weight="bold" className="text-white/60" />
            )}
            <Percent size={24} weight="duotone" className="text-[#00B8D4]" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-white">Partial Execution</h3>
              <p className="text-white/40 text-sm">Trades closed with partial takes or scaled exits</p>
            </div>
            <SectionEditControls sectionId="partial" />
          </div>
          <span className="text-white/30 text-sm font-mono">
            {partialTradesData.total} trades
          </span>
        </button>

        {/* Collapsible Content */}
        <div className={`overflow-hidden transition-all duration-300 ${
          expandedSections.partial ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          {/* Setup Filter Row */}
          <div className="flex items-center justify-end mb-4">
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-xs font-mono">SETUP:</span>
              <select
                value={partialSetupFilter}
                onChange={(e) => setPartialSetupFilter(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs font-mono focus:border-[#00B8D4] focus:outline-none min-w-[140px]"
                data-testid="partial-setup-filter"
              >
                <option value="">All Setups</option>
                {setups.map((setup) => (
                  <option key={setup.id} value={setup.id}>{setup.name}</option>
                ))}
                <option value="__no_setup__">No setup</option>
              </select>
            </div>
          </div>

        {/* Filter indicator when setup is selected */}
        {partialSetupFilter && partialSetupDisplayName && (
          <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
            <span>Showing partial analytics for:</span>
            <span className="font-bold text-[#00B8D4]">{partialSetupDisplayName}</span>
            <button
              onClick={() => setPartialSetupFilter("")}
              className="text-white/40 hover:text-white/60 ml-1"
              data-testid="clear-partial-filter"
            >
              (clear)
            </button>
          </div>
        )}

        {partialTradesData.total === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center" data-testid="no-partial-trades">
            <div className="w-16 h-16 bg-[#00B8D4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Percent size={32} weight="duotone" className="text-[#00B8D4]/60" />
            </div>
            <h4 className="text-xl font-bold font-heading mb-2 text-white/80">No partial trades yet</h4>
            <p className="text-white/50">
              Trades closed before full TP/SL will appear here
            </p>
          </div>
        ) : (
          <div
            className="bg-[#121212] border border-white/10 rounded-sm p-6"
            data-testid="partial-trades-card"
          >
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div 
                className="bg-[#000000] border border-white/10 rounded-sm p-4 cursor-pointer hover:border-[#00B8D4]/50 hover:bg-[#00B8D4]/5 transition-colors group"
                onClick={() => {
                  // Navigate to filtered view - history in public mode, detail page in private mode
                  const filter = { hasPartialExecution: true };
                  if (partialSetupFilter) {
                    filter.setup = partialSetupFilter;
                  }
                  const privateUrl = partialSetupFilter
                    ? `/advanced/partial-execution?setup=${encodeURIComponent(partialSetupFilter)}`
                    : '/advanced/partial-execution';
                  navigateToFilteredView(privateUrl, filter);
                }}
                data-testid="total-partial-card"
              >
                <div className="text-white/40 text-xs font-mono mb-1 flex items-center justify-between">
                  <span>TOTAL PARTIAL</span>
                  <span className="text-white/20 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">View all →</span>
                </div>
                <div className="text-2xl font-bold font-mono text-[#00B8D4]" data-testid="partial-count">
                  {partialTradesData.total}
                </div>
              </div>
              <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                <div className="text-white/40 text-xs font-mono mb-1">% OF TOTAL TRADES</div>
                <div className="text-2xl font-bold font-mono text-white" data-testid="partial-percentage">
                  {totalTrades > 0 ? ((partialTradesData.total / totalTrades) * 100).toFixed(1) : 0}%
                </div>
              </div>
              <div className="bg-[#000000] border border-[#FF3B30]/20 rounded-sm p-4">
                <div className="text-white/40 text-xs font-mono mb-1">REDUCED LOSSES</div>
                <div className="text-2xl font-bold font-mono text-[#FF3B30]" data-testid="reduced-losses-count">
                  {partialTradesData.reducedLosses}
                </div>
              </div>
              <div className="bg-[#000000] border border-[#00E676]/20 rounded-sm p-4">
                <div className="text-white/40 text-xs font-mono mb-1">PARTIAL PROFITS</div>
                <div className="text-2xl font-bold font-mono text-[#00E676]" data-testid="partial-profits-count">
                  {partialTradesData.partialProfits}
                </div>
              </div>
            </div>
            
            {/* Info text */}
            <div className="text-white/40 text-sm text-center pt-2 border-t border-white/5">
              Trades where |profit| &lt; hand size (closed before full TP/SL)
            </div>
          </div>
        )}

        {/* Partial Action Analytics - Sub-block within Partial Execution */}
        <div className="mt-6 bg-[#0A0A0A] border border-white/10 rounded-sm p-6" data-testid="partial-analytics-section">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
            <div className="w-10 h-10 bg-[#9C27B0]/10 rounded-sm flex items-center justify-center">
              <Percent size={20} weight="duotone" className="text-[#9C27B0]" />
            </div>
            <div>
              <h4 className="font-bold font-heading text-white">Partial Action Analytics</h4>
              <p className="text-white/40 text-xs">Performance after first partial execution</p>
            </div>
          </div>

          {partialAnalytics.partialUsedCount === 0 ? (
            <div className="text-center py-6">
              <p className="text-white/50 text-sm">No trades with partial action yet</p>
            </div>
          ) : (
            <>
              {/* KPI Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Trades With Partial */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-[10px] font-mono mb-1">TRADES W/ PARTIAL</div>
                  <div className="text-xl font-bold font-mono text-[#9C27B0]" data-testid="partial-count-adv">
                    {partialAnalytics.partialUsedCount}
                  </div>
                  <div className="text-white/30 text-[10px] mt-1">
                    {partialAnalytics.percentage}% of completed
                  </div>
                </div>

                {/* Avg First Partial RR */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-[10px] font-mono mb-1">AVG 1ST PARTIAL RR</div>
                  <div className="text-xl font-bold font-mono text-[#00B8D4]" data-testid="avg-first-partial-rr">
                    {partialAnalytics.avgFirstPartialRR}R
                  </div>
                  <div className="text-white/30 text-[10px] mt-1">
                    RR at first partial
                  </div>
                </div>

                {/* Avg Final Realized R */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-[10px] font-mono mb-1">AVG FINAL R</div>
                  <div className={`text-xl font-bold font-mono ${
                    parseFloat(partialAnalytics.avgFinalRealizedR) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                  }`} data-testid="avg-final-realized-r">
                    {partialAnalytics.avgFinalRealizedR}R
                  </div>
                  <div className="text-white/30 text-[10px] mt-1">
                    Final realized R
                  </div>
                </div>

                {/* Avg R Given Back */}
                <div className="bg-[#000000] border border-[#FF9800]/20 rounded-sm p-3">
                  <div className="text-white/40 text-[10px] font-mono mb-1">AVG R GIVEN BACK</div>
                  <div className="text-xl font-bold font-mono text-[#FF9800]" data-testid="avg-given-back-r">
                    {partialAnalytics.avgGivenBackR}R
                  </div>
                  <div className="text-white/30 text-[10px] mt-1">
                    Avg opportunity cost
                  </div>
                </div>

                {/* Total Given Back USD */}
                <div className="bg-[#000000] border border-[#FF9800]/20 rounded-sm p-3">
                  <div className="text-white/40 text-[10px] font-mono mb-1">TOTAL GIVEN BACK</div>
                  <div className="text-xl font-bold font-mono text-[#FF9800]" data-testid="total-given-back-usd">
                    ${partialAnalytics.totalGivenBackUsd}
                  </div>
                  <div className="text-white/30 text-[10px] mt-1">
                    Potential profit lost
                  </div>
                </div>
              </div>

              {/* Info text */}
              <div className="text-white/40 text-xs text-center pt-3 mt-3 border-t border-white/5">
                Given back = max(0, first_partial_RR - final_realized_R) × hand_size
              </div>

              {/* Insight Card */}
              {partialInsight && (
                <div 
                  className="mt-4 p-4 rounded-sm border"
                  style={{ 
                    backgroundColor: `${partialInsight.color}08`,
                    borderColor: `${partialInsight.color}30`
                  }}
                  data-testid="partial-insight-card"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-lg">{partialInsight.icon}</span>
                    <div className="flex-1">
                      <div className="text-white/40 text-[10px] font-mono mb-1">INSIGHT</div>
                      <h5 
                        className="font-bold text-sm mb-1"
                        style={{ color: partialInsight.color }}
                      >
                        {partialInsight.title}
                      </h5>
                      <p className="text-white/60 text-xs leading-relaxed">
                        {partialInsight.message}
                      </p>
                      {/* Supporting numeric context */}
                      {partialInsight.context && (
                        <p className="text-white/40 text-[10px] mt-2 font-mono">
                          {partialInsight.context}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Partial Decision Card */}
              {partialDecision && (
                <div 
                  className="mt-4 p-4 rounded-sm border"
                  style={{ 
                    backgroundColor: `${partialDecision.color}08`,
                    borderColor: `${partialDecision.color}30`
                  }}
                  data-testid="partial-decision-card"
                >
                  <div className="flex items-start gap-3">
                    {partialDecision.icon === 'positive' && (
                      <Check size={20} weight="bold" style={{ color: partialDecision.color }} />
                    )}
                    {partialDecision.icon === 'negative' && (
                      <Warning size={20} weight="bold" style={{ color: partialDecision.color }} />
                    )}
                    {partialDecision.icon === 'neutral' && (
                      <Info size={20} weight="duotone" style={{ color: partialDecision.color }} />
                    )}
                    <div className="flex-1">
                      <div className="text-white/40 text-[10px] font-mono mb-1">STRATEGIC VERDICT</div>
                      <h5 
                        className="font-bold text-sm mb-1"
                        style={{ color: partialDecision.color }}
                        data-testid="partial-decision-title"
                      >
                        {partialDecision.title}
                      </h5>
                      <p className="text-white/60 text-xs leading-relaxed">
                        {partialDecision.subtext}
                      </p>
                    </div>
                  </div>
                  
                  {/* Supporting metrics - only show if we have data */}
                  {partialDecision.avgPartialR !== null && (
                    <div className="flex items-center gap-3 pt-3 mt-3 border-t border-white/10">
                      <div className="flex-1 text-center">
                        <div className="text-white/40 text-[10px] font-mono">WITH PARTIAL</div>
                        <div className={`text-sm font-bold font-mono ${
                          partialDecision.avgPartialR >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                        }`}>
                          {partialDecision.avgPartialR.toFixed(2)}R
                        </div>
                        <div className="text-white/30 text-[10px]">
                          {partialDecision.partialCount} trades
                        </div>
                      </div>
                      <div className="text-white/20 text-sm font-mono">vs</div>
                      <div className="flex-1 text-center">
                        <div className="text-white/40 text-[10px] font-mono">WITHOUT PARTIAL</div>
                        <div className={`text-sm font-bold font-mono ${
                          partialDecision.avgNoPartialR >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                        }`}>
                          {partialDecision.avgNoPartialR.toFixed(2)}R
                        </div>
                        <div className="text-white/30 text-[10px]">
                          {partialDecision.noPartialCount} trades
                        </div>
                      </div>
                      <div className="text-white/20 text-sm font-mono">=</div>
                      <div className="flex-1 text-center">
                        <div className="text-white/40 text-[10px] font-mono">DELTA</div>
                        <div className={`text-sm font-bold font-mono ${
                          partialDecision.deltaR >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                        }`}>
                          {partialDecision.deltaR >= 0 ? '+' : ''}{partialDecision.deltaR.toFixed(2)}R
                        </div>
                        <div className="text-white/30 text-[10px]">
                          difference
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* EXECUTION QUALITY SECTION - Collapsible */}
      {/* ============================================ */}
      <div className={`bg-[#121212] border border-white/10 rounded-sm p-6 mt-8 ${isEditingLayout ? 'ring-1 ring-[#00E676]/20' : ''}`} data-testid="execution-quality-section" data-section-id="executionQuality" style={{ order: getSectionCssOrder('executionQuality') }}>
        {/* Collapsible Header */}
        <button
          onClick={() => toggleSection('executionQuality')}
          className="w-full flex items-center justify-between mb-3"
          data-testid="section-toggle-execution-quality"
        >
          <div className="flex items-center gap-3">
            {expandedSections.executionQuality ? (
              <CaretDown size={20} weight="bold" className="text-white/60" />
            ) : (
              <CaretRight size={20} weight="bold" className="text-white/60" />
            )}
            <Crosshair size={28} weight="duotone" className="text-white/60" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-white">Execution Quality</h3>
              <p className="text-white/40 text-sm">Measure how well you follow your plan</p>
            </div>
            <SectionEditControls sectionId="executionQuality" />
          </div>
          <span className="text-white/30 text-sm font-mono">
            {executionQualityData?.totalTrades || 0} trades
          </span>
        </button>

        {/* Collapsible Content */}
        <div className={`overflow-hidden transition-all duration-300 ${
          expandedSections.executionQuality ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          {/* Setup Filter Row */}
          <div className="flex items-center justify-end mb-4">
            <Select value={executionSetupFilter} onValueChange={setExecutionSetupFilter}>
              <SelectTrigger className="w-[180px] bg-[#0A0A0A] border-white/10 text-white text-sm">
                <SelectValue placeholder="All Setups" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/5">All Setups</SelectItem>
                <SelectItem value="__no_setup__" className="text-white/60 hover:bg-white/5">No setup</SelectItem>
                {setups.map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-white hover:bg-white/5">
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        {/* Setup Filter Badge */}
        {executionSetupDisplayName && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-white/40 text-xs font-mono">FILTERED BY:</span>
            <span className="text-[#00E676] text-xs font-mono px-2 py-1 bg-[#00E676]/10 border border-[#00E676]/20 rounded">
              {executionSetupDisplayName}
            </span>
          </div>
        )}

        {/* Main Metrics Cards */}
        {executionQualityData.total === 0 ? (
          <div className="text-center py-12">
            <Crosshair size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            <p className="text-white/40">No closed trades to analyze</p>
            <p className="text-white/30 text-sm mt-1">
              {executionSetupDisplayName 
                ? `No trades found for "${executionSetupDisplayName}"`
                : "Complete some trades to see execution metrics"
              }
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Card 1: Followed Plan % */}
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Target size={20} weight="duotone" className="text-white/60" />
                  <span className="text-white/40 text-xs font-mono uppercase">Followed Plan</span>
                  <div className="relative group">
                    <Info size={14} weight="fill" className="text-white/30 hover:text-white/60 cursor-help transition-colors" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-[#1A1A1A] border border-white/10 rounded-sm text-xs text-white/80 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-lg">
                      <div className="font-semibold text-white mb-1">What does this mean?</div>
                      <div className="text-white/60 leading-relaxed">
                        Followed Plan measures whether you respected your risk and avoided exiting too early. A trade follows plan when loss stays within risk and exit wasn't premature.
                      </div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1A1A1A]"></div>
                    </div>
                  </div>
                </div>
                
                <div className="text-5xl font-bold font-mono mb-2" style={{
                  color: executionQualityData.disciplineLevel === 'high' 
                    ? '#00E676' 
                    : executionQualityData.disciplineLevel === 'moderate'
                    ? '#FFA726'
                    : '#FF3B30'
                }}>
                  {executionQualityData.followedPlanPercent}%
                </div>
                
                <div className="text-white/60 text-sm mb-3">
                  Risk respected & exit not premature
                </div>
                
                <div className={`text-xs font-mono px-2 py-1 rounded inline-block ${
                  executionQualityData.disciplineLevel === 'high'
                    ? 'bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/20'
                    : executionQualityData.disciplineLevel === 'moderate'
                    ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                  {executionQualityData.disciplineLevel === 'high' && 'High discipline'}
                  {executionQualityData.disciplineLevel === 'moderate' && 'Mostly consistent'}
                  {executionQualityData.disciplineLevel === 'low' && 'Needs review'}
                </div>
                
                <div className="text-white/30 text-xs mt-3">
                  Based on {executionQualityData.eligibleCount || executionQualityData.total} eligible trades
                </div>
              </div>

              {/* Card 2: Hold Time (Split by Trade Type) */}
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={20} weight="duotone" className="text-white/60" />
                  <span className="text-white/40 text-xs font-mono uppercase">Hold Time</span>
                  <div className="relative group">
                    <Info size={14} weight="fill" className="text-white/30 hover:text-white/60 cursor-help transition-colors" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-[#1A1A1A] border border-white/10 rounded-sm text-xs text-white/80 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-lg">
                      <div className="font-semibold text-white mb-1">What does this mean?</div>
                      <div className="text-white/60 leading-relaxed">
                        Hold Time compares how long you keep trades open versus your historical behavior. Expected is based on your average win and loss duration. Holding too long may indicate letting losers run or hesitation to exit.
                      </div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1A1A1A]"></div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Day Trade Hold Time */}
                  <div className="pb-3 border-b border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Sun size={14} weight="fill" className="text-yellow-400/70" />
                      <span className="text-white/50 text-xs font-mono">Day Trade</span>
                    </div>
                    {executionQualityData.dayTradeMetrics?.tradesCount >= 3 ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold font-mono text-white">
                            {formatHoldTime(executionQualityData.dayTradeMetrics.avgHoldMinutes)}
                          </span>
                          <span className="text-white/40 text-sm font-mono">vs</span>
                          <span className="text-lg font-mono text-white/50">
                            {formatHoldTime(executionQualityData.dayTradeMetrics.expectedHoldMinutes)}
                          </span>
                        </div>
                        <div className={`text-[10px] font-mono px-1.5 py-0.5 rounded inline-block mt-1 ${
                          executionQualityData.dayTradeMetrics.interpretation === 'aligned'
                            ? 'bg-[#00E676]/10 text-[#00E676]'
                            : executionQualityData.dayTradeMetrics.interpretation === 'early'
                            ? 'bg-orange-500/10 text-orange-400'
                            : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {executionQualityData.dayTradeMetrics.interpretation === 'aligned' && 'Aligned'}
                          {executionQualityData.dayTradeMetrics.interpretation === 'early' && 'Exiting early'}
                          {executionQualityData.dayTradeMetrics.interpretation === 'long' && 'Holding too long'}
                        </div>
                      </>
                    ) : (
                      <span className="text-white/30 text-xs">Not enough data ({executionQualityData.dayTradeMetrics?.tradesCount || 0} trades)</span>
                    )}
                  </div>
                  
                  {/* Swing Trade Hold Time */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Moon size={14} weight="fill" className="text-blue-400/70" />
                      <span className="text-white/50 text-xs font-mono">Swing Trade</span>
                    </div>
                    {executionQualityData.swingTradeMetrics?.tradesCount >= 3 ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold font-mono text-white">
                            {formatHoldTime(executionQualityData.swingTradeMetrics.avgHoldMinutes)}
                          </span>
                          <span className="text-white/40 text-sm font-mono">vs</span>
                          <span className="text-lg font-mono text-white/50">
                            {formatHoldTime(executionQualityData.swingTradeMetrics.expectedHoldMinutes)}
                          </span>
                        </div>
                        <div className={`text-[10px] font-mono px-1.5 py-0.5 rounded inline-block mt-1 ${
                          executionQualityData.swingTradeMetrics.interpretation === 'aligned'
                            ? 'bg-[#00E676]/10 text-[#00E676]'
                            : executionQualityData.swingTradeMetrics.interpretation === 'early'
                            ? 'bg-orange-500/10 text-orange-400'
                            : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {executionQualityData.swingTradeMetrics.interpretation === 'aligned' && 'Aligned'}
                          {executionQualityData.swingTradeMetrics.interpretation === 'early' && 'Exiting early'}
                          {executionQualityData.swingTradeMetrics.interpretation === 'long' && 'Holding too long'}
                        </div>
                      </>
                    ) : (
                      <span className="text-white/30 text-xs">Not enough data ({executionQualityData.swingTradeMetrics?.tradesCount || 0} trades)</span>
                    )}
                  </div>
                </div>
                
                <div className="text-white/30 text-xs mt-4 pt-3 border-t border-white/5">
                  Based on {executionQualityData.total} closed trades
                </div>
              </div>
            </div>

            {/* Mini Breakdown Row - Risk Violations and Early Exits */}
            <div className="flex gap-4 mb-6">
              <div 
                className="flex items-center gap-2 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-sm cursor-pointer hover:border-red-500/50 hover:bg-red-500/5 transition-colors group"
                onClick={() => navigateToFilteredView('/advanced/risk-violations', { riskViolation: true })}
                data-testid="risk-violations-link"
                title="Trades where realized loss went beyond the planned stop/risk"
              >
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span className="text-white/60 text-xs">Risk Violations</span>
                <div className="relative group/tooltip">
                  <Info size={10} weight="fill" className="text-white/20 hover:text-white/40 cursor-help" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-44 p-2 bg-[#1A1A1A] border border-white/10 rounded text-[10px] text-white/70 opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all z-50 shadow-lg">
                    Loss exceeded planned risk
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1A1A1A]"></div>
                  </div>
                </div>
                <span className="text-white font-mono text-sm">{executionQualityData.riskViolationPercent}%</span>
                <span className="text-white/20 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">→</span>
              </div>
              <div 
                className="flex items-center gap-2 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-sm cursor-pointer hover:border-orange-500/50 hover:bg-orange-500/5 transition-colors group"
                onClick={() => navigateToFilteredView('/advanced/early-exits', { earlyExit: true })}
                data-testid="early-exits-link"
                title="Trades closed before reaching at least ~80% of planned RR"
              >
                <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                <span className="text-white/60 text-xs">Early Exits</span>
                <div className="relative group/tooltip">
                  <Info size={10} weight="fill" className="text-white/20 hover:text-white/40 cursor-help" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-44 p-2 bg-[#1A1A1A] border border-white/10 rounded text-[10px] text-white/70 opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all z-50 shadow-lg">
                    Closed early relative to planned RR
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1A1A1A]"></div>
                  </div>
                </div>
                <span className="text-white font-mono text-sm">{executionQualityData.earlyExitPercent}%</span>
                <span className="text-white/20 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">→</span>
              </div>
            </div>

            {/* Insight Block */}
            {executionQualityData.insight && (
              <div 
                className="p-4 rounded-sm border"
                style={{
                  backgroundColor: `${executionQualityData.insight.color}10`,
                  borderColor: `${executionQualityData.insight.color}30`
                }}
              >
                <div className="flex items-start gap-3">
                  <span className="text-lg">{executionQualityData.insight.icon}</span>
                  <div className="flex-1">
                    <div className="text-white/40 text-[10px] font-mono mb-1">EXECUTION INSIGHT</div>
                    <p 
                      className="text-sm leading-relaxed"
                      style={{ color: executionQualityData.insight.color }}
                    >
                      {executionQualityData.insight.text}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* ============================================ */}
      {/* HEDGE ANALYTICS SECTION - Collapsible */}
      {/* ============================================ */}
      <div className={`bg-[#121212] border border-white/10 rounded-sm p-6 mt-8 ${isEditingLayout ? 'ring-1 ring-[#00E676]/20' : ''}`} data-testid="hedge-analytics-section" data-section-id="hedge" style={{ order: getSectionCssOrder('hedge') }}>
        {/* Collapsible Header */}
        <button
          onClick={() => toggleSection('hedge')}
          className="w-full flex items-center justify-between mb-3"
          data-testid="section-toggle-hedge"
        >
          <div className="flex items-center gap-3">
            {expandedSections.hedge ? (
              <CaretDown size={20} weight="bold" className="text-white/60" />
            ) : (
              <CaretRight size={20} weight="bold" className="text-white/60" />
            )}
            <Shield size={28} weight="duotone" className="text-cyan-400" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-white">Hedge Analytics</h3>
              <p className="text-white/40 text-sm">Measure the impact of your hedging decisions</p>
            </div>
            <SectionEditControls sectionId="hedge" />
          </div>
          <span className="text-white/30 text-sm font-mono">
            {hedgeAnalyticsData?.totalHedgedTrades || 0} hedged
          </span>
        </button>

        {/* Collapsible Content */}
        <div className={`overflow-hidden transition-all duration-300 ${
          expandedSections.hedge ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          {/* Setup Filter Row */}
          <div className="flex items-center justify-end mb-4">
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-xs font-mono">SETUP:</span>
              <select
                value={hedgeSetupFilter}
                onChange={(e) => setHedgeSetupFilter(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs font-mono focus:border-cyan-500 focus:outline-none min-w-[140px]"
                data-testid="hedge-setup-filter"
              >
                <option value="">All Setups</option>
                {setups.map((setup) => (
                  <option key={setup.id} value={setup.id}>{setup.name}</option>
                ))}
                <option value="__no_setup__">No setup</option>
              </select>
            </div>
          </div>

        {/* Filter indicator when setup is selected */}
        {hedgeSetupFilter && hedgeSetupDisplayName && (
          <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
            <span>Showing hedge analytics for:</span>
            <span className="font-bold text-cyan-400">{hedgeSetupDisplayName}</span>
            <button
              onClick={() => setHedgeSetupFilter("")}
              className="text-white/40 hover:text-white/60 ml-1"
              data-testid="clear-hedge-filter"
            >
              (clear)
            </button>
          </div>
        )}

        {hedgeAnalyticsData.totalHedgedTrades === 0 ? (
          <div className="text-center py-12">
            <Shield size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            {hedgeSetupFilter ? (
              <>
                <p className="text-white/40 font-semibold">No hedge data for this setup</p>
                <p className="text-white/30 text-sm mt-1">
                  This setup has no recorded hedge activity yet.
                </p>
              </>
            ) : (
              <>
                <p className="text-white/40">No hedged trades to analyze</p>
                <p className="text-white/30 text-sm mt-1">
                  Add hedges to your open positions to see analytics here
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Main Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Hedged Trades Count - CLICKABLE */}
              <div 
                className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4 cursor-pointer hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-all group"
                onClick={() => {
                  const filter = { hasHedge: true };
                  if (hedgeSetupFilter) {
                    filter.setup = hedgeSetupFilter;
                  }
                  const privateUrl = hedgeSetupFilter 
                    ? `/advanced/hedge-trades?setup=${encodeURIComponent(hedgeSetupFilter)}`
                    : '/advanced/hedge-trades';
                  navigateToFilteredView(privateUrl, filter);
                }}
                data-testid="hedged-trades-card-link"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Shield size={18} weight="duotone" className="text-cyan-400" />
                    <span className="text-white/40 text-xs font-mono uppercase">Hedged Trades</span>
                  </div>
                  <span className="text-white/30 text-xs group-hover:text-cyan-400 transition-colors">View all →</span>
                </div>
                <div className="text-4xl font-bold font-mono text-white mb-2">
                  {hedgeAnalyticsData.totalHedgedTrades}
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="text-[#00E676]">{hedgeAnalyticsData.beneficialCount} beneficial</span>
                  <span className="text-[#FF3B30]">{hedgeAnalyticsData.harmfulCount} harmful</span>
                  <span className="text-white/40">{hedgeAnalyticsData.neutralCount} neutral</span>
                </div>
              </div>

              {/* Beneficial Rate */}
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendUp size={18} weight="duotone" className="text-[#00E676]" />
                  <span className="text-white/40 text-xs font-mono uppercase">Beneficial Rate</span>
                </div>
                <div className={`text-4xl font-bold font-mono mb-2 ${
                  hedgeAnalyticsData.beneficialPercent >= 60 ? 'text-[#00E676]' :
                  hedgeAnalyticsData.beneficialPercent >= 40 ? 'text-[#FFA726]' :
                  'text-[#FF3B30]'
                }`}>
                  {hedgeAnalyticsData.beneficialPercent}%
                </div>
                <div className="text-white/40 text-xs">
                  of hedged trades improved
                </div>
              </div>

              {/* Net Hedge Impact */}
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target size={18} weight="duotone" className="text-white/60" />
                  <span className="text-white/40 text-xs font-mono uppercase">Net Impact</span>
                </div>
                <div className={`text-4xl font-bold font-mono mb-2 ${
                  hedgeAnalyticsData.netDifference > 0 ? 'text-[#00E676]' :
                  hedgeAnalyticsData.netDifference < 0 ? 'text-[#FF3B30]' :
                  'text-white/60'
                }`}>
                  {hedgeAnalyticsData.netDifference >= 0 ? '+' : ''}${hedgeAnalyticsData.netDifference.toFixed(2)}
                </div>
                <div className="text-white/40 text-xs">
                  total hedge contribution
                </div>
              </div>
            </div>

            {/* Comparison Panel */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5 mb-6">
              <div className="text-white/40 text-xs font-mono mb-4 uppercase">Comparison: Without vs With Hedge</div>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-white/60 text-sm mb-2">Total Without Hedge</div>
                  <div className={`text-2xl font-mono font-bold ${
                    hedgeAnalyticsData.totalWithoutHedge >= 0 ? 'text-white/60' : 'text-white/60'
                  }`}>
                    {hedgeAnalyticsData.totalWithoutHedge >= 0 ? '+' : ''}${hedgeAnalyticsData.totalWithoutHedge.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-white/60 text-sm mb-2">Total With Hedge (Actual)</div>
                  <div className={`text-2xl font-mono font-bold ${
                    hedgeAnalyticsData.totalWithHedge > 0 ? 'text-[#00E676]' :
                    hedgeAnalyticsData.totalWithHedge < 0 ? 'text-[#FF3B30]' :
                    'text-white'
                  }`}>
                    {hedgeAnalyticsData.totalWithHedge >= 0 ? '+' : ''}${hedgeAnalyticsData.totalWithHedge.toFixed(2)}
                  </div>
                </div>
              </div>
              
              {/* Visual Progress Bar */}
              <div className="mt-6">
                <div className="flex justify-between text-xs text-white/40 mb-2">
                  <span>Harmful ({hedgeAnalyticsData.harmfulPercent}%)</span>
                  <span>Beneficial ({hedgeAnalyticsData.beneficialPercent}%)</span>
                </div>
                <div className="h-3 bg-white/5 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-[#FF3B30] transition-all duration-500"
                    style={{ width: `${hedgeAnalyticsData.harmfulPercent}%` }}
                  />
                  <div 
                    className="bg-white/20 transition-all duration-500"
                    style={{ width: `${100 - hedgeAnalyticsData.harmfulPercent - hedgeAnalyticsData.beneficialPercent}%` }}
                  />
                  <div 
                    className="bg-[#00E676] transition-all duration-500"
                    style={{ width: `${hedgeAnalyticsData.beneficialPercent}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Average Impacts */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-[#0A0A0A] border border-[#00E676]/20 rounded-sm p-4">
                <div className="text-[#00E676]/60 text-xs font-mono mb-2">Avg Beneficial Impact</div>
                <div className="text-[#00E676] text-xl font-mono font-bold">
                  +${hedgeAnalyticsData.avgBeneficialImpact.toFixed(2)}
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-[#FF3B30]/20 rounded-sm p-4">
                <div className="text-[#FF3B30]/60 text-xs font-mono mb-2">Avg Harmful Impact</div>
                <div className="text-[#FF3B30] text-xl font-mono font-bold">
                  ${hedgeAnalyticsData.avgHarmfulImpact.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Verdict and Insight */}
            {hedgeAnalyticsData.verdict && (
              <div 
                className="p-4 rounded-sm border mb-4"
                style={{
                  backgroundColor: `${hedgeAnalyticsData.verdict.color}10`,
                  borderColor: `${hedgeAnalyticsData.verdict.color}30`
                }}
              >
                <div className="flex items-center gap-2">
                  <Shield size={20} weight="duotone" style={{ color: hedgeAnalyticsData.verdict.color }} />
                  <span className="font-bold" style={{ color: hedgeAnalyticsData.verdict.color }}>
                    {hedgeAnalyticsData.verdict.text}
                  </span>
                </div>
              </div>
            )}

            {hedgeAnalyticsData.insight && (
              <div 
                className="p-4 rounded-sm border"
                style={{
                  backgroundColor: `${hedgeAnalyticsData.insight.color}10`,
                  borderColor: `${hedgeAnalyticsData.insight.color}30`
                }}
              >
                <div className="text-white/40 text-[10px] font-mono mb-1">HEDGE INSIGHT</div>
                <p className="text-sm" style={{ color: hedgeAnalyticsData.insight.color }}>
                  {hedgeAnalyticsData.insight.text}
                </p>
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* ============================================ */}
      {/* HAND SIZE CONSISTENCY SECTION - Collapsible */}
      {/* ============================================ */}
      <div className={`bg-[#121212] border border-white/10 rounded-sm p-6 mt-8 ${isEditingLayout ? 'ring-1 ring-[#00E676]/20' : ''}`} data-testid="hand-size-consistency-section" data-section-id="handSize" style={{ order: getSectionCssOrder('handSize') }}>
        {/* Collapsible Header */}
        <button
          onClick={() => toggleSection('handSize')}
          className="w-full flex items-center justify-between mb-3"
          data-testid="section-toggle-hand-size"
        >
          <div className="flex items-center gap-3">
            {expandedSections.handSize ? (
              <CaretDown size={20} weight="bold" className="text-white/60" />
            ) : (
              <CaretRight size={20} weight="bold" className="text-white/60" />
            )}
            <Scales size={28} weight="duotone" className="text-purple-400" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-white">Hand Size Consistency</h3>
              <p className="text-white/40 text-sm">Measure your position sizing discipline</p>
            </div>
            <SectionEditControls sectionId="handSize" />
          </div>
          <span className="text-white/30 text-sm font-mono">
            {handSizeConsistencyData?.totalTrades || 0} trades
          </span>
        </button>

        {/* Collapsible Content */}
        <div className={`overflow-hidden transition-all duration-300 ${
          expandedSections.handSize ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          {/* Setup Filter Row */}
          <div className="flex items-center justify-end mb-4">
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-xs font-mono">SETUP:</span>
              <select
                value={handSizeSetupFilter}
                onChange={(e) => setHandSizeSetupFilter(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs font-mono focus:border-purple-500 focus:outline-none min-w-[140px]"
                data-testid="hand-size-setup-filter"
              >
                <option value="">All Setups</option>
                {setups.map((setup) => (
                  <option key={setup.id} value={setup.id}>{setup.name}</option>
                ))}
                <option value="__no_setup__">No setup</option>
              </select>
            </div>
          </div>

        {/* Filter indicator when setup is selected */}
        {handSizeSetupFilter && handSizeSetupDisplayName && (
          <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
            <span>Showing hand size analytics for:</span>
            <span className="font-bold text-purple-400">{handSizeSetupDisplayName}</span>
            <button
              onClick={() => setHandSizeSetupFilter("")}
              className="text-white/40 hover:text-white/60 ml-1"
              data-testid="clear-hand-size-filter"
            >
              (clear)
            </button>
          </div>
        )}

        {handSizeConsistencyData.insufficientData ? (
          <div className="text-center py-12">
            <Scales size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            <p className="text-white/40 font-semibold">Not enough sizing data yet</p>
            <p className="text-white/30 text-sm mt-1">
              Close more trades to analyze your hand size consistency.
              <br />
              <span className="text-white/20">({handSizeConsistencyData.totalTrades} of {handSizeConsistencyData.minRequired} minimum trades)</span>
            </p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {/* Baseline Hand Size */}
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target size={16} weight="duotone" className="text-purple-400" />
                  <span className="text-white/40 text-[10px] font-mono uppercase">Baseline</span>
                </div>
                <div className="text-2xl font-bold font-mono text-white mb-1">
                  ${handSizeConsistencyData.baselineHandSize.toLocaleString()}
                </div>
                <div className="text-white/30 text-[10px]">
                  Median hand size
                </div>
              </div>

              {/* Consistency Rate */}
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Check size={16} weight="bold" className={
                    handSizeConsistencyData.consistencyRate >= 70 ? 'text-[#00E676]' :
                    handSizeConsistencyData.consistencyRate >= 50 ? 'text-[#FFA726]' :
                    'text-[#FF3B30]'
                  } />
                  <span className="text-white/40 text-[10px] font-mono uppercase">Consistency</span>
                </div>
                <div className={`text-2xl font-bold font-mono mb-1 ${
                  handSizeConsistencyData.consistencyRate >= 70 ? 'text-[#00E676]' :
                  handSizeConsistencyData.consistencyRate >= 50 ? 'text-[#FFA726]' :
                  'text-[#FF3B30]'
                }`}>
                  {handSizeConsistencyData.consistencyRate}%
                </div>
                <div className="text-white/30 text-[10px]">
                  {handSizeConsistencyData.consistentCount} of {handSizeConsistencyData.totalTrades} trades
                </div>
              </div>

              {/* Oversized Trades */}
              <div 
                className="bg-[#0A0A0A] border border-[#FF3B30]/20 rounded-sm p-4 cursor-pointer hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/5 transition-all group"
                onClick={() => {
                  if (handSizeConsistencyData.oversizedCount > 0) {
                    const filter = { handSizeType: 'oversized' };
                    if (handSizeSetupFilter) filter.setup = handSizeSetupFilter;
                    const privateUrl = handSizeSetupFilter 
                      ? `/advanced/hand-size-trades?type=oversized&setup=${encodeURIComponent(handSizeSetupFilter)}`
                      : '/advanced/hand-size-trades?type=oversized';
                    navigateToFilteredView(privateUrl, filter);
                  }
                }}
                data-testid="oversized-trades-card"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ArrowUp size={16} weight="bold" className="text-[#FF3B30]" />
                    <span className="text-white/40 text-[10px] font-mono uppercase">Oversized</span>
                  </div>
                  {handSizeConsistencyData.oversizedCount > 0 && (
                    <span className="text-white/30 text-[10px] group-hover:text-[#FF3B30] transition-colors">View →</span>
                  )}
                </div>
                <div className="text-2xl font-bold font-mono text-[#FF3B30] mb-1">
                  {handSizeConsistencyData.oversizedCount}
                </div>
                <div className="text-white/30 text-[10px]">
                  Above +15% band
                </div>
              </div>

              {/* Undersized Trades */}
              <div 
                className="bg-[#0A0A0A] border border-[#FFA726]/20 rounded-sm p-4 cursor-pointer hover:border-[#FFA726]/40 hover:bg-[#FFA726]/5 transition-all group"
                onClick={() => {
                  if (handSizeConsistencyData.undersizedCount > 0) {
                    const filter = { handSizeType: 'undersized' };
                    if (handSizeSetupFilter) filter.setup = handSizeSetupFilter;
                    const privateUrl = handSizeSetupFilter 
                      ? `/advanced/hand-size-trades?type=undersized&setup=${encodeURIComponent(handSizeSetupFilter)}`
                      : '/advanced/hand-size-trades?type=undersized';
                    navigateToFilteredView(privateUrl, filter);
                  }
                }}
                data-testid="undersized-trades-card"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ArrowDown size={16} weight="bold" className="text-[#FFA726]" />
                    <span className="text-white/40 text-[10px] font-mono uppercase">Undersized</span>
                  </div>
                  {handSizeConsistencyData.undersizedCount > 0 && (
                    <span className="text-white/30 text-[10px] group-hover:text-[#FFA726] transition-colors">View →</span>
                  )}
                </div>
                <div className="text-2xl font-bold font-mono text-[#FFA726] mb-1">
                  {handSizeConsistencyData.undersizedCount}
                </div>
                <div className="text-white/30 text-[10px]">
                  Below -15% band
                </div>
              </div>

              {/* Material Alerts */}
              <div 
                className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4 cursor-pointer hover:border-purple-500/40 hover:bg-purple-500/5 transition-all group"
                onClick={() => {
                  if (handSizeConsistencyData.materialAlertsCount > 0) {
                    const filter = { handSizeType: 'material' };
                    if (handSizeSetupFilter) filter.setup = handSizeSetupFilter;
                    const privateUrl = handSizeSetupFilter 
                      ? `/advanced/hand-size-trades?type=material&setup=${encodeURIComponent(handSizeSetupFilter)}`
                      : '/advanced/hand-size-trades?type=material';
                    navigateToFilteredView(privateUrl, filter);
                  }
                }}
                data-testid="material-alerts-card"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Warning size={16} weight="fill" className={
                      handSizeConsistencyData.materialAlertsCount > 0 ? 'text-[#FF3B30]' : 'text-white/40'
                    } />
                    <span className="text-white/40 text-[10px] font-mono uppercase">Material Alerts</span>
                  </div>
                  {handSizeConsistencyData.materialAlertsCount > 0 && (
                    <span className="text-white/30 text-[10px] group-hover:text-purple-400 transition-colors">View →</span>
                  )}
                </div>
                <div className={`text-2xl font-bold font-mono mb-1 ${
                  handSizeConsistencyData.materialAlertsCount > 0 ? 'text-[#FF3B30]' : 'text-white/40'
                }`}>
                  {handSizeConsistencyData.materialAlertsCount}
                </div>
                <div className="text-white/30 text-[10px]">
                  &gt; 1% of ${(handSizeConsistencyData.initialCapital / 1000).toFixed(0)}K capital
                </div>
              </div>
            </div>

            {/* Consistency Breakdown Panel */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5 mb-6">
              <div className="text-white/40 text-xs font-mono mb-4 uppercase">Consistency Breakdown</div>
              
              {/* Visual Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-white/40 mb-2">
                  <span>Under ({handSizeConsistencyData.undersizedCount})</span>
                  <span>Consistent ({handSizeConsistencyData.consistentCount})</span>
                  <span>Over ({handSizeConsistencyData.oversizedCount})</span>
                </div>
                <div className="h-4 bg-white/5 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-[#FFA726] transition-all duration-500"
                    style={{ width: `${(handSizeConsistencyData.undersizedCount / handSizeConsistencyData.totalTrades) * 100}%` }}
                  />
                  <div 
                    className="bg-[#00E676] transition-all duration-500"
                    style={{ width: `${(handSizeConsistencyData.consistentCount / handSizeConsistencyData.totalTrades) * 100}%` }}
                  />
                  <div 
                    className="bg-[#FF3B30] transition-all duration-500"
                    style={{ width: `${(handSizeConsistencyData.oversizedCount / handSizeConsistencyData.totalTrades) * 100}%` }}
                  />
                </div>
              </div>

              {/* Band Info */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-white/40 text-[10px] mb-1">Lower Band (-15%)</div>
                  <div className="font-mono text-[#FFA726]">${handSizeConsistencyData.lowerBand.toLocaleString()}</div>
                </div>
                <div className="text-center">
                  <div className="text-white/40 text-[10px] mb-1">Baseline (Median)</div>
                  <div className="font-mono text-white font-bold">${handSizeConsistencyData.baselineHandSize.toLocaleString()}</div>
                </div>
                <div className="text-center">
                  <div className="text-white/40 text-[10px] mb-1">Upper Band (+15%)</div>
                  <div className="font-mono text-[#FF3B30]">${handSizeConsistencyData.upperBand.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Material Deviation Analysis */}
            {(handSizeConsistencyData.oversizedCount > 0 || handSizeConsistencyData.undersizedCount > 0) && (
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#0A0A0A] border border-[#FF3B30]/20 rounded-sm p-4">
                  <div className="text-[#FF3B30]/60 text-xs font-mono mb-2">Avg Oversized Deviation</div>
                  <div className="text-[#FF3B30] text-xl font-mono font-bold">
                    {handSizeConsistencyData.oversizedCount > 0 
                      ? `+$${handSizeConsistencyData.avgOversizedDeviation.toFixed(2)}`
                      : 'N/A'
                    }
                  </div>
                  <div className="text-white/30 text-[10px] mt-1">
                    {handSizeConsistencyData.oversizedCount} trades
                  </div>
                </div>
                <div className="bg-[#0A0A0A] border border-[#FFA726]/20 rounded-sm p-4">
                  <div className="text-[#FFA726]/60 text-xs font-mono mb-2">Avg Undersized Deviation</div>
                  <div className="text-[#FFA726] text-xl font-mono font-bold">
                    {handSizeConsistencyData.undersizedCount > 0 
                      ? `-$${handSizeConsistencyData.avgUndersizedDeviation.toFixed(2)}`
                      : 'N/A'
                    }
                  </div>
                  <div className="text-white/30 text-[10px] mt-1">
                    {handSizeConsistencyData.undersizedCount} trades
                  </div>
                </div>
              </div>
            )}

            {/* Trade Type Segmentation (Day vs Swing) */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5 mb-6">
              <div className="text-white/40 text-xs font-mono mb-4 uppercase">Hand Size Consistency by Trade Type</div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Day Trade */}
                <div className="border border-blue-500/20 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sun size={18} weight="duotone" className="text-blue-400" />
                    <span className="text-blue-400 text-sm font-bold">Day Trade</span>
                    <span className="text-white/30 text-xs ml-auto">(&lt;4H)</span>
                  </div>
                  {handSizeConsistencyData.dayTradeMetrics.insufficientData ? (
                    <p className="text-white/30 text-sm">Not enough Day Trade data</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Trades</span>
                        <span className="font-mono text-white">{handSizeConsistencyData.dayTradeMetrics.total}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Consistency</span>
                        <span className={`font-mono font-bold ${
                          parseFloat(handSizeConsistencyData.dayTradeMetrics.consistencyRate) >= 70 ? 'text-[#00E676]' :
                          parseFloat(handSizeConsistencyData.dayTradeMetrics.consistencyRate) >= 50 ? 'text-[#FFA726]' :
                          'text-[#FF3B30]'
                        }`}>
                          {handSizeConsistencyData.dayTradeMetrics.consistencyRate}%
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Oversized / Undersized</span>
                        <span className="font-mono">
                          <span className="text-[#FF3B30]">{handSizeConsistencyData.dayTradeMetrics.oversizedCount}</span>
                          <span className="text-white/30"> / </span>
                          <span className="text-[#FFA726]">{handSizeConsistencyData.dayTradeMetrics.undersizedCount}</span>
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Material Alerts</span>
                        <span className={`font-mono ${
                          handSizeConsistencyData.dayTradeMetrics.materialAlerts > 0 ? 'text-[#FF3B30]' : 'text-white/40'
                        }`}>
                          {handSizeConsistencyData.dayTradeMetrics.materialAlerts}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Swing Trade */}
                <div className="border border-purple-500/20 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Moon size={18} weight="duotone" className="text-purple-400" />
                    <span className="text-purple-400 text-sm font-bold">Swing Trade</span>
                    <span className="text-white/30 text-xs ml-auto">(≥4H)</span>
                  </div>
                  {handSizeConsistencyData.swingTradeMetrics.insufficientData ? (
                    <p className="text-white/30 text-sm">Not enough Swing Trade data</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Trades</span>
                        <span className="font-mono text-white">{handSizeConsistencyData.swingTradeMetrics.total}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Consistency</span>
                        <span className={`font-mono font-bold ${
                          parseFloat(handSizeConsistencyData.swingTradeMetrics.consistencyRate) >= 70 ? 'text-[#00E676]' :
                          parseFloat(handSizeConsistencyData.swingTradeMetrics.consistencyRate) >= 50 ? 'text-[#FFA726]' :
                          'text-[#FF3B30]'
                        }`}>
                          {handSizeConsistencyData.swingTradeMetrics.consistencyRate}%
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Oversized / Undersized</span>
                        <span className="font-mono">
                          <span className="text-[#FF3B30]">{handSizeConsistencyData.swingTradeMetrics.oversizedCount}</span>
                          <span className="text-white/30"> / </span>
                          <span className="text-[#FFA726]">{handSizeConsistencyData.swingTradeMetrics.undersizedCount}</span>
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Material Alerts</span>
                        <span className={`font-mono ${
                          handSizeConsistencyData.swingTradeMetrics.materialAlerts > 0 ? 'text-[#FF3B30]' : 'text-white/40'
                        }`}>
                          {handSizeConsistencyData.swingTradeMetrics.materialAlerts}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Trade Type Insight */}
              {handSizeConsistencyData.tradeTypeInsight && (
                <div 
                  className="mt-4 p-3 rounded-sm border"
                  style={{
                    backgroundColor: `${handSizeConsistencyData.tradeTypeInsight.color}10`,
                    borderColor: `${handSizeConsistencyData.tradeTypeInsight.color}30`
                  }}
                >
                  <p className="text-sm" style={{ color: handSizeConsistencyData.tradeTypeInsight.color }}>
                    {handSizeConsistencyData.tradeTypeInsight.text}
                  </p>
                </div>
              )}
            </div>

            {/* Main Insight */}
            {handSizeConsistencyData.insight && (
              <div 
                className="p-4 rounded-sm border"
                style={{
                  backgroundColor: `${handSizeConsistencyData.insight.color}10`,
                  borderColor: `${handSizeConsistencyData.insight.color}30`
                }}
              >
                <div className="flex items-start gap-3">
                  {handSizeConsistencyData.insight.icon === 'check' && (
                    <Check size={20} weight="bold" style={{ color: handSizeConsistencyData.insight.color }} />
                  )}
                  {handSizeConsistencyData.insight.icon === 'warning' && (
                    <Warning size={20} weight="fill" style={{ color: handSizeConsistencyData.insight.color }} />
                  )}
                  {handSizeConsistencyData.insight.icon === 'info' && (
                    <Info size={20} weight="duotone" style={{ color: handSizeConsistencyData.insight.color }} />
                  )}
                  <div>
                    <div className="text-white/40 text-[10px] font-mono mb-1">SIZING INSIGHT</div>
                    <p className="text-sm" style={{ color: handSizeConsistencyData.insight.color }}>
                      {handSizeConsistencyData.insight.text}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* ============================================ */}
      {/* STOP ADJUSTMENTS SECTION - Collapsible */}
      {/* ============================================ */}
      <div className={`bg-[#121212] border border-white/10 rounded-sm p-6 mt-8 ${isEditingLayout ? 'ring-1 ring-[#00E676]/20' : ''}`} data-testid="stop-adjustments-section" data-section-id="stopAdjustments" style={{ order: getSectionCssOrder('stopAdjustments') }}>
        {/* Collapsible Header */}
        <button
          onClick={() => toggleSection('stopAdjustments')}
          className="w-full flex items-center justify-between mb-3"
          data-testid="section-toggle-stop-adjustments"
        >
          <div className="flex items-center gap-3">
            {expandedSections.stopAdjustments ? (
              <CaretDown size={20} weight="bold" className="text-white/60" />
            ) : (
              <CaretRight size={20} weight="bold" className="text-white/60" />
            )}
            <SlidersHorizontal size={28} weight="duotone" className="text-cyan-400" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-white">Stop Adjustments</h3>
              <p className="text-white/40 text-sm">Track your stop loss management behavior</p>
            </div>
            <SectionEditControls sectionId="stopAdjustments" />
          </div>
          <span className="text-white/30 text-sm font-mono">
            {stopAdjustmentsData?.totalAdjustedTrades || 0} adjusted
          </span>
        </button>

        {/* Collapsible Content */}
        <div className={`overflow-hidden transition-all duration-300 ${
          expandedSections.stopAdjustments ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          {/* Setup Filter Row */}
          <div className="flex items-center justify-end mb-4">
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-xs font-mono">SETUP:</span>
              <select
                value={stopAdjustmentsSetupFilter}
                onChange={(e) => setStopAdjustmentsSetupFilter(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-2 py-1 text-xs font-mono focus:border-cyan-500 focus:outline-none min-w-[140px]"
                data-testid="stop-adjustments-setup-filter"
              >
                <option value="">All Setups</option>
                {setups.map((setup) => (
                  <option key={setup.id} value={setup.id}>{setup.name}</option>
                ))}
                <option value="__no_setup__">No setup</option>
              </select>
            </div>
          </div>

        {/* Filter indicator when setup is selected */}
        {stopAdjustmentsSetupFilter && stopAdjustmentsSetupDisplayName && (
          <div className="mb-4 flex items-center gap-2 text-white/60 text-sm">
            <span>Showing stop adjustment analytics for:</span>
            <span className="font-bold text-cyan-400">{stopAdjustmentsSetupDisplayName}</span>
            <button
              onClick={() => setStopAdjustmentsSetupFilter("")}
              className="text-white/40 hover:text-white/60 ml-1"
              data-testid="clear-stop-adjustments-filter"
            >
              (clear)
            </button>
          </div>
        )}

        {stopAdjustmentsData.noData ? (
          <div className="text-center py-12">
            <SlidersHorizontal size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            <p className="text-white/40 font-semibold">No closed trades yet</p>
            <p className="text-white/30 text-sm mt-1">
              Close some trades to see your stop adjustment analytics.
            </p>
          </div>
        ) : stopAdjustmentsData.adjustedCount === 0 ? (
          <div className="text-center py-12">
            <SlidersHorizontal size={48} weight="duotone" className="text-white/20 mx-auto mb-4" />
            <p className="text-white/40 font-semibold">No stop adjustment data yet</p>
            <p className="text-white/30 text-sm mt-1">
              Adjust stops on open positions to track your active stop management.
            </p>
          </div>
        ) : (
          <>
            {/* Summary Cards - Row 1: Counts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {/* Adjusted Trades */}
              <div 
                className="bg-[#0A0A0A] border border-cyan-500/20 rounded-sm p-4 cursor-pointer hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-all group"
                onClick={() => {
                  const filter = { hasStopAdjustment: true };
                  if (stopAdjustmentsSetupFilter) filter.setup = stopAdjustmentsSetupFilter;
                  const privateUrl = stopAdjustmentsSetupFilter 
                    ? `/advanced/stop-adjustments-trades?setup=${encodeURIComponent(stopAdjustmentsSetupFilter)}`
                    : '/advanced/stop-adjustments-trades';
                  navigateToFilteredView(privateUrl, filter);
                }}
                data-testid="adjusted-trades-card"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal size={16} weight="duotone" className="text-cyan-400" />
                    <span className="text-white/40 text-[10px] font-mono uppercase">Adjusted Trades</span>
                  </div>
                  <span className="text-white/30 text-[10px] group-hover:text-cyan-400 transition-colors">View →</span>
                </div>
                <div className="text-2xl font-bold font-mono text-cyan-400 mb-1">
                  {stopAdjustmentsData.adjustedCount}
                </div>
                <div className="text-white/30 text-[10px]">
                  {stopAdjustmentsData.adjustedPercent}% of {stopAdjustmentsData.totalClosed} closed
                </div>
              </div>

              {/* Risk Reduction Trades */}
              <div 
                className="bg-[#0A0A0A] border border-[#FFA726]/20 rounded-sm p-4 cursor-pointer hover:border-[#FFA726]/40 hover:bg-[#FFA726]/5 transition-all group"
                onClick={() => {
                  const filter = { stopAdjustmentType: 'risk_reduction' };
                  if (stopAdjustmentsSetupFilter) filter.setup = stopAdjustmentsSetupFilter;
                  const privateUrl = stopAdjustmentsSetupFilter 
                    ? `/advanced/stop-adjustments-trades?type=risk_reduction&setup=${encodeURIComponent(stopAdjustmentsSetupFilter)}`
                    : '/advanced/stop-adjustments-trades?type=risk_reduction';
                  navigateToFilteredView(privateUrl, filter);
                }}
                data-testid="risk-reduction-card"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={16} weight="duotone" className="text-[#FFA726]" />
                    <span className="text-white/40 text-[10px] font-mono uppercase">Risk Reduction</span>
                  </div>
                  {stopAdjustmentsData.riskReductionCount > 0 && (
                    <span className="text-white/30 text-[10px] group-hover:text-[#FFA726] transition-colors">View →</span>
                  )}
                </div>
                <div className="text-2xl font-bold font-mono text-[#FFA726] mb-1">
                  {stopAdjustmentsData.riskReductionCount}
                </div>
                <div className="text-white/30 text-[10px]">
                  Stop improved, still loss side
                </div>
              </div>

              {/* Break-even Trades */}
              <div 
                className="bg-[#0A0A0A] border border-[#00B8D4]/20 rounded-sm p-4 cursor-pointer hover:border-[#00B8D4]/40 hover:bg-[#00B8D4]/5 transition-all group"
                onClick={() => {
                  const filter = { stopAdjustmentType: 'breakeven' };
                  if (stopAdjustmentsSetupFilter) filter.setup = stopAdjustmentsSetupFilter;
                  const privateUrl = stopAdjustmentsSetupFilter 
                    ? `/advanced/stop-adjustments-trades?type=breakeven&setup=${encodeURIComponent(stopAdjustmentsSetupFilter)}`
                    : '/advanced/stop-adjustments-trades?type=breakeven';
                  navigateToFilteredView(privateUrl, filter);
                }}
                data-testid="breakeven-card"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Equals size={16} weight="bold" className="text-[#00B8D4]" />
                    <span className="text-white/40 text-[10px] font-mono uppercase">Break-even</span>
                  </div>
                  {stopAdjustmentsData.breakevenCount > 0 && (
                    <span className="text-white/30 text-[10px] group-hover:text-[#00B8D4] transition-colors">View →</span>
                  )}
                </div>
                <div className="text-2xl font-bold font-mono text-[#00B8D4] mb-1">
                  {stopAdjustmentsData.breakevenCount}
                </div>
                <div className="text-white/30 text-[10px]">
                  Stop moved to entry
                </div>
              </div>

              {/* Profit Lock Trades */}
              <div 
                className="bg-[#0A0A0A] border border-[#00E676]/20 rounded-sm p-4 cursor-pointer hover:border-[#00E676]/40 hover:bg-[#00E676]/5 transition-all group"
                onClick={() => {
                  const filter = { stopAdjustmentType: 'profit_lock' };
                  if (stopAdjustmentsSetupFilter) filter.setup = stopAdjustmentsSetupFilter;
                  const privateUrl = stopAdjustmentsSetupFilter 
                    ? `/advanced/stop-adjustments-trades?type=profit_lock&setup=${encodeURIComponent(stopAdjustmentsSetupFilter)}`
                    : '/advanced/stop-adjustments-trades?type=profit_lock';
                  navigateToFilteredView(privateUrl, filter);
                }}
                data-testid="profit-lock-card"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Lock size={16} weight="fill" className="text-[#00E676]" />
                    <span className="text-white/40 text-[10px] font-mono uppercase">Profit Lock</span>
                  </div>
                  {stopAdjustmentsData.profitLockCount > 0 && (
                    <span className="text-white/30 text-[10px] group-hover:text-[#00E676] transition-colors">View →</span>
                  )}
                </div>
                <div className="text-2xl font-bold font-mono text-[#00E676] mb-1">
                  {stopAdjustmentsData.profitLockCount}
                </div>
                <div className="text-white/30 text-[10px]">
                  Stop beyond entry
                </div>
              </div>
            </div>

            {/* Adjustment Type Distribution */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5 mb-6">
              <div className="text-white/40 text-xs font-mono mb-4 uppercase">Adjustment Type Distribution</div>
              
              {/* Visual Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-white/40 mb-2">
                  <span>Risk Reduction ({stopAdjustmentsData.typeDistribution.riskReduction.count})</span>
                  <span>Break-even ({stopAdjustmentsData.typeDistribution.breakeven.count})</span>
                  <span>Profit Lock ({stopAdjustmentsData.typeDistribution.profitLock.count})</span>
                </div>
                <div className="h-4 bg-white/5 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-[#FFA726] transition-all duration-500"
                    style={{ width: `${stopAdjustmentsData.typeDistribution.riskReduction.percent}%` }}
                    title={`Risk Reduction: ${stopAdjustmentsData.typeDistribution.riskReduction.percent}%`}
                  />
                  <div 
                    className="bg-[#00B8D4] transition-all duration-500"
                    style={{ width: `${stopAdjustmentsData.typeDistribution.breakeven.percent}%` }}
                    title={`Break-even: ${stopAdjustmentsData.typeDistribution.breakeven.percent}%`}
                  />
                  <div 
                    className="bg-[#00E676] transition-all duration-500"
                    style={{ width: `${stopAdjustmentsData.typeDistribution.profitLock.percent}%` }}
                    title={`Profit Lock: ${stopAdjustmentsData.typeDistribution.profitLock.percent}%`}
                  />
                </div>
              </div>

              {/* Percentages */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-white/40 text-[10px] mb-1">Risk Reduction</div>
                  <div className="font-mono text-[#FFA726] font-bold">{stopAdjustmentsData.typeDistribution.riskReduction.percent}%</div>
                </div>
                <div className="text-center">
                  <div className="text-white/40 text-[10px] mb-1">Break-even</div>
                  <div className="font-mono text-[#00B8D4] font-bold">{stopAdjustmentsData.typeDistribution.breakeven.percent}%</div>
                </div>
                <div className="text-center">
                  <div className="text-white/40 text-[10px] mb-1">Profit Lock</div>
                  <div className="font-mono text-[#00E676] font-bold">{stopAdjustmentsData.typeDistribution.profitLock.percent}%</div>
                </div>
              </div>
            </div>

            {/* Trade Type Segmentation with Type Breakdown */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-5 mb-6">
              <div className="text-white/40 text-xs font-mono mb-4 uppercase">Stop Adjustments by Trade Type</div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Day Trade */}
                <div className="border border-blue-500/20 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sun size={18} weight="duotone" className="text-blue-400" />
                    <span className="text-blue-400 text-sm font-bold">Day Trade</span>
                    <span className="text-white/30 text-xs ml-auto">(&lt;4H)</span>
                  </div>
                  {stopAdjustmentsData.dayMetrics.total === 0 ? (
                    <p className="text-white/30 text-sm">No Day Trade data</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Total Closed</span>
                        <span className="font-mono text-white">{stopAdjustmentsData.dayMetrics.total}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Adjusted</span>
                        <span className="font-mono text-cyan-400 font-bold">{stopAdjustmentsData.dayMetrics.adjusted}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Adjustment Rate</span>
                        <span className="font-mono text-white">{stopAdjustmentsData.dayMetrics.percent}%</span>
                      </div>
                      {stopAdjustmentsData.dayMetrics.adjusted > 0 && stopAdjustmentsData.dayMetrics.typeBreakdown && (
                        <div className="pt-2 mt-2 border-t border-white/10">
                          <div className="text-white/40 text-[10px] font-mono mb-2">TYPE BREAKDOWN</div>
                          <div className="flex gap-2 text-[11px]">
                            <span className="text-[#FFA726]">{stopAdjustmentsData.dayMetrics.typeBreakdown.riskReduction} RR</span>
                            <span className="text-white/20">·</span>
                            <span className="text-[#00B8D4]">{stopAdjustmentsData.dayMetrics.typeBreakdown.breakeven} BE</span>
                            <span className="text-white/20">·</span>
                            <span className="text-[#00E676]">{stopAdjustmentsData.dayMetrics.typeBreakdown.profitLock} PL</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Swing Trade */}
                <div className="border border-purple-500/20 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Moon size={18} weight="duotone" className="text-purple-400" />
                    <span className="text-purple-400 text-sm font-bold">Swing Trade</span>
                    <span className="text-white/30 text-xs ml-auto">(≥4H)</span>
                  </div>
                  {stopAdjustmentsData.swingMetrics.total === 0 ? (
                    <p className="text-white/30 text-sm">No Swing Trade data</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Total Closed</span>
                        <span className="font-mono text-white">{stopAdjustmentsData.swingMetrics.total}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Adjusted</span>
                        <span className="font-mono text-cyan-400 font-bold">{stopAdjustmentsData.swingMetrics.adjusted}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Adjustment Rate</span>
                        <span className="font-mono text-white">{stopAdjustmentsData.swingMetrics.percent}%</span>
                      </div>
                      {stopAdjustmentsData.swingMetrics.adjusted > 0 && stopAdjustmentsData.swingMetrics.typeBreakdown && (
                        <div className="pt-2 mt-2 border-t border-white/10">
                          <div className="text-white/40 text-[10px] font-mono mb-2">TYPE BREAKDOWN</div>
                          <div className="flex gap-2 text-[11px]">
                            <span className="text-[#FFA726]">{stopAdjustmentsData.swingMetrics.typeBreakdown.riskReduction} RR</span>
                            <span className="text-white/20">·</span>
                            <span className="text-[#00B8D4]">{stopAdjustmentsData.swingMetrics.typeBreakdown.breakeven} BE</span>
                            <span className="text-white/20">·</span>
                            <span className="text-[#00E676]">{stopAdjustmentsData.swingMetrics.typeBreakdown.profitLock} PL</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Insight */}
            {stopAdjustmentsData.insight && (
              <div 
                className="p-4 rounded-sm border"
                style={{
                  backgroundColor: `${stopAdjustmentsData.insight.color}10`,
                  borderColor: `${stopAdjustmentsData.insight.color}30`
                }}
              >
                <div className="flex items-start gap-3">
                  {stopAdjustmentsData.insight.icon === 'gear' && (
                    <Gear size={20} weight="duotone" style={{ color: stopAdjustmentsData.insight.color }} />
                  )}
                  {stopAdjustmentsData.insight.icon === 'info' && (
                    <Info size={20} weight="duotone" style={{ color: stopAdjustmentsData.insight.color }} />
                  )}
                  {stopAdjustmentsData.insight.icon === 'lock' && (
                    <Lock size={20} weight="fill" style={{ color: stopAdjustmentsData.insight.color }} />
                  )}
                  {stopAdjustmentsData.insight.icon === 'breakeven' && (
                    <Equals size={20} weight="bold" style={{ color: stopAdjustmentsData.insight.color }} />
                  )}
                  {stopAdjustmentsData.insight.icon === 'shield' && (
                    <ShieldCheck size={20} weight="duotone" style={{ color: stopAdjustmentsData.insight.color }} />
                  )}
                  <div>
                    <div className="text-white/40 text-[10px] font-mono mb-1">STOP MANAGEMENT INSIGHT</div>
                    <p className="text-sm" style={{ color: stopAdjustmentsData.insight.color }}>
                      {stopAdjustmentsData.insight.text}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* === END ORDERABLE SECTIONS CONTAINER === */}
      </div>

      {/* Share Modal */}
      <ShareModal 
        open={showShareModal} 
        onOpenChange={setShowShareModal}
        defaultTitle="My Trading Analytics"
      />

      {/* Footer Branding */}
      <div className="flex items-center justify-center gap-2 mt-16 mb-6 text-sm text-white/40">
        <img
          src={LOGO_PUREBRAIN}
          alt="GENI"
          className="h-5 w-auto object-contain opacity-80"
        />
        <span>
          Powered by <span className="text-white font-medium">GENI</span>
        </span>
      </div>
      </div>
    </div>
  );
}
